import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Plus, Search, Filter, Edit3, Trash2, AlertTriangle, CheckCircle, Share2, User, FolderOpen, Truck, FileText, Route, MapPin, RefreshCw, Upload, History, ChevronRight, ChevronDown, Download, Layers, ArrowUpDown, ArrowUp, ArrowDown, ArrowLeft, LayoutGrid } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import LocationPicker from '../warehouse/LocationPicker';
import { getCachedSetting, setCachedSetting, removeCachedSetting } from '../../utils/categoryEquipmentCache';
import { getCurrentCurrency, getCurrencySymbol, formatPrice, onCurrencyChange } from '../../utils/currency';
import { robotoRegularBase64 } from '../ecc/roboto';
import GlassIcons from '../GlassIcons';
import Dock from '../Dock';
import Folder from '../Folder';
import FileTree from '../warehouse/FileTree';
import '../Folder.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseInventory = () => {
  // Helper function to get icon and color for category
  const getCategoryIconAndColor = (categoryName) => {
    const name = categoryName?.toLowerCase() || '';

    // Default icon and color
    let Icon = Layers;
    let color = 'blue';

    // Map category names to icons and colors
    if (name.includes('tool') || name.includes('narzędzi') || name.includes('narzedzi')) {
      Icon = Edit3;
      color = 'orange';
    } else if (name.includes('electric') || name.includes('elektry') || name.includes('electrical')) {
      Icon = AlertTriangle;
      color = 'yellow';
    } else if (name.includes('safety') || name.includes('bezpiecz') || name.includes('bhp')) {
      Icon = CheckCircle;
      color = 'green';
    } else if (name.includes('material') || name.includes('materia')) {
      Icon = Package;
      color = 'purple';
    } else if (name.includes('part') || name.includes('części') || name.includes('czesci')) {
      Icon = Layers;
      color = 'indigo';
    } else if (name.includes('chemical') || name.includes('chem')) {
      Icon = AlertTriangle;
      color = 'red';
    } else if (name.includes('office') || name.includes('biur')) {
      Icon = FileText;
      color = 'cyan';
    } else if (name.includes('transport') || name.includes('vehicle')) {
      Icon = Truck;
      color = 'teal';
    } else if (name.includes('equipment') || name.includes('sprz')) {
      Icon = Package;
      color = 'pink';
    }

    return { Icon, color };
  };

  // Helper function to format location display
  const formatLocationDisplay = (location) => {
    if (!location) return '';
    const locations = location.split(',').map(l => l.trim()).filter(l => l);
    if (locations.length <= 2) {
      return location;
    }
    return `${locations[0]}, ${locations[1]}...`;
  };

  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [allItemsUnfiltered, setAllItemsUnfiltered] = useState([]); // Store original unfiltered items
  const [hasLoadedAllItems, setHasLoadedAllItems] = useState(false); // Track if all items have been loaded once
  const [loadingAllItems, setLoadingAllItems] = useState(false); // Track when we're loading all items for search
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [groupedEquipment, setGroupedEquipment] = useState([]);
  const [expandedEquipmentTypes, setExpandedEquipmentTypes] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubcategory, setFilterSubcategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Sidebar state
  const [selectedSidebarCategory, setSelectedSidebarCategory] = useState(null);
  const [sidebarSubcategories, setSidebarSubcategories] = useState([]);
  const [showEquipmentOnly, setShowEquipmentOnly] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50); // Pokazuj 50 itemów na stronę
  const [totalCount, setTotalCount] = useState(0); // Total items from backend
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    scope: 'all', // 'all', 'filtered', 'selected'
    format: 'excel', // 'excel', 'csv', 'pdf'
    columns: {
      name: true,
      sku: true,
      category: true,
      subcategory: true,
      quantity: true,
      unit: true,
      unitPrice: true,
      location: true,
      status: true,
      lastUpdated: true
    }
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [showEquipmentSharingModal, setShowEquipmentSharingModal] = useState(false);
  const [showEquipmentLocationModal, setShowEquipmentLocationModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null); // For image preview modal
  const [editingItem, setEditingItem] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const [sharingEquipmentGroup, setSharingEquipmentGroup] = useState(null);
  const [locationEquipmentGroup, setLocationEquipmentGroup] = useState(null);

  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedEquipmentItems, setSelectedEquipmentItems] = useState([]); // For individual equipment items
  const [showEquipmentItemsLocationModal, setShowEquipmentItemsLocationModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentType, setAssignmentType] = useState(null); // 'person' or 'project'
  const [loading, setLoading] = useState(true);

  // Restock modal state
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockingItem, setRestockingItem] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  // Currency state
  const [currentCurrency, setCurrentCurrency] = useState(getCurrentCurrency());
  const [historyData, setHistoryData] = useState([]);

  // Individual items state for expandable rows
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [individualItemsData, setIndividualItemsData] = useState({});
  const [loadingIndividualItems, setLoadingIndividualItems] = useState(new Set());

  // Individual item edit modal state
  const [showEditIndividualModal, setShowEditIndividualModal] = useState(false);
  const [editingIndividualItem, setEditingIndividualItem] = useState(null);

  // Manual return modal state (for issued items with orphaned projects)
  const [showManualReturnModal, setShowManualReturnModal] = useState(false);
  const [manualReturnItem, setManualReturnItem] = useState(null);

  // Car Inventory state
  const [showCarInventoryModal, setShowCarInventoryModal] = useState(false);
  const [cars, setCars] = useState([]);
  const [carInventoryAssignments, setCarInventoryAssignments] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [carInventoryLoading, setCarInventoryLoading] = useState(false);
  const [showAssignToCarModal, setShowAssignToCarModal] = useState(false);

  // Equipment custom fields state
  const [equipmentCustomFields, setEquipmentCustomFields] = useState([]);

  // Toast notification state (auto-dismissing)
  const [toast, setToast] = useState(null);

  // Auto-dismiss toast after 2.5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Inline serial number editing state
  const [editingSerialNumber, setEditingSerialNumber] = useState(null); // stores item id that's being edited
  const [tempSerialNumber, setTempSerialNumber] = useState('');

  // Inline custom field editing state
  const [editingCustomField, setEditingCustomField] = useState(null); // stores item id and field name
  const [tempCustomFieldValue, setTempCustomFieldValue] = useState('');
  const [editIndividualFormData, setEditIndividualFormData] = useState({
    serial_number: '',
    notes: '',
    custom_fields: {}
  });

  // Build tree structure for FileTree component
  const treeData = useMemo(() => {
    const tree = [];

    // Add "All Items" root node
    tree.push({
      id: 'all-items',
      label: 'All Items',
      icon: Layers,
      count: totalCount,
      isRoot: true
    });

    // Add "Equipment" root node if equipment exists
    if (groupedEquipment.length > 0) {
      tree.push({
        id: 'equipment',
        label: 'Equipment',
        icon: Package,
        count: groupedEquipment.length,
        isRoot: true
      });
    }

    // Add categories with their subcategories as children
    categories
      .filter(cat => cat.value !== 'all')
      .forEach(category => {
        const { Icon, color } = getCategoryIconAndColor(category.label);
        const categoryNode = {
          id: category.value,
          label: category.label,
          icon: Icon,
          color: color,
          children: []
        };

        // Add subcategories as children
        const categorySubcategories = subcategories.filter(
          sub => sub.category === category.value
        );

        categorySubcategories.forEach(subcategory => {
          categoryNode.children.push({
            id: `sub-${subcategory.id}`,
            label: subcategory.name,
            icon: FolderOpen,
            parentId: category.value,
            subcategoryId: subcategory.id
          });
        });

        tree.push(categoryNode);
      });

    return tree;
  }, [categories, subcategories, groupedEquipment, totalCount]);

  // Determine currently selected tree item ID
  const selectedTreeItemId = useMemo(() => {
    if (showEquipmentOnly) return 'equipment';
    if (filterSubcategory !== 'all') return `sub-${filterSubcategory}`;
    if (filterCategory !== 'all') return filterCategory;
    return 'all-items';
  }, [showEquipmentOnly, filterCategory, filterSubcategory]);

  useEffect(() => {
    fetchInventoryData();
    fetchCategories();
    fetchSubcategories();
    fetchCustomFields();
    fetchEquipmentCustomFields();
  }, []);

  // Background load all items for search after initial page load (with delay)
  useEffect(() => {
    // Wait 2 seconds after page load, then start loading all items in background
    const timer = setTimeout(() => {
      console.log('[Background] Starting to load all items for search in background...');
      ensureAllItemsLoaded();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Clean up expanded items when custom fields change
  useEffect(() => {
    if (!customFields || customFields.length === 0) {
      setExpandedItems(new Set());
      setIndividualItemsData({});
    }
  }, [customFields]);

  // Listen for currency changes
  useEffect(() => {
    const cleanup = onCurrencyChange((newCurrency) => {
      setCurrentCurrency(newCurrency);
    });

    return cleanup;
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('warehouse_recent_searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  }, []);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && !event.target.closest('.search-input-container')) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  // Reset page to 1 when filters change (but not when currentPage changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterSubcategory, filterStatus]);

  useEffect(() => {
    fetchFilteredItems();
    // filterEquipment is no longer needed - equipment filtering is handled by backend
  }, [searchTerm, filterCategory, filterSubcategory, filterStatus, currentPage]);

  // Generate search suggestions based on current items
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const suggestions = [];
      const searchLower = searchTerm.toLowerCase();

      // Search in all items (not just paginated)
      allItemsUnfiltered.forEach(item => {
        // Search in name
        if (item.name && item.name.toLowerCase().includes(searchLower)) {
          suggestions.push({
            type: 'name',
            value: item.name,
            field: 'Name',
            item: item
          });
        }
        // Search in SKU
        if (item.sku && item.sku.toLowerCase().includes(searchLower)) {
          suggestions.push({
            type: 'sku',
            value: item.sku,
            field: 'SKU',
            item: item
          });
        }
        // Search in location
        if (item.location && item.location.toLowerCase().includes(searchLower)) {
          suggestions.push({
            type: 'location',
            value: item.location,
            field: 'Location',
            item: item
          });
        }
      });

      // Remove duplicates and limit to 8 suggestions
      const uniqueSuggestions = suggestions.filter((suggestion, index, self) =>
        index === self.findIndex(s => s.value === suggestion.value && s.type === suggestion.type)
      ).slice(0, 8);

      setSearchSuggestions(uniqueSuggestions);
      setShowSuggestions(uniqueSuggestions.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, allItemsUnfiltered]);

  // Handle search with recent history
  const handleSearch = (term) => {
    setSearchTerm(term);
    setShowSuggestions(false);

    if (term.trim()) {
      // Add to recent searches
      const newRecent = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
      setRecentSearches(newRecent);
      localStorage.setItem('warehouse_recent_searches', JSON.stringify(newRecent));
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    handleSearch(suggestion.value);
  };

  // Handle keyboard navigation in suggestions
  const handleSearchKeyDown = (e) => {
    if (!showSuggestions || searchSuggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(searchTerm);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < searchSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSelectSuggestion(searchSuggestions[selectedSuggestionIndex]);
        } else {
          handleSearch(searchTerm);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  // Highlight matching text
  const highlightText = (text, highlight) => {
    if (!highlight || !text) return text;

    const parts = text.toString().split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase()
        ? <mark key={index} className="bg-yellow-200 font-semibold">{part}</mark>
        : part
    );
  };

  const toggleEquipmentType = async (typeName, globalTypeId) => {
    const isExpanding = !expandedEquipmentTypes.has(typeName);

    setExpandedEquipmentTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeName)) {
        newSet.delete(typeName);
      } else {
        newSet.add(typeName);
      }
      return newSet;
    });

    // Lazy load individual items when expanding
    if (isExpanding && globalTypeId) {
      try {
        const equipmentAPI = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;
        const config = {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        };

        // Use new optimized expanded endpoint with global_type_id
        const response = await axios.get(
          `${equipmentAPI}/safetyequipment/expanded/?global_type_id=${globalTypeId}`,
          config
        );

        const equipmentData = Array.isArray(response.data) ? response.data : [];

        console.log(`[Equipment] Loaded ${equipmentData.length} individual items for global type: ${typeName} (global_type_id=${globalTypeId})`);

        // Format individual items - data is already optimized from backend
        const formattedEquipment = equipmentData.map(item => {
          // Build full URL for equipment picture
          let equipmentPictureUrl = null;
          if (item.picture) {
            if (item.picture.startsWith('http://') || item.picture.startsWith('https://')) {
              equipmentPictureUrl = item.picture;
            } else {
              equipmentPictureUrl = `${API_BASE_URL}${item.picture.startsWith('/') ? '' : '/'}${item.picture}`;
            }
          }

          // Determine location display based on assignment type
          // If assigned to project, show project name in location column
          let locationDisplay = item.location;
          if (item.assignment_data?.assigned_project_name) {
            locationDisplay = item.assignment_data.assigned_project_name;
          }

          return {
            id: `equipment_${item.id}`,
            originalId: item.id,
            name: typeName,
            equipmentType: typeName,
            globalTypeId: globalTypeId,  // Use global_type_id for consistent grouping
            typeId: item.type_id,  // Company-specific type ID (for custom fields)
            serialNumber: item.serial_number,
            status: item.status,
            location: locationDisplay,
            actualLocation: item.location, // Store actual location separately
            company: item.company,
            companyId: item.company_id, // Owner company ID
            isOwner: item.is_owner, // True if user's company owns this item
            model: item.model,
            manufacturer: item.manufacturer,
            lastUpdated: 'N/A',
            type: 'equipment',
            category: 'Equipment',
            quantity: 1,
            unit: 'pcs',
            unitPrice: 0,
            ownerCompany: item.company,
            isShared: item.is_shared,
            sharedWithCompanies: item.shared_with_companies || [],
            customFields: item.custom_fields || {},
            item_picture: equipmentPictureUrl,
            // Assignment data
            issued_to: item.assignment_data?.assigned_user_name || null,
            project_name: item.assignment_data?.assigned_project_name || null
          };
        });

        // Update the group's equipmentItems in the items state
        setItems(prevItems => {
          return prevItems.map(item => {
            if (item.type === 'equipment_group' && item.globalTypeId === globalTypeId) {
              return {
                ...item,
                equipmentItems: formattedEquipment
              };
            }
            return item;
          });
        });

        // Also update allItemsUnfiltered
        setAllItemsUnfiltered(prevItems => {
          return prevItems.map(item => {
            if (item.type === 'equipment_group' && item.globalTypeId === globalTypeId) {
              return {
                ...item,
                equipmentItems: formattedEquipment
              };
            }
            return item;
          });
        });

      } catch (error) {
        console.error('Error loading equipment items for type:', typeName, error);
      }
    }
  };

  const filterEquipment = () => {
    if (!equipmentItems.length) return;

    let filtered = equipmentItems;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.type.toLowerCase().includes(searchLower) ||
        item.serialNumber.toLowerCase().includes(searchLower) ||
        item.model.toLowerCase().includes(searchLower) ||
        item.manufacturer.toLowerCase().includes(searchLower)
      );
    }

    groupEquipmentByType(filtered);
  };


  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/categories/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const categoriesData = Array.isArray(response.data) ? response.data :
                            response.data.results ? response.data.results : [];
      const categoryOptions = [
        { value: 'all', label: 'All Categories' },
        ...categoriesData.map(cat => ({
          value: cat.id,
          label: cat.name,
          // Use cached value since backend doesn't support it yet
          show_in_equipment: cat.show_in_equipment || getCachedSetting(cat.id),
          description: cat.description || ''
        }))
      ];
      setCategories(categoryOptions);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([{ value: 'all', label: 'All Categories' }]);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const subcategoriesData = Array.isArray(response.data) ? response.data :
                                response.data.results ? response.data.results : [];
      setSubcategories(subcategoriesData);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/custom-fields/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const fieldsData = Array.isArray(response.data) ? response.data : 
                        response.data.results ? response.data.results : [];
      setCustomFields(fieldsData);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setCustomFields([]);
    }
  };

  const updateIndividualItem = async (itemId, individualItemId, updateData) => {
    try {
      const payload = {
        item_id: individualItemId,
        custom_fields: updateData.custom_fields || {},
        serial_number: updateData.serial_number || '',
        notes: updateData.notes || ''
      };

      // Add retired field if provided
      if (updateData.retired !== undefined) {
        payload.retired = updateData.retired;
      }

      // Add status field if provided
      if (updateData.status !== undefined) {
        payload.status = updateData.status;
      }

      const response = await axios.post(`${API_BASE_URL}/api/warehouse/items/${itemId}/update_individual_item/`, payload, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error updating individual item:', error);
      throw error;
    }
  };

  const handleExportExcel = async () => {
    setShowExportModal(true);
  };

  const handleEnhancedExport = () => {
    // Determine which items to export
    let itemsToExport = [];

    if (exportConfig.scope === 'all') {
      itemsToExport = allItemsUnfiltered;
    } else if (exportConfig.scope === 'filtered') {
      itemsToExport = sortedItems;
    } else if (exportConfig.scope === 'selected') {
      itemsToExport = allItemsUnfiltered.filter(item => selectedItems.includes(item.id));
    }

    if (itemsToExport.length === 0) {
      alert('No items to export');
      return;
    }

    // Filter columns based on selection
    const exportData = itemsToExport.map(item => {
      const row = {};
      if (exportConfig.columns.name) row['Item Name'] = item.name;
      if (exportConfig.columns.sku) row['SKU'] = item.sku || '';
      if (exportConfig.columns.category) row['Category'] = item.category;
      if (exportConfig.columns.subcategory) row['Subcategory'] = item.subcategory_name || '';
      if (exportConfig.columns.quantity) row['Quantity'] = item.quantity;
      if (exportConfig.columns.unit) row['Unit'] = item.unit;
      if (exportConfig.columns.unitPrice) row['Unit Price'] = item.unitPrice;
      if (exportConfig.columns.location) row['Location'] = item.location || '';
      if (exportConfig.columns.status) row['Status'] = item.status;
      if (exportConfig.columns.lastUpdated) row['Last Updated'] = item.lastUpdated;
      return row;
    });

    // Export based on format
    if (exportConfig.format === 'excel' || exportConfig.format === 'csv') {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventory');

      const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
      const extension = exportConfig.format === 'excel' ? 'xlsx' : 'csv';
      const filename = `inventory_${exportConfig.scope}_${timestamp}.${extension}`;

      if (exportConfig.format === 'csv') {
        XLSX.writeFile(wb, filename, { bookType: 'csv' });
      } else {
        XLSX.writeFile(wb, filename);
      }
    } else if (exportConfig.format === 'pdf') {
      // PDF Export
      const doc = new jsPDF('l', 'mm', 'a4'); // landscape

      // Add Polish font (Roboto)
      doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.setFont('Roboto');

      // Add header
      doc.setFontSize(18);
      doc.text('Inventory Export', 14, 15);
      doc.setFontSize(10);
      doc.text(`Export Date: ${new Date().toLocaleDateString()}`, 14, 22);
      doc.text(`Scope: ${exportConfig.scope}`, 14, 27);
      doc.text(`Total Items: ${itemsToExport.length}`, 14, 32);

      // Prepare table data
      const headers = Object.keys(exportData[0] || {});
      const rows = exportData.map(item => headers.map(header => item[header] || ''));

      // Add table
      doc.autoTable({
        head: [headers],
        body: rows,
        startY: 38,
        styles: {
          fontSize: 8,
          font: 'Roboto'
        },
        headStyles: {
          fillColor: [59, 130, 246],
          font: 'Roboto',
          fontStyle: 'normal'
        }
      });

      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        const pageHeight = doc.internal.pageSize.height;
        doc.text('printed from ventro.energy system - warehouse module', 14, pageHeight - 10);
      }

      const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
      doc.save(`inventory_${exportConfig.scope}_${timestamp}.pdf`);
    }

    setShowExportModal(false);
    alert(`Successfully exported ${itemsToExport.length} items as ${exportConfig.format.toUpperCase()}`);
  };

  const fetchInventoryData = async (page = 1) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/?page=${page}&page_size=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      // Handle paginated response from backend
      const itemsData = Array.isArray(response.data) ? response.data :
                       response.data.results ? response.data.results : [];

      // Update total count for pagination
      if (response.data.count !== undefined) {
        setTotalCount(response.data.count);
      }

      // Update current page
      setCurrentPage(page);

      const formattedItems = itemsData.map(item => {
        // Build full URL for item picture if it exists
        let itemPictureUrl = null;

        if (item.item_picture) {
          // If it's already a full URL, use it as is
          if (item.item_picture.startsWith('http://') || item.item_picture.startsWith('https://')) {
            itemPictureUrl = item.item_picture;
          } else {
            // Otherwise, prepend the API base URL
            itemPictureUrl = `${API_BASE_URL}${item.item_picture.startsWith('/') ? '' : '/'}${item.item_picture}`;
          }
        }

        const mappedItem = {
          id: `warehouse_${item.id}`,
          originalId: item.id,
          name: item.name,
          category: item.category_name,
          subcategory_name: item.subcategory_name,
          quantity: item.quantity_in_stock || 0,
          quantityInStock: item.quantity_in_stock || 0,
          minStock: item.minimum_stock_level || 0,
          unit: item.unit,
          unitPrice: parseFloat(item.unit_price || 0),
          location: item.location,
          status: item.stock_status,
          lastUpdated: item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A',
          sku: item.sku,
          ownerCompany: item.owner_company_name || 'Legacy Item',
          isShared: item.is_shared || false,
          sharedWithCompanies: item.shared_with_company_names || [],
          customFields: item.custom_fields || [],
          requires_individual_tracking: item.requires_individual_tracking,
          item_picture: itemPictureUrl,
          type: 'warehouse',
          serialNumber: null
        };

        return mappedItem;
      });

      // Fetch equipment and combine with warehouse items for display
      try {
        const groupedEquipmentArray = await fetchEquipmentData();
        const displayCombinedItems = [...formattedItems, ...groupedEquipmentArray];
        console.log('[fetchInventoryData] Combined items for display:', displayCombinedItems.length);
        setItems(displayCombinedItems);
      } catch (equipError) {
        console.error('[fetchInventoryData] Error loading equipment, showing only warehouse items:', equipError);
        setItems(formattedItems);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentData = async () => {
    try {
      const equipmentAPI = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;
      const config = {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      };

      // Use new optimized grouped endpoint
      const groupedResponse = await axios.get(`${equipmentAPI}/safetyequipment/grouped/`, config);
      const groupedData = Array.isArray(groupedResponse.data) ? groupedResponse.data : [];

      console.log(`[Equipment] Fetched ${groupedData.length} equipment groups (optimized endpoint)`);

      // Transform grouped data to match expected format
      const formattedGroups = groupedData.map(group => {
        // Build picture URL
        let pictureUrl = null;
        if (group.picture) {
          if (group.picture.startsWith('http://') || group.picture.startsWith('https://')) {
            pictureUrl = group.picture;
          } else {
            pictureUrl = `${API_BASE_URL}${group.picture.startsWith('/') ? '' : '/'}${group.picture}`;
          }
        }

        // Format locations
        let locationDisplay = 'Warehouse';
        if (group.locations && group.locations.length > 0) {
          locationDisplay = group.locations.join(', ');
        }

        return {
          id: `group_${group.type_name.toLowerCase().replace(/\s+/g, '_')}`,
          name: group.type_name,
          category: 'Equipment',
          quantity: group.total_count,
          unit: 'pcs',
          unitPrice: 0,
          location: locationDisplay,
          status: 'equipment_group',
          lastUpdated: 'N/A',
          ownerCompany: 'Equipment Management',
          isShared: false,
          sharedWithCompanies: [],
          type: 'equipment_group',
          serialNumber: null,
          equipmentItems: [], // Will be loaded on demand when expanded
          totalCount: group.total_count,
          availableCount: group.available_count,
          globalTypeId: group.global_type_id,  // Use global_type_id for proper grouping
          item_picture: pictureUrl
        };
      });

      setGroupedEquipment(formattedGroups);
      return formattedGroups;
    } catch (error) {
      console.error('Error fetching equipment data:', error);
      setEquipmentItems([]);
      setGroupedEquipment([]);
      return [];
    }
  };

  const fetchEquipmentCustomFields = async () => {
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      };

      const response = await axios.get(`${API_BASE_URL}/api/equipment-custom-fields/`, config);
      const customFieldsData = Array.isArray(response.data) ? response.data :
                              response.data.results ? response.data.results : [];

      setEquipmentCustomFields(customFieldsData);
      return customFieldsData;
    } catch (error) {
      console.error('Error fetching equipment custom fields:', error);
      setEquipmentCustomFields([]);
      return [];
    }
  };

  // Helper function to get custom fields for a specific equipment type
  const getEquipmentCustomFieldsForType = (equipmentTypeId) => {
    return equipmentCustomFields.filter(field =>
      field.equipment_type === equipmentTypeId || field.equipment_type === parseInt(equipmentTypeId)
    );
  };

  // Inline serial number editing functions
  const startEditingSerialNumber = (individualItem, parentItem) => {
    const itemKey = `${parentItem.id}-${individualItem.id}`;
    setEditingSerialNumber(itemKey);
    setTempSerialNumber(individualItem.serial_number || '');
  };

  const saveSerialNumber = async (individualItem, parentItem) => {
    try {
      const parentItemId = parentItem.id;
      const originalId = parentItemId ? parentItemId.replace('warehouse_', '') : null;

      if (!originalId) {
        alert('Error: Cannot find parent item');
        return;
      }

      await updateIndividualItem(originalId, individualItem.id, {
        serial_number: tempSerialNumber,
        notes: individualItem.notes || '',
        custom_fields: individualItem.custom_fields || {}
      });

      // Update local state
      setIndividualItemsData(prev => ({
        ...prev,
        [parentItemId]: prev[parentItemId]?.map(item =>
          item.id === individualItem.id
            ? { ...item, serial_number: tempSerialNumber }
            : item
        ) || []
      }));

      setEditingSerialNumber(null);
      setTempSerialNumber('');
    } catch (error) {
      console.error('Error updating serial number:', error);
      alert('Error updating serial number. Please try again.');
    }
  };

  const cancelSerialNumberEdit = () => {
    setEditingSerialNumber(null);
    setTempSerialNumber('');
  };

  // Inline custom field editing functions
  const startEditingCustomField = (individualItem, parentItem, fieldName, currentValue) => {
    const itemKey = `${parentItem.id}-${individualItem.id}-${fieldName}`;
    setEditingCustomField(itemKey);
    setTempCustomFieldValue(currentValue || '');
  };

  const saveCustomField = async (individualItem, parentItem, fieldName) => {
    try {
      const parentItemId = parentItem.id;
      const originalId = parentItemId ? parentItemId.replace('warehouse_', '') : null;

      if (!originalId) {
        alert('Error: Cannot find parent item');
        return;
      }

      const updatedCustomFields = {
        ...individualItem.custom_fields,
        [fieldName]: tempCustomFieldValue
      };

      await updateIndividualItem(originalId, individualItem.id, {
        serial_number: individualItem.serial_number || '',
        notes: individualItem.notes || '',
        custom_fields: updatedCustomFields
      });

      // Update local state
      setIndividualItemsData(prev => ({
        ...prev,
        [parentItemId]: prev[parentItemId]?.map(item =>
          item.id === individualItem.id
            ? { ...item, custom_fields: updatedCustomFields }
            : item
        ) || []
      }));

      setEditingCustomField(null);
      setTempCustomFieldValue('');
    } catch (error) {
      console.error('Error updating custom field:', error);
      alert('Error updating field. Please try again.');
    }
  };

  const cancelCustomFieldEdit = () => {
    setEditingCustomField(null);
    setTempCustomFieldValue('');
  };

  // Fetch ALL warehouse items for the allItemsUnfiltered state (used in Add More Items modal)
  const fetchAllWarehouseItems = async () => {
    try {
      console.log('[fetchAllWarehouseItems] Fetching all warehouse items...');

      let allItems = [];
      let page = 1;
      let hasMore = true;

      // Backend has max page size limit, so we need to fetch all pages
      while (hasMore) {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/?page=${page}&page_size=1000`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        const itemsData = Array.isArray(response.data) ? response.data :
                         response.data.results ? response.data.results : [];

        allItems = [...allItems, ...itemsData];

        // Check if there's a next page
        hasMore = response.data.next !== null && response.data.next !== undefined;

        if (hasMore) {
          page++;
          console.log(`[fetchAllWarehouseItems] Fetched page ${page-1}, total so far: ${allItems.length}, fetching next page...`);
        }
      }

      console.log('[fetchAllWarehouseItems] Fetched all', allItems.length, 'warehouse items across', page, 'pages');
      const itemsData = allItems;

      const formattedItems = itemsData.map(item => {
        // Build full URL for item picture if it exists
        let itemPictureUrl = null;

        if (item.item_picture) {
          // If it's already a full URL, use it as is
          if (item.item_picture.startsWith('http://') || item.item_picture.startsWith('https://')) {
            itemPictureUrl = item.item_picture;
          } else {
            // Otherwise, prepend the API base URL
            itemPictureUrl = `${API_BASE_URL}${item.item_picture.startsWith('/') ? '' : '/'}${item.item_picture}`;
          }
        }

        const mappedItem = {
          id: `warehouse_${item.id}`,
          originalId: item.id,
          name: item.name,
          category: item.category_name,
          subcategory_name: item.subcategory_name,
          quantity: item.quantity_in_stock || 0,
          quantityInStock: item.quantity_in_stock || 0,
          minStock: item.minimum_stock_level || 0,
          unit: item.unit,
          unitPrice: parseFloat(item.unit_price || 0),
          location: item.location,
          status: item.stock_status,
          lastUpdated: item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A',
          sku: item.sku,
          ownerCompany: item.owner_company_name || 'Legacy Item',
          isShared: item.is_shared || false,
          sharedWithCompanies: item.shared_with_company_names || [],
          customFields: item.custom_fields || [],
          requires_individual_tracking: item.requires_individual_tracking,
          item_picture: itemPictureUrl,
          type: 'warehouse',
          serialNumber: null
        };

        return mappedItem;
      });

      return formattedItems;
    } catch (error) {
      console.error('Error fetching all warehouse items:', error);
      return [];
    }
  };

  const combineWithEquipment = async (allWarehouseItems, displayWarehouseItems = null) => {
    // If displayWarehouseItems is not provided, use allWarehouseItems for both
    const itemsToDisplay = displayWarehouseItems || allWarehouseItems;

    try {
      // Fetch already-grouped equipment data from optimized endpoint
      const groupedEquipmentArray = await fetchEquipmentData();

      console.log('[Equipment] Using optimized grouped equipment data:', groupedEquipmentArray.map(g => ({
        name: g.name,
        totalCount: g.totalCount,
        availableCount: g.availableCount,
        quantity: g.quantity
      })));

      // Combine ALL items for allItemsUnfiltered (used in Add More Items modal)
      const allCombinedItems = [...allWarehouseItems, ...groupedEquipmentArray];
      console.log('[combineWithEquipment] Total items in allCombinedItems:', allCombinedItems.length);
      console.log('[combineWithEquipment] Warehouse items:', allWarehouseItems.length);
      console.log('[combineWithEquipment] Equipment groups:', groupedEquipmentArray.length);
      setAllItemsUnfiltered(allCombinedItems); // Store ALL unfiltered items

      // Combine display items for items state (current page view)
      const displayCombinedItems = [...itemsToDisplay, ...groupedEquipmentArray];
      setItems(displayCombinedItems);
    } catch (error) {
      console.error('Error combining data:', error);
      setItems(itemsToDisplay);
      setAllItemsUnfiltered(allWarehouseItems); // Store original unfiltered items even on error
    }
  };

  // Lazy load all items for "Add More Items" modal search functionality
  const ensureAllItemsLoaded = async () => {
    // If already loaded or currently loading, don't reload
    if (hasLoadedAllItems || loadingAllItems) {
      console.log('[ensureAllItemsLoaded] Items already loaded or loading, skipping...');
      return;
    }

    try {
      console.log('[ensureAllItemsLoaded] Loading all items for search...');
      setLoadingAllItems(true);

      // Fetch all warehouse items
      const allWarehouseItems = await fetchAllWarehouseItems();

      // Fetch all equipment items
      const groupedEquipmentArray = await fetchEquipmentData();

      // Combine all items
      const allCombinedItems = [...allWarehouseItems, ...groupedEquipmentArray];
      console.log('[ensureAllItemsLoaded] Loaded', allCombinedItems.length, 'total items for search');

      setAllItemsUnfiltered(allCombinedItems);
      setHasLoadedAllItems(true);
    } catch (error) {
      console.error('[ensureAllItemsLoaded] Error loading all items:', error);
    } finally {
      setLoadingAllItems(false);
    }
  };

  const groupEquipmentByType = (equipment) => {
    const grouped = equipment.reduce((acc, item) => {
      const typeKey = item.type;
      if (!acc[typeKey]) {
        acc[typeKey] = {
          type: typeKey,
          globalTypeId: item.globalTypeId,
          totalCount: 0,
          availableCount: 0,
          items: []
        };
      }
      acc[typeKey].totalCount += 1;
      if (item.status === 'available') {
        acc[typeKey].availableCount += 1;
      }
      acc[typeKey].items.push(item);
      return acc;
    }, {});

    const groupedArray = Object.values(grouped).sort((a, b) => a.type.localeCompare(b.type));
    setGroupedEquipment(groupedArray);
  };


  const fetchFilteredItems = async () => {
    try {
      let url = `${API_BASE_URL}/api/warehouse/items/?page=${currentPage}&page_size=${itemsPerPage}&`;
      if (searchTerm) {
        url += `search=${encodeURIComponent(searchTerm)}&`;
      }
      if (filterCategory !== 'all') {
        url += `category=${filterCategory}&`;
      }
      if (filterSubcategory !== 'all') {
        url += `subcategory=${filterSubcategory}&`;
      }
      if (filterStatus !== 'all') {
        url += `status=${filterStatus}&`;
      }

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const itemsData = Array.isArray(response.data) ? response.data :
                       response.data.results ? response.data.results : [];

      // Update total count for filtered results
      if (response.data.count !== undefined) {
        setTotalCount(response.data.count);
      } else {
        // If no count in response, use the length of results
        setTotalCount(itemsData.length);
      }

      const formattedItems = itemsData.map(item => {
        // Build full URL for item picture if it exists
        let itemPictureUrl = null;

        if (item.item_picture) {
          // If it's already a full URL, use it as is
          if (item.item_picture.startsWith('http://') || item.item_picture.startsWith('https://')) {
            itemPictureUrl = item.item_picture;
          } else {
            // Otherwise, prepend the API base URL
            itemPictureUrl = `${API_BASE_URL}${item.item_picture.startsWith('/') ? '' : '/'}${item.item_picture}`;
          }
        }

        return {
          id: `warehouse_${item.id}`,
          originalId: item.id,
          name: item.name,
          category: item.category_name,
          subcategory_name: item.subcategory_name,
          quantity: item.quantity_in_stock || 0,
          quantityInStock: item.quantity_in_stock || 0,
          minStock: item.minimum_stock_level || 0,
          unit: item.unit,
          unitPrice: parseFloat(item.unit_price || 0),
          location: item.location,
          status: item.stock_status,
          lastUpdated: item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A',
          sku: item.sku,
          ownerCompany: item.owner_company_name || 'Legacy Item',
          isShared: item.is_shared || false,
          sharedWithCompanies: item.shared_with_company_names || [],
          customFields: item.custom_fields || [],
          requires_individual_tracking: item.requires_individual_tracking,
          item_picture: itemPictureUrl,
          type: 'warehouse',
          serialNumber: null
        };
      });

      // Fetch equipment and combine with warehouse items for display ONLY if no search/filters active
      if (!searchTerm && filterCategory === 'all' && filterSubcategory === 'all' && filterStatus === 'all') {
        try {
          const groupedEquipmentArray = await fetchEquipmentData();
          const displayCombinedItems = [...formattedItems, ...groupedEquipmentArray];
          console.log('[fetchFilteredItems] Combined items for display:', displayCombinedItems.length);
          setItems(displayCombinedItems);
        } catch (equipError) {
          console.error('[fetchFilteredItems] Error loading equipment, showing only warehouse items:', equipError);
          setItems(formattedItems);
        }
      } else {
        // When searching or filtering, show only warehouse items
        console.log('[fetchFilteredItems] Search/filter active, showing only warehouse items:', formattedItems.length);
        setItems(formattedItems);
      }

      // Note: allItemsUnfiltered is now loaded lazily when user clicks "Add More Items"
      // This improves initial page load performance
    } catch (error) {
      console.error('Error fetching filtered items:', error);
    }
  };

  // Fetch cars for car inventory
  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/cars/?page_size=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const allCars = Array.isArray(response.data) ? response.data : response.data.results || [];
      // Filter only cars (equipment_type === 'car'), exclude machines and other equipment
      const carsOnly = allCars.filter(car => car.equipment_type === 'car');
      setCars(carsOnly);
      return carsOnly;
    } catch (error) {
      console.error('Error fetching cars:', error);
      return [];
    }
  };

  // Fetch car inventory assignments
  const fetchCarInventoryAssignments = async (carId = null) => {
    try {
      setCarInventoryLoading(true);
      let url = `${API_BASE_URL}/api/warehouse/car-inventory/`;
      if (carId) {
        url = `${API_BASE_URL}/api/warehouse/car-inventory/by_car/?car_id=${carId}`;
      }
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const assignments = Array.isArray(response.data) ? response.data : response.data.results || [];
      setCarInventoryAssignments(assignments);
      return assignments;
    } catch (error) {
      console.error('Error fetching car inventory assignments:', error);
      return [];
    } finally {
      setCarInventoryLoading(false);
    }
  };

  // Assign item to car
  const handleAssignToCar = async (carId, itemId, quantity, notes = '', responsiblePersonId1 = null, responsiblePersonId2 = null) => {
    try {
      // Extract original ID if it has warehouse_ prefix
      const originalItemId = itemId.toString().startsWith('warehouse_')
        ? itemId.toString().replace('warehouse_', '')
        : itemId;

      const requestData = {
        car: carId,
        item: originalItemId,
        quantity: quantity,
        notes: notes
      };

      // Add responsible persons if provided
      if (responsiblePersonId1) {
        requestData.responsible_person = responsiblePersonId1;
      }
      if (responsiblePersonId2) {
        requestData.responsible_person_2 = responsiblePersonId2;
      }

      console.log('Sending car assignment request:', requestData);

      await axios.post(`${API_BASE_URL}/api/warehouse/car-inventory/`, requestData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      setToast({ type: 'success', message: 'Item assigned to car!' });
      fetchCarInventoryAssignments(selectedCar);
      fetchFilteredItems(); // Refresh inventory
    } catch (error) {
      console.error('Error assigning item to car:', error);
      console.error('Response data:', error.response?.data);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.response?.data?.item?.[0] ||
                          error.response?.data?.car?.[0] ||
                          error.response?.data?.quantity?.[0] ||
                          JSON.stringify(error.response?.data) ||
                          'Error assigning item to car.';
      setToast({ type: 'error', message: errorMessage });
    }
  };

  // Return item from car
  const handleReturnFromCar = async (assignmentId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/warehouse/car-inventory/${assignmentId}/return_item/`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setToast({ type: 'success', message: 'Item returned!' });
      fetchCarInventoryAssignments(selectedCar);
      fetchFilteredItems(); // Refresh inventory
    } catch (error) {
      console.error('Error returning item from car:', error);
      setToast({ type: 'error', message: 'Error returning item. Please try again.' });
    }
  };

  // Mark item as lost
  const handleMarkLost = async (assignmentId) => {
    if (window.confirm('Are you sure you want to mark this item as lost?')) {
      try {
        await axios.post(`${API_BASE_URL}/api/warehouse/car-inventory/${assignmentId}/mark_lost/`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        setToast({ type: 'success', message: 'Item marked as lost' });
        fetchCarInventoryAssignments(selectedCar);
        fetchFilteredItems();
      } catch (error) {
        console.error('Error marking item as lost:', error);
        setToast({ type: 'error', message: 'Error marking item as lost' });
      }
    }
  };

  // Mark item as damaged
  const handleMarkDamaged = async (assignmentId) => {
    if (window.confirm('Are you sure you want to mark this item as damaged?')) {
      try {
        await axios.post(`${API_BASE_URL}/api/warehouse/car-inventory/${assignmentId}/mark_damaged/`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        setToast({ type: 'success', message: 'Item marked as damaged' });
        fetchCarInventoryAssignments(selectedCar);
        fetchFilteredItems();
      } catch (error) {
        console.error('Error marking item as damaged:', error);
        setToast({ type: 'error', message: 'Error marking item as damaged' });
      }
    }
  };

  // Fetch hazardous items for a specific car
  const fetchCarHazardousItems = async (carId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/car-inventory/hazardous_items/?car_id=${carId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching hazardous items:', error);
      return { items: [], total_hazardous_items: 0 };
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        // Extract original ID by removing warehouse_ prefix
        const itemIdStr = itemId ? itemId.toString() : '';
        const originalId = itemIdStr.startsWith('warehouse_') ? itemIdStr.replace('warehouse_', '') : itemId;
        await axios.delete(`${API_BASE_URL}/api/warehouse/items/${originalId}/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        fetchInventoryData(); // Refresh the list
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  const handleAddItem = async (itemData, invoiceFile = null, itemPicture = null) => {
    try {
      let response;

      if (invoiceFile || itemPicture) {
        // If there's any file, use FormData
        const formData = new FormData();

        // Add all item data fields to FormData
        Object.keys(itemData).forEach(key => {
          if (key === 'custom_fields_data') {
            // Handle custom fields data as JSON
            formData.append(key, JSON.stringify(itemData[key]));
          } else if (itemData[key] !== null && itemData[key] !== undefined) {
            // Only append non-null values to avoid "null" strings
            formData.append(key, itemData[key]);
            if (key === 'requires_individual_tracking') {
            }
          }
          // Skip null/undefined values - they won't be sent
        });

        // Add the invoice file if present
        if (invoiceFile) {
          formData.append('invoice_file', invoiceFile);
        }

        // Add the item picture if present
        if (itemPicture) {
          formData.append('item_picture', itemPicture);
        }

        response = await axios.post(`${API_BASE_URL}/api/warehouse/items/`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // No file, use JSON
        response = await axios.post(`${API_BASE_URL}/api/warehouse/items/`, itemData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
      }


      // If item has custom fields and requires individual tracking, generate individual items
      if (itemData.requires_individual_tracking && response.data.id) {
        try {
          const indivResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/${response.data.id}/individual_items/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
        } catch (error) {
          console.error('❌ Error generating individual items:', error);
          // Don't fail the whole operation if this fails
        }
      }

      fetchInventoryData(); // Refresh the list
      setShowAddModal(false);
      alert('Item added successfully!');
    } catch (error) {
      console.error('Error adding item:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          JSON.stringify(error.response?.data) ||
                          'Error adding item. Please try again.';
      alert(`Error adding item: ${errorMessage}`);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleShareItem = (item) => {
    setSharingItem(item);
    setShowSharingModal(true);
  };

  const handleShareEquipmentGroup = (equipmentGroup) => {
    setSharingEquipmentGroup(equipmentGroup);
    setShowEquipmentSharingModal(true);
  };

  const handleSetEquipmentGroupLocation = (equipmentGroup) => {
    setLocationEquipmentGroup(equipmentGroup);
    setShowEquipmentLocationModal(true);
  };

  // Sidebar category click handler
  const handleSidebarCategoryClick = async (category) => {
    setSelectedSidebarCategory(category);
    setFilterCategory(category.value);
    setFilterSubcategory('all');
    setShowEquipmentOnly(false);

    // Fetch subcategories for this category
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/?category=${category.value}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const subcategoriesData = Array.isArray(response.data) ? response.data : response.data.results || [];
      setSidebarSubcategories(subcategoriesData);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSidebarSubcategories([]);
    }
    // useEffect will trigger fetchFilteredItems automatically
  };

  const handleSidebarSubcategoryClick = (subcategory) => {
    setFilterSubcategory(subcategory.id);
    // useEffect will trigger fetchFilteredItems automatically
  };

  // Handler for FileTree item clicks
  const handleTreeItemClick = (item) => {
    if (item.id === 'all-items') {
      // Show all items
      setSelectedSidebarCategory(null);
      setFilterCategory('all');
      setFilterSubcategory('all');
      setShowEquipmentOnly(false);
      setSidebarSubcategories([]);
    } else if (item.id === 'equipment') {
      // Show equipment
      setShowEquipmentOnly(true);
      setSelectedSidebarCategory(null);
      setFilterCategory('all');
      setFilterSubcategory('all');
      setSidebarSubcategories([]);
    } else if (item.subcategoryId) {
      // Subcategory clicked
      setFilterSubcategory(item.subcategoryId);
    } else if (!item.isRoot) {
      // Category clicked
      const category = categories.find(cat => cat.value === item.id);
      if (category) {
        handleSidebarCategoryClick(category);
      }
    }
  };

  const handleDownloadTemplate = () => {
    // Create template data with example rows
    const templateData = [
      {
        'Item Name': 'Example Item 1',
        'Internal Number (SKU)': 'SKU001',
        'Category': 'Materials',
        'Subcategory': 'Raw Materials',
        'Unit': 'pcs',
        'Unit Price': '10.50',
        'Initial Stock Quantity': '100',
        'Minimum Stock Level': '10',
        'Location': 'A-01-A',
        'Supplier': 'Example Supplier Ltd',
        'Description': 'Example item description'
      },
      {
        'Item Name': 'Example Item 2',
        'Internal Number (SKU)': 'SKU002',
        'Category': 'Tools',
        'Subcategory': 'Hand Tools',
        'Unit': 'pcs',
        'Unit Price': '25.00',
        'Initial Stock Quantity': '50',
        'Minimum Stock Level': '5',
        'Location': 'B-02-C',
        'Supplier': 'Tool Supplier Inc',
        'Description': 'Another example item'
      }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Item Name
      { wch: 20 }, // SKU
      { wch: 15 }, // Category
      { wch: 18 }, // Subcategory
      { wch: 10 }, // Unit
      { wch: 12 }, // Unit Price
      { wch: 20 }, // Initial Stock
      { wch: 20 }, // Min Stock
      { wch: 15 }, // Location
      { wch: 25 }, // Supplier
      { wch: 40 }  // Description
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items Template');

    // Add instructions sheet
    const instructions = [
      ['Import Instructions'],
      [''],
      ['Required Fields:'],
      ['- Item Name: Name of the item (required)'],
      ['- Category: Choose from existing categories or create new (required)'],
      ['- Unit: pcs, kg, m, l, etc. (required)'],
      ['- Unit Price: Price per unit (required)'],
      [''],
      ['Optional Fields:'],
      ['- Internal Number (SKU): Internal reference code'],
      ['- Subcategory: Subcategory name (will be created if doesn\'t exist under the category)'],
      ['- Initial Stock Quantity: Starting quantity (default: 0)'],
      ['- Minimum Stock Level: Alert threshold (default: 0)'],
      ['- Location: Warehouse location code (e.g., A-01-A)'],
      ['- Supplier: Supplier name'],
      ['- Description: Item description'],
      [''],
      ['Notes:'],
      ['- Remove example rows before importing your data'],
      ['- Categories must exist in the system or will be created'],
      ['- Subcategories will be automatically created under specified category'],
      ['- Location codes should match your warehouse structure'],
      ['- Prices should be numeric values without currency symbols']
    ];

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
    wsInstructions['!cols'] = [{ wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

    // Download file
    XLSX.writeFile(wb, 'warehouse_items_template.xlsx');
  };

  const handleRestock = (item) => {
    setRestockingItem(item);
    setShowRestockModal(true);
  };

  const handleShowHistory = async (item) => {
    setHistoryItem(item);
    setHistoryData([]);
    setShowHistoryModal(true);

    try {
      // Extract original ID
      const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/${originalId}/history/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setHistoryData(response.data);
    } catch (error) {
      console.error('Error fetching item history:', error);
      alert('Error fetching item history');
    }
  };

  const handleManualReturn = (individualItem, parentItem) => {
    setManualReturnItem({
      individualItem,
      parentItem
    });
    setShowManualReturnModal(true);
  };

  const confirmManualReturn = async () => {
    if (!manualReturnItem) return;

    const { individualItem, parentItem } = manualReturnItem;

    try {
      // Update individual item status to 'in_stock' (returned)
      const itemType = individualItem.type || 'warehouse';

      if (itemType === 'equipment') {
        // For equipment items - use PUT endpoint
        await axios.put(
          `${API_BASE_URL}/api/safetyequipment/${individualItem.id}/`,
          { safety_equipment_status: 'available' },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // For warehouse individual items - need to find the warehouse-issue-item first
        console.log('Finding warehouse issue for individual item:', individualItem);

        // Get all warehouse issues
        const issuesResponse = await axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        const allIssues = issuesResponse.data;
        console.log('All warehouse issues:', allIssues);

        // Find the issue-item that contains this individual item
        let foundIssueItemId = null;
        for (const issue of allIssues) {
          if (issue.items && Array.isArray(issue.items)) {
            for (const issueItem of issue.items) {
              // Check if this issue-item contains our individual item
              if (issueItem.individual_items && Array.isArray(issueItem.individual_items)) {
                const matchingIndividual = issueItem.individual_items.find(
                  ind => ind.serial_number === individualItem.serial_number ||
                         ind.id === individualItem.id
                );
                if (matchingIndividual) {
                  foundIssueItemId = issueItem.id;
                  console.log('Found matching issue-item:', issueItem);
                  break;
                }
              }
            }
          }
          if (foundIssueItemId) break;
        }

        if (!foundIssueItemId) {
          throw new Error('Could not find warehouse issue for this item. It may have been deleted.');
        }

        // Use the same endpoint as warehouse/history - return_items
        await axios.post(
          `${API_BASE_URL}/api/warehouse/warehouse-issue-items/${foundIssueItemId}/return_items/`,
          {
            items: [{
              custom_fields: individualItem.custom_fields || {},
              serial_number: individualItem.serial_number || '',
              condition: 'good',
              notes: 'Manual return from inventory'
            }]
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      alert('Item successfully returned to warehouse!');
      setShowManualReturnModal(false);
      setManualReturnItem(null);

      // Refresh the items list
      await fetchInventoryData();

      // Refresh individual items if expanded
      if (expandedItems.has(parentItem.id)) {
        handleToggleIndividualItems(parentItem);
        setTimeout(() => handleToggleIndividualItems(parentItem), 100);
      }
    } catch (error) {
      console.error('Error performing manual return:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to return item: ${error.message || 'Please try again or contact support.'}`);
    }
  };

  const handleToggleIndividualItems = async (item) => {
    // Only show individual items for warehouse items (not equipment groups)
    if (item.type === 'equipment_group') return;

    // Only show individual items if tracking is enabled
    if (!item.requires_individual_tracking) {
      return; // Don't expand if tracking disabled or not set
    }

    const itemId = item.id;
    const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);

    // If already expanded, collapse it
    if (expandedItems.has(itemId)) {
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      return;
    }

    // Expand and fetch individual items if not already loaded
    setExpandedItems(prev => new Set(prev.add(itemId)));

    if (!individualItemsData[itemId]) {
      setLoadingIndividualItems(prev => new Set(prev.add(itemId)));
      try {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/${originalId}/individual_items/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        setIndividualItemsData(prev => ({
          ...prev,
          [itemId]: response.data.individual_items || []
        }));
      } catch (error) {
        console.error('Error fetching individual items:', error);
        setIndividualItemsData(prev => ({
          ...prev,
          [itemId]: []
        }));
      } finally {
        setLoadingIndividualItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }
    }
  };

  const handleEditIndividualItem = (individualItem) => {
    setEditingIndividualItem(individualItem);
    setEditIndividualFormData({
      serial_number: individualItem.serial_number || '',
      notes: individualItem.notes || '',
      custom_fields: individualItem.custom_fields || {}
    });
    setShowEditIndividualModal(true);
  };

  const handleUpdateIndividualItem = async () => {
    if (!editingIndividualItem) return;

    try {
      const parentItemId = editingIndividualItem.parentItemId;

      if (editingIndividualItem.type === 'equipment') {
        // Handle equipment item update
        const updateData = {
          safety_equipment_serial_number: editIndividualFormData.serial_number,
          safety_equipment_comment: editIndividualFormData.notes,
          custom_fields: editIndividualFormData.custom_fields
        };

        const response = await axios.put(`${API_BASE_URL}/api/safetyequipment/${editingIndividualItem.id}/`, updateData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        // Refresh inventory data to update the display
        await fetchInventoryData();

      } else {
        // Handle warehouse item update (original logic)
        const originalId = parentItemId ? parentItemId.replace('warehouse_', '') : null;

        if (!originalId) {
          alert('Error: Cannot find parent item');
          return;
        }

        const response = await updateIndividualItem(originalId, editingIndividualItem.id, editIndividualFormData);

        // Update the local state with new data
        setIndividualItemsData(prev => ({
          ...prev,
          [parentItemId]: response.individual_items || prev[parentItemId]
        }));

        // Refresh to get updated quantity in stock
        await fetchInventoryData();
      }

      setShowEditIndividualModal(false);
      setEditingIndividualItem(null);
      alert('Item updated successfully!');
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Error updating item. Please try again.');
    }
  };

  const handleUpdateItem = async (itemData, itemPicture = null) => {
    console.log('🔧 handleUpdateItem called');
    console.log('📦 itemData:', itemData);
    console.log('🖼️ itemPicture:', itemPicture);
    console.log('📝 custom_fields_data:', itemData.custom_fields_data);

    try {
      // Extract original ID
      const originalId = editingItem.originalId || (editingItem.id ? editingItem.id.replace('warehouse_', '') : editingItem.id);

      let response;

      if (itemPicture && itemPicture !== 'DELETE') {
        // If there's a new picture file, use FormData
        const formData = new FormData();

        // Add all item data fields to FormData
        Object.keys(itemData).forEach(key => {
          if (key === 'custom_fields_data') {
            // Handle custom fields data as JSON
            console.log('✅ Adding custom_fields_data to FormData:', itemData[key]);
            formData.append(key, JSON.stringify(itemData[key]));
          } else if (itemData[key] !== null && itemData[key] !== undefined) {
            // Only append non-null values to avoid "null" strings
            formData.append(key, itemData[key]);
          }
          // Skip null/undefined values - they won't be sent
        });

        // Add the item picture
        formData.append('item_picture', itemPicture);

        response = await axios.put(`${API_BASE_URL}/api/warehouse/items/${originalId}/`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else if (itemPicture === 'DELETE') {
        // User wants to delete the picture
        const dataWithNullPicture = {
          ...itemData,
          item_picture: null
        };
        response = await axios.put(`${API_BASE_URL}/api/warehouse/items/${originalId}/`, dataWithNullPicture, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // No file, use JSON
        response = await axios.put(`${API_BASE_URL}/api/warehouse/items/${originalId}/`, itemData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
      }

      await fetchInventoryData(); // Refresh the list

      // If the item is expanded, refresh its individual items too
      const itemId = `warehouse_${originalId}`;
      if (expandedItems.has(itemId)) {
        try {
          const indivResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/${originalId}/individual_items/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          setIndividualItemsData(prev => ({
            ...prev,
            [itemId]: indivResponse.data.individual_items || []
          }));
        } catch (error) {
          console.error('Error refreshing individual items:', error);
        }
      }

      setShowEditModal(false);
      setEditingItem(null);
      alert('Item updated successfully!');
    } catch (error) {
      console.error('❌ Error updating item:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error response status:', error.response?.status);
      console.error('❌ Request data was:', itemData);
      console.error('❌ Item picture:', itemPicture);

      // Extract error message - handle both detail and field-specific errors
      let errorMessage = 'Error updating item. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for quantity_in_stock specific error (validation error)
        if (errorData.quantity_in_stock) {
          errorMessage = errorData.quantity_in_stock;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      }

      // Show alert for all errors including validation errors
      alert(errorMessage);
    }
  };

  // Bulk selection functions
  const handleSelectItem = (itemId) => {
    console.log('[handleSelectItem] Toggling item:', itemId);

    // Check if the item is in allItemsUnfiltered, if not add it
    setAllItemsUnfiltered(prevAll => {
      const itemExists = prevAll.find(item => item.id === itemId);
      if (!itemExists) {
        // Find the item in current items (filtered view) and add to allItemsUnfiltered
        const itemFromCurrentView = items.find(item => item.id === itemId);
        if (itemFromCurrentView) {
          console.log('[handleSelectItem] Adding item to allItemsUnfiltered:', itemFromCurrentView.name);
          return [...prevAll, itemFromCurrentView];
        }
      }
      return prevAll;
    });

    setSelectedItems(prev => {
      const newSelection = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      console.log('[handleSelectItem] Previous selection:', prev);
      console.log('[handleSelectItem] New selection:', newSelection);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleAssignment = (type, itemId = null) => {
    // If itemId is provided, use it; otherwise use selectedItems
    const itemsToAssign = itemId ? [itemId] : selectedItems;

    console.log('[handleAssignment] type:', type);
    console.log('[handleAssignment] itemId:', itemId);
    console.log('[handleAssignment] selectedItems:', selectedItems);
    console.log('[handleAssignment] itemsToAssign:', itemsToAssign);

    if (itemsToAssign.length === 0) {
      alert('Please select items to assign');
      return;
    }

    // Update selectedItems if itemId was provided
    if (itemId) {
      setSelectedItems([itemId]);
    }

    setAssignmentType(type);
    setShowAssignmentModal(true);
  };

  const handleAssignmentSave = async (assignmentData) => {
    // Generate unique issue number
    const issueNumber = `ASSIGN-${Date.now()}`;
    
    // Prepare warehouse issue data with items_data (backend requires this field)
    const warehouseIssueData = {
        issue_number: issueNumber,
        issue_type: assignmentData.assignmentType, // 'project' or 'person'
        status: 'issued',
        purpose: `Assignment via Inventory Management - ${assignmentData.assignmentType === 'project' ? 'Project' : 'Person'}`,
        notes: assignmentData.notes || '',
        assigned_to_person: assignmentData.selectedPerson ?
          (assignmentData.selectedPersonName || '').trim() || `Person ID: ${assignmentData.selectedPerson}` : '',
        issued_at: new Date().toISOString(),
        items_data: (() => {
          const groupedItems = {};

          assignmentData.items.filter(item => item.quantityToAssign > 0).forEach(item => {
            // Ensure itemId exists
            if (!item.itemId) {
              console.error('Item missing itemId:', item);
              return;
            }

            const itemIdStr = item.itemId.toString();
            let actualItemId;
            let individualItems = [];

            // Handle individual warehouse items
            if (itemIdStr.startsWith('individual_')) {
              const parts = itemIdStr.split('_');
              actualItemId = parseInt(parts[1]);
              const individualId = parts[2];
              individualItems = [{
                id: parseInt(individualId),
                customFieldValues: item.customFieldValues || {}
              }];
            } else {
              // Handle regular warehouse and equipment items
              actualItemId = itemIdStr.startsWith('warehouse_') || itemIdStr.startsWith('equipment_') ?
                parseInt(itemIdStr.replace(/^(warehouse_|equipment_)/, '')) :
                parseInt(item.itemId);
              individualItems = item.individualItems || Array.from({ length: item.quantityToAssign }, () => ({
                customFieldValues: {}
              }));
            }

            // Group by actual item ID
            if (!groupedItems[actualItemId]) {
              groupedItems[actualItemId] = {
                item_id: actualItemId,
                quantity: 0,
                notes: '',
                individual_items: []
              };
            }

            // Accumulate quantity and individual items
            groupedItems[actualItemId].quantity += (itemIdStr.startsWith('individual_') ? 1 : item.quantityToAssign);
            groupedItems[actualItemId].individual_items.push(...individualItems);

            // Combine notes
            if (item.notes) {
              groupedItems[actualItemId].notes += (groupedItems[actualItemId].notes ? '; ' : '') + item.notes;
            }
          });

          return Object.values(groupedItems);
        })()
      };

      // Add project field only for project assignments
      if (assignmentData.assignmentType === 'project' && assignmentData.project && assignmentData.project !== '') {
        warehouseIssueData.project = parseInt(assignmentData.project);
      }
      // For person assignments, project is optional


    try {
      // Always use warehouse-issues endpoint which supports custom fields and individual items
      const issueResponse = await axios.post(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, warehouseIssueData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const createdIssue = issueResponse.data;

      // Items are created automatically by backend through items_data
      // If project assignment with vehicle/driver/shipment, create additional assignments
      if (assignmentData.assignmentType === 'project') {
        const assignmentPromises = [];

        // Vehicle assignment (one per project, not per item)
        if (assignmentData.selectedVehicle && assignmentData.selectedDriver) {
          assignmentPromises.push(
            axios.post(`${API_BASE_URL}/api/vehicle-assignments/`, {
              car: parseInt(assignmentData.selectedVehicle),
              user: parseInt(assignmentData.selectedDriver),
              project: parseInt(assignmentData.project),
              assignment_reason: `Warehouse Assignment - Project`,
              assignment_notes: `Vehicle assigned for project delivery. Issue #${issueNumber}`,
              status: 'assigned',
              mileage_start: null
            }, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Content-Type': 'application/json'
              }
            }).then(() => {
              // Update vehicle status to 'assigned_loading' like in WarehouseIssue.js
              return axios.post(`${API_BASE_URL}/api/vehicles/${assignmentData.selectedVehicle}/change-status/`, {
                status: 'assigned_loading',
                location: 'Warehouse - Loading Bay',
                notes: `Loading items for delivery - Issue #${issueNumber}`
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                  'Content-Type': 'application/json'
                }
              });
            })
          );

          // Create VehicleItemAssignments for each item with quantity > 0
          const itemsToAssign = assignmentData.items.filter(item => item.quantityToAssign > 0);
          
          if (itemsToAssign.length > 0) {
            assignmentPromises.push(
              // First, get the created issue items to get their IDs
              axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/${createdIssue.id}/`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              }).then(issueDetailResponse => {
                const issueItems = issueDetailResponse.data.items || [];
                
                // Create VehicleItemAssignments for each item
                const vehicleItemPromises = itemsToAssign.map(assignItem => {
                  // Find matching issue item by item ID
                  const matchingIssueItem = issueItems.find(issueItem =>
                    issueItem.item === assignItem.itemId
                  );

                  if (matchingIssueItem) {
                    return axios.post(`${API_BASE_URL}/api/warehouse/vehicle-assignments/`, {
                      item: assignItem.itemId, // Required field - original inventory item ID
                      project_issue_item: matchingIssueItem.id, // Optional - link to project issue item
                      vehicle: parseInt(assignmentData.selectedVehicle),
                      quantity: assignItem.quantityToAssign,
                      status: 'assigned',
                      notes: `Assigned via Inventory Management - Issue #${issueNumber}`
                    }, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json'
                      }
                    });
                  } else {
                    console.error(`No matching issue item found for ${assignItem.itemName} (ID: ${assignItem.itemId})`);
                  }
                }).filter(Boolean); // Filter out undefined promises

                return Promise.all(vehicleItemPromises);
              })
            );
          }
        }

        // Wait for all assignments to complete
        if (assignmentPromises.length > 0) {
          await Promise.all(assignmentPromises);
        }
      }

      alert(`Assignment created successfully! Issue number: ${issueNumber}`);
      return createdIssue;

    } catch (error) {
      console.error('Error creating assignment:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      console.error('Request payload was:', warehouseIssueData);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          JSON.stringify(error.response?.data) ||
                          'Failed to create assignment';
      throw new Error(`Assignment failed: ${errorMessage}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'text-green-600 bg-green-100';
      case 'low_stock': return 'text-orange-600 bg-orange-100';
      case 'out_of_stock': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-steel-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_stock': return <CheckCircle className="h-4 w-4" />;
      case 'low_stock': case 'out_of_stock': return <AlertTriangle className="h-4 w-4" />;
      default: return null;
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort items
  const sortedItems = useMemo(() => {
    if (!sortField) return items;

    const sorted = [...items].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle special cases
      if (sortField === 'name') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      } else if (sortField === 'quantity' || sortField === 'unitPrice') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'category') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      } else if (sortField === 'status') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      } else if (sortField === 'location') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [items, sortField, sortDirection]);

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-slate-400" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-brand-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-brand-600" />;
  };

  // Server-side Pagination
  const equipmentCount = sortedItems.filter(item => item.type === 'equipment_group').length;
  const effectiveTotalCount = showEquipmentOnly ? equipmentCount : totalCount;
  const totalPages = Math.ceil(effectiveTotalCount / itemsPerPage);
  const paginatedItems = showEquipmentOnly
    ? sortedItems.filter(item => item.type === 'equipment_group')
    : sortedItems; // Items are already paginated from backend

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Handle page change - update currentPage which triggers fetchFilteredItems via useEffect
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-steel-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-steel-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-steel-300 rounded w-1/2 mb-8"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-steel-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDeleteCategory = async (categoryId, categoryName) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/categories/${categoryId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Update categories list in main component
      setCategories(prev => prev.filter(cat => cat.value != categoryId));
      
      alert('Category deleted successfully!');
      return true; // Success
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.status === 400 
        ? 'Cannot delete category. It may contain items that need to be moved to another category first.'
        : 'Error deleting category. Please try again.';
      alert(errorMessage);
      return false; // Failure
    }
  };

  // Handle equipment item selection
  const handleEquipmentItemToggle = (equipmentItem) => {
    setSelectedEquipmentItems(prev => {
      const isSelected = prev.some(item => item.id === equipmentItem.id);
      if (isSelected) {
        return prev.filter(item => item.id !== equipmentItem.id);
      } else {
        return [...prev, equipmentItem];
      }
    });
  };

  const handleSetLocationForEquipmentItems = () => {
    if (selectedEquipmentItems.length === 0) {
      alert('Please select equipment items first');
      return;
    }
    setShowEquipmentItemsLocationModal(true);
  };

  return (
    <div className="bg-steel-50 min-h-screen">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 transition-all duration-300 ${
          toast.type === 'success'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      <div className="w-full max-w-none">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Inventory Management
              </h1>
              <p className="text-slate-600 mt-2">Manage warehouse items, track stock levels, and maintain inventory</p>
            </div>
            <Dock
              items={[
                {
                  icon: <Search className={showSearch ? "h-5 w-5 text-brand-700" : "h-5 w-5 text-slate-700"} />,
                  label: 'Search',
                  onClick: () => setShowSearch(!showSearch),
                  className: showSearch ? 'dock-item-active' : ''
                },
                {
                  icon: <Filter className="h-5 w-5 text-slate-600" />,
                  label: 'Categories',
                  onClick: () => setShowCategoryModal(true)
                },
                ...(groupedEquipment.length > 0 ? [{
                  icon: <Package className="h-5 w-5 text-green-600" />,
                  label: 'Equipment',
                  onClick: () => navigate('/equipment/management')
                }] : []),
                {
                  icon: <LayoutGrid className="h-5 w-5 text-red-600" />,
                  label: 'Cabinets',
                  onClick: () => navigate('/warehouse/cabinet-configurator')
                },
                {
                  icon: <Download className="h-5 w-5 text-brand-600" />,
                  label: 'Template',
                  onClick: handleDownloadTemplate
                },
                {
                  icon: <Upload className="h-5 w-5 text-brand-600" />,
                  label: 'Import',
                  onClick: () => setShowImportModal(true)
                },
                {
                  icon: <Download className="h-5 w-5 text-green-600" />,
                  label: 'Export',
                  onClick: handleExportExcel
                },
                {
                  icon: <Plus className="h-5 w-5 text-white" />,
                  label: 'Add Item',
                  onClick: () => setShowAddModal(true),
                  className: 'dock-item-primary'
                }
              ]}
              baseItemSize={40}
              magnification={60}
              distance={140}
              panelHeight={50}
            />
          </div>
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div className="flex gap-0">
          {/* Left Sidebar - Categories */}
          <div className="w-52 flex-shrink-0">
            <div className="bg-white rounded-l-xl shadow-sm border-l-2 border-t border-b border-brand-500 sticky top-0">
              <div className="px-3 py-3 bg-brand-50 border-b border-brand-200">
                <h2 className="text-xs font-semibold text-brand-800 uppercase tracking-wider flex items-center">
                  <Layers className="h-4 w-4 mr-2 text-brand-600 flex-shrink-0" />
                  <span className="truncate">Categories</span>
                </h2>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 150px)' }}>
                <FileTree
                  data={treeData}
                  onItemClick={handleTreeItemClick}
                  selectedId={selectedTreeItemId}
                  initialExpandedIds={filterCategory !== 'all' ? [filterCategory] : []}
                />
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 min-w-0">

        {/* Search & Filters */}
        {showSearch && (
          <div className="bg-white rounded-r-xl shadow-sm p-6 border-r-2 border-t border-b border-brand-500 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative search-input-container">
                  <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2 z-10" />
                  <input
                    type="text"
                    placeholder="Search by name, SKU, or location..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setSelectedSuggestionIndex(-1);
                    }}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => {
                      if (searchTerm.length >= 2) {
                        setShowSuggestions(true);
                      }
                    }}
                    className="w-full pl-10 pr-10 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setShowSuggestions(false);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}

                  {/* Search Suggestions Dropdown */}
                  {showSuggestions && (searchSuggestions.length > 0 || recentSearches.length > 0) && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-brand-200 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
                      {/* Recent Searches */}
                      {!searchTerm && recentSearches.length > 0 && (
                        <div className="p-2 border-b border-brand-100">
                          <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Recent Searches</div>
                          {recentSearches.map((recent, index) => (
                            <button
                              key={index}
                              onClick={() => handleSearch(recent)}
                              className="w-full text-left px-3 py-2 hover:bg-steel-100 rounded flex items-center text-sm"
                            >
                              <History className="h-4 w-4 mr-2 text-slate-400" />
                              {recent}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Suggestions */}
                      {searchSuggestions.length > 0 && (
                        <div className="p-2">
                          <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Suggestions</div>
                          {searchSuggestions.map((suggestion, index) => (
                            <button
                              key={`${suggestion.type}-${index}`}
                              onClick={() => handleSelectSuggestion(suggestion)}
                              className={`w-full text-left px-3 py-2 rounded flex items-center justify-between ${
                                index === selectedSuggestionIndex ? 'bg-brand-50' : 'hover:bg-steel-100'
                              }`}
                            >
                              <div className="flex items-center min-w-0 flex-1">
                                <Search className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium text-slate-900 truncate">
                                    {highlightText(suggestion.value, searchTerm)}
                                  </div>
                                  {suggestion.item && (
                                    <div className="text-xs text-slate-500 truncate">
                                      {suggestion.item.sku && `SKU: ${suggestion.item.sku} • `}
                                      {suggestion.item.category} • Stock: {suggestion.item.quantity}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs text-slate-400 bg-steel-100 px-2 py-0.5 rounded ml-2 flex-shrink-0">
                                {suggestion.field}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full md:w-56">
                <div className="relative">
                  <Filter className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <select
                    value={filterCategory}
                    onChange={(e) => {
                      setFilterCategory(e.target.value);
                      setFilterSubcategory('all'); // Reset subcategory when category changes
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="w-full md:w-56">
                <div className="relative">
                  <Layers className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <select
                    value={filterSubcategory}
                    onChange={(e) => setFilterSubcategory(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    disabled={filterCategory === 'all'}
                  >
                    <option value="all">All Subcategories</option>
                    {subcategories
                      .filter(sub => filterCategory === 'all' || sub.category === parseInt(filterCategory))
                      .map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="w-full md:w-56">
                <div className="relative">
                  <CheckCircle className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="in_stock">In Stock</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Equipment Items Selection Bar */}
        {selectedEquipmentItems.length > 0 && (
          <div className="mb-4 bg-brand-50 border border-brand-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-brand-600" />
                <span className="text-sm font-medium text-brand-800">
                  {selectedEquipmentItems.length} equipment item{selectedEquipmentItems.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSetLocationForEquipmentItems}
                  className="flex items-center px-3 py-1 bg-brand-600 text-white rounded-xl text-sm hover:bg-brand-700"
                >
                  <MapPin className="h-4 w-4 mr-1" />
                  Set Location
                </button>
                <button
                  onClick={() => setSelectedEquipmentItems([])}
                  className="text-slate-500 hover:text-slate-700"
                  title="Clear selection"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions Info - Sticky */}
        {selectedItems.length > 0 && (
          <div className="sticky top-0 z-40 bg-brand-50 border border-brand-200 rounded-xl p-3 mb-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-brand-600 mr-2" />
                <span className="text-sm font-medium text-brand-800">
                  {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAssignment('person')}
                  className="flex items-center px-3 py-1 bg-green-600 text-white rounded-xl text-sm hover:bg-green-700"
                >
                  <User className="h-4 w-4 mr-1" />
                  Assign to Person
                </button>
                <button
                  onClick={() => handleAssignment('project')}
                  className="flex items-center px-3 py-1 bg-brand-600 text-white rounded-xl text-sm hover:bg-brand-700"
                >
                  <FolderOpen className="h-4 w-4 mr-1" />
                  Assign to Project
                </button>
                <button
                  onClick={() => {
                    fetchCars();
                    setShowAssignToCarModal(true);
                  }}
                  className="flex items-center px-3 py-1 bg-teal-600 text-white rounded-xl text-sm hover:bg-teal-700"
                >
                  <Truck className="h-4 w-4 mr-1" />
                  Assign to Car
                </button>
                <button
                  onClick={() => setSelectedItems([])}
                  className="text-slate-500 hover:text-slate-700"
                  title="Clear selection"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Items Table */}
        <div className="bg-white rounded-r-xl shadow-sm border-r-2 border-t border-b border-brand-500 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-steel-100 border-b-2 border-brand-500">
                <tr>
                  <th className="pl-2 sm:pl-6 pr-1 sm:pr-3 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={paginatedItems.length > 0 && paginatedItems.every(item => selectedItems.includes(item.id))}
                      onChange={handleSelectAll}
                      className="rounded border-steel-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                  </th>
                  <th
                    className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-steel-100 transition-colors select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Item
                      {renderSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-steel-100 transition-colors select-none"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center">
                      Category
                      {renderSortIcon('category')}
                    </div>
                  </th>
                  <th
                    className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-steel-100 transition-colors select-none"
                    onClick={() => handleSort('quantity')}
                  >
                    <div className="flex items-center">
                      Stock
                      {renderSortIcon('quantity')}
                    </div>
                  </th>
                  <th
                    className="pl-2 sm:pl-6 pr-0.5 sm:pr-1.5 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-steel-100 transition-colors select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {renderSortIcon('status')}
                    </div>
                  </th>
                  <th className="pl-0.5 sm:pl-1.5 pr-1 sm:pr-3 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Owner/Sharing</th>
                  <th
                    className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-steel-100 transition-colors select-none"
                    onClick={() => handleSort('location')}
                  >
                    <div className="flex items-center">
                      Location
                      {renderSortIcon('location')}
                    </div>
                  </th>
                  <th
                    className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-steel-100 transition-colors select-none"
                    onClick={() => handleSort('unitPrice')}
                  >
                    <div className="flex items-center">
                      Unit Price ({getCurrencySymbol()})
                      {renderSortIcon('unitPrice')}
                    </div>
                  </th>
                  <th className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Total Value</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-brand-100">
                {paginatedItems.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr className={`hover:bg-brand-50 transition-colors ${item.type === 'equipment_group' ? 'cursor-pointer' : ''}`}
                         onClick={item.type === 'equipment_group' ? () => toggleEquipmentType(item.name, item.globalTypeId) : undefined}>
                      <td className="pl-2 sm:pl-6 pr-1 sm:pr-3 py-4 whitespace-nowrap">
                        {item.type === 'equipment_group' ? (
                          <div className="flex items-center">
                            {/* Empty space for equipment groups - click handled by row */}
                          </div>
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="rounded border-steel-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                      <td className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          {/* Item Picture Thumbnail */}
                          {item.item_picture ? (
                            <img
                              src={item.item_picture}
                              alt={item.name}
                              className="w-10 h-10 object-cover rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                setImagePreview(item.item_picture);
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                              title="Click to enlarge"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-steel-200 rounded flex items-center justify-center flex-shrink-0">
                              <span className="text-slate-400 text-sm">📦</span>
                            </div>
                          )}

                          <div>
                            <div className="text-sm font-medium text-slate-900 flex items-center">
                              {item.type === 'warehouse' && item.requires_individual_tracking ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleIndividualItems(item);
                                  }}
                                  className="text-slate-900 hover:text-slate-700 hover:underline text-left flex items-center font-medium"
                                  title="Click to view individual items"
                                >
                                  <span className="mr-2">
                                    {expandedItems.has(item.id) ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </span>
                                  {highlightText(item.name, searchTerm)}
                                </button>
                              ) : (
                                <span>{highlightText(item.name, searchTerm)}</span>
                              )}
                              {item.type === 'equipment_group' && (
                                <span className="ml-2 text-xs text-slate-600">
                                  ({item.availableCount}/{item.totalCount} available)
                                </span>
                              )}
                            </div>
                            {item.type !== 'equipment_group' && (
                              <div className="text-sm text-slate-500">
                                Last updated: {item.lastUpdated}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    <td className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-4 whitespace-nowrap">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-steel-100 text-slate-800 capitalize">
                          {item.category}
                        </span>
                        {item.subcategory_name && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-700">
                              → {item.subcategory_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {item.quantity} {item.unit}
                        {item.requires_individual_tracking && individualItemsData[item.id] && (
                          <span className="ml-2 text-xs text-slate-500">
                            (Total: {individualItemsData[item.id].length})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-500">
                        Min: {item.minStock} {item.unit}
                      </div>
                    </td>
                    <td className="pl-2 sm:pl-6 pr-0.5 sm:pr-1.5 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {getStatusIcon(item.status)}
                        <span className="ml-1 capitalize">{item.status?.replace('_', ' ') || 'unknown'}</span>
                      </span>
                    </td>
                    <td className="pl-0.5 sm:pl-1.5 pr-1 sm:pr-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        <div className="font-medium">{item.ownerCompany}</div>
                        {item.isShared && (
                          <div className="text-xs text-green-600 mt-1">
                            Shared with: {item.sharedWithCompanies.length > 0 ? item.sharedWithCompanies.join(', ') : 'All companies'}
                          </div>
                        )}
                        {!item.isShared && item.ownerCompany !== 'Legacy Item' && (
                          <div className="text-xs text-slate-500 mt-1">Private</div>
                        )}
                      </div>
                    </td>
                    <td className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-4 text-sm text-slate-900">
                      <div className="max-w-[150px]" title={item.location}>
                        {highlightText(formatLocationDisplay(item.location), searchTerm)}
                      </div>
                    </td>
                    <td className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatPrice(item.unitPrice || 0)}
                    </td>
                    <td className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {formatPrice((item.quantity || 0) * (item.unitPrice || 0))}
                    </td>
                    <td className="px-2 sm:px-6 py-4 text-sm font-medium">
                      {item.type !== 'equipment_group' ? (
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => handleShareItem(item)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Manage sharing"
                          >
                            <Share2 className="h-4 w-4" />
                          </button>
                          {/* Assignment Actions */}
                          <button
                            onClick={() => {
                              handleAssignment('person', item.id);
                            }}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Assign to Person"
                          >
                            <User className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              handleAssignment('project', item.id);
                            }}
                            className="text-brand-600 hover:text-brand-800 p-1"
                            title="Assign to Project"
                          >
                            <FolderOpen className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedItems([item.id]);
                              fetchCars();
                              setShowAssignToCarModal(true);
                            }}
                            className="text-teal-600 hover:text-teal-900 p-1"
                            title="Assign to Car"
                          >
                            <Truck className="h-4 w-4" />
                          </button>

                          {/* Restock Action - Available for all items */}
                          <button
                            onClick={() => handleRestock(item)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Restock item / Add stock"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>

                          {/* History Action */}
                          <button
                            onClick={() => handleShowHistory(item)}
                            className="text-brand-600 hover:text-brand-800 p-1"
                            title="View item history"
                          >
                            <History className="h-4 w-4" />
                          </button>

                          {/* Regular Actions */}
                          <button
                            onClick={() => handleEditItem(item)}
                            className="text-brand-600 hover:text-brand-800 p-1"
                            title="Edit item"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetEquipmentGroupLocation(item);
                            }}
                            className="text-brand-600 hover:text-brand-800 p-1"
                            title="Set equipment group location"
                          >
                            <MapPin className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareEquipmentGroup(item);
                            }}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Share equipment group"
                          >
                            <Share2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Equipment Items */}
                  {item.type === 'equipment_group' && expandedEquipmentTypes.has(item.name) && item.equipmentItems && (
                    item.equipmentItems.map((equipItem) => (
                      <tr key={equipItem.id} className="bg-white">
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div className="ml-6 flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedEquipmentItems.some(item => item.id === equipItem.id)}
                              onChange={() => handleEquipmentItemToggle(equipItem)}
                              className="rounded border-steel-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                            />
                          </div>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div className="ml-6">
                            <div className="text-sm font-medium text-slate-900">
                              S/N: {equipItem.serialNumber}
                            </div>
                            <div className="text-xs text-slate-900">
                              {equipItem.model} - {equipItem.manufacturer}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-steel-100 text-slate-900">
                            Equipment
                          </span>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div className="text-sm text-slate-900">1 pcs</div>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              equipItem.status === 'available' ? 'bg-green-100 text-green-800' :
                              equipItem.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                              equipItem.status === 'issued' ? 'bg-yellow-100 text-yellow-800' :
                              equipItem.status === 'maintenance' ? 'bg-brand-100 text-brand-800' :
                              equipItem.status === 'service' ? 'bg-brand-100 text-brand-800' :
                              equipItem.status === 'damaged' ? 'bg-red-100 text-red-800' :
                              'bg-steel-100 text-slate-800'
                            }`}>
                              {equipItem.status}
                            </span>

                            {/* Show additional info for issued equipment */}
                            {equipItem.status === 'issued' && (
                              <div className="text-xs text-slate-600 space-y-0.5 mt-1">
                                {equipItem.issued_to && (
                                  <div>→ {equipItem.issued_to}</div>
                                )}
                                {equipItem.issued_at && (
                                  <div>📅 {new Date(equipItem.issued_at).toLocaleDateString()}</div>
                                )}
                                {equipItem.project_name && (
                                  <div>📋 {equipItem.project_name}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div className="text-sm text-slate-900">
                            <div className="font-medium">{equipItem.company}</div>
                            {equipItem.isShared && (
                              <div className="text-xs text-green-600 mt-1">
                                Shared with: {equipItem.sharedWithCompanies.length > 0 ? equipItem.sharedWithCompanies.join(', ') : 'All companies'}
                              </div>
                            )}
                            {!equipItem.isShared && (
                              <div className="text-xs text-slate-900 mt-1">Private</div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-6 py-2">
                          <div className="text-sm text-slate-900 max-w-[150px]" title={equipItem.location}>
                            {formatLocationDisplay(equipItem.location)}
                          </div>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div className="text-sm text-slate-900">-</div>
                        </td>
                        <td className="px-2 sm:px-6 py-2 whitespace-nowrap">
                          <div className="text-sm text-slate-900">-</div>
                        </td>
                        <td className="px-2 sm:px-6 py-2">
                          <div className="flex flex-wrap items-center gap-1">
                            {/* Manual Return button - only show for issued equipment */}
                            {equipItem.status === 'issued' && (
                              <button
                                onClick={() => handleManualReturn(equipItem, item)}
                                className="p-1 text-orange-600 hover:text-orange-900"
                                title="Manually return this equipment (use if project was deleted)"
                              >
                                <RefreshCw className="h-3 w-3" />
                              </button>
                            )}

                            <button
                              onClick={() => {
                                // Set up equipment item for editing
                                setEditingIndividualItem({
                                  ...equipItem,
                                  id: equipItem.id,
                                  parentItemId: item.id,
                                  type: 'equipment',
                                  typeId: equipItem.typeId  // Company-specific type ID for custom fields
                                });
                                setEditIndividualFormData({
                                  serial_number: equipItem.serialNumber || '',
                                  notes: equipItem.comment || equipItem.notes || '',
                                  custom_fields: equipItem.custom_fields || {}
                                });
                                setShowEditIndividualModal(true);
                              }}
                              className="text-brand-600 hover:text-brand-800 p-1"
                              title="Edit equipment item details"
                            >
                              <Edit3 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}

                  {/* Expanded Individual Items for Warehouse Items */}
                  {item.type === 'warehouse' && item.requires_individual_tracking && expandedItems.has(item.id) && (
                    loadingIndividualItems.has(item.id) ? (
                      <tr className="bg-white">
                        <td colSpan="11" className="px-6 py-4">
                          <div className="flex items-center justify-center">
                            <RefreshCw className="h-4 w-4 text-brand-600 mr-2 animate-spin" />
                            <span className="text-slate-600">Loading individual items...</span>
                          </div>
                        </td>
                      </tr>
                    ) : individualItemsData[item.id]?.length === 0 ? (
                      <tr className="bg-white">
                        <td colSpan="11" className="px-6 py-4">
                          <div className="flex items-center justify-center text-slate-500">
                            <Package className="h-4 w-4 mr-2" />
                            <span>No individual items found. Items will be generated when first issued.</span>
                          </div>
                        </td>
                      </tr>
                    ) : individualItemsData[item.id]?.map((individualItem, index) => {
                      // Debug log for problematic items
                      if (!individualItem || !individualItem.id) {
                        console.error('Individual item missing ID:', {individualItem, index, parentItemId: item.id});
                      }
                      return (
                      <tr key={`individual-${item.id}-${individualItem?.id || index}-${index}`} className={`${individualItem?.retired ? 'bg-pink-50' : 'bg-white'} border-l-4 border-brand-200 ${(individualItem?.status === 'issued' || individualItem?.status === 'received') ? 'opacity-60' : ''}`}>
                        <td className="pl-2 sm:pl-6 pr-1 sm:pr-3 py-3">
                          <div className="ml-6">
                            <input
                              type="checkbox"
                              className={`rounded border-steel-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50 ${
                                (individualItem?.status === 'issued' || individualItem?.status === 'received') ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              checked={selectedItems.includes(`individual_${item.id}_${individualItem?.id || index}`)}
                              disabled={individualItem?.status === 'issued' || individualItem?.status === 'received'}
                              onChange={(e) => {
                                const individualItemId = `individual_${item.id}_${individualItem?.id || index}`;

                                if (e.target.checked) {
                                  // Create individual item for selection with custom field values
                                  const individualItemForSelection = {
                                    id: individualItemId,
                                    name: `${item.name} (Individual Item #${individualItem?.id || index + 1})`,
                                    quantityInStock: 1,
                                    originalId: item.id,
                                    individualId: individualItem?.id || index,
                                    type: 'warehouse_individual',
                                    customFields: item.customFields || [],
                                    // Pre-fill with current individual item's custom field values
                                    customFieldValues: individualItem?.custom_fields || {},
                                    quantity: 1,
                                    sku: item.sku,
                                    location: item.location
                                  };
                                  setItems(prev => [...prev, individualItemForSelection]);
                                  setAllItemsUnfiltered(prev => [...prev, individualItemForSelection]);
                                  setSelectedItems(prev => [...prev, individualItemId]);
                                } else {
                                  // Remove from selection
                                  setSelectedItems(prev => prev.filter(id => id !== individualItemId));
                                  setItems(prev => prev.filter(item => item.id !== individualItemId));
                                  setAllItemsUnfiltered(prev => prev.filter(item => item.id !== individualItemId));
                                }
                              }}
                            />
                          </div>
                        </td>
                        <td className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-3">
                          <div className="ml-6">
                            <div className="space-y-1">


                              {/* Show additional info */}
                              <div className="text-xs text-slate-500 space-y-0.5">
                                {item.model && <div>Model: {item.model}</div>}
                                {item.manufacturer && <div>Mfg: {item.manufacturer}</div>}

                                {/* Show custom fields specific to this item */}
                                {item.customFields && item.customFields.map((customField) => {
                                  const fieldDef = customField.field_definition || customField;
                                  const fieldName = fieldDef.name;
                                  // Extract clean field name (remove _item_xxxxx suffix)
                                  const cleanFieldName = fieldName.includes('_item_') ? fieldName.split('_item_')[0] : fieldName;
                                  const currentValue = individualItem.custom_fields?.[fieldName];
                                  const editKey = `${item.id}-${individualItem.id}-${fieldName}`;

                                  if (editingCustomField === editKey) {
                                    // Inline editing mode
                                    return (
                                      <div key={fieldName} className="flex items-center space-x-1 mt-1">
                                        <input
                                          type="text"
                                          value={tempCustomFieldValue}
                                          onChange={(e) => setTempCustomFieldValue(e.target.value)}
                                          className="text-xs px-2 py-1 border border-brand-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                                          placeholder={`Enter ${cleanFieldName.toLowerCase()}`}
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              saveCustomField(individualItem, item, fieldName);
                                            } else if (e.key === 'Escape') {
                                              cancelCustomFieldEdit();
                                            }
                                          }}
                                        />
                                        <button
                                          onClick={() => saveCustomField(individualItem, item, fieldName)}
                                          className="text-green-600 hover:text-green-800 p-1"
                                          title="Save"
                                        >
                                          <CheckCircle className="h-2 w-2" />
                                        </button>
                                        <button
                                          onClick={cancelCustomFieldEdit}
                                          className="text-red-600 hover:text-red-800 p-1"
                                          title="Cancel"
                                        >
                                          <AlertTriangle className="h-2 w-2" />
                                        </button>
                                      </div>
                                    );
                                  } else {
                                    // Display mode
                                    return (
                                      <div key={fieldName} className="mt-1">
                                        {currentValue ? (
                                          <div
                                            className="text-xs text-slate-900 font-semibold cursor-pointer hover:text-slate-700 hover:underline"
                                            onClick={() => startEditingCustomField(individualItem, item, fieldName, currentValue)}
                                            title={`Click to edit ${cleanFieldName.toLowerCase()}`}
                                          >
                                            {cleanFieldName}: {currentValue}
                                          </div>
                                        ) : (
                                          <div
                                            className="text-xs text-slate-600 cursor-pointer hover:text-brand-600 hover:underline"
                                            onClick={() => startEditingCustomField(individualItem, item, fieldName, '')}
                                            title={`Click to add ${cleanFieldName.toLowerCase()}`}
                                          >
                                            No {cleanFieldName.toLowerCase()} - click to add
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                })}

                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            #{individualItem.id}
                          </span>
                        </td>
                        <td className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-3">
                          <div className="text-sm text-slate-700">1 {item.unit}</div>
                        </td>
                        <td className="pl-2 sm:pl-6 pr-0.5 sm:pr-1.5 py-3">
                          <div className="space-y-1">
                            {/* Status dropdown */}
                            <select
                              value={individualItem.status || 'in_stock'}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);
                                try {
                                  await updateIndividualItem(originalId, individualItem.id, {
                                    serial_number: individualItem.serial_number || '',
                                    notes: individualItem.notes || '',
                                    custom_fields: individualItem.custom_fields || {},
                                    status: newStatus
                                  });
                                  // Update local state
                                  setIndividualItemsData(prev => ({
                                    ...prev,
                                    [item.id]: prev[item.id]?.map(ii =>
                                      ii.id === individualItem.id ? { ...ii, status: newStatus } : ii
                                    ) || []
                                  }));
                                  // Refresh items to sync with backend
                                  fetchFilteredItems();
                                } catch (error) {
                                  console.error('Error updating status:', error);
                                  alert('Error updating status');
                                }
                              }}
                              className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${
                                individualItem.status === 'in_stock' ? 'bg-green-100 text-green-800' :
                                individualItem.status === 'issued' ? 'bg-yellow-100 text-yellow-800' :
                                individualItem.status === 'received' ? 'bg-brand-100 text-brand-800' :
                                individualItem.status === 'returned' ? 'bg-brand-100 text-brand-800' :
                                'bg-steel-100 text-slate-800'
                              }`}
                            >
                              <option value="in_stock">in_stock</option>
                              <option value="issued">issued</option>
                              <option value="received">received</option>
                              <option value="returned">returned</option>
                            </select>

                            {/* Show additional info for issued items */}
                            {individualItem.status === 'issued' && (
                              <div className="text-xs text-slate-600 space-y-0.5">
                                {individualItem.issued_to && (
                                  <div>→ {individualItem.issued_to}</div>
                                )}
                                {individualItem.issued_at && (
                                  <div>📅 {new Date(individualItem.issued_at).toLocaleDateString()}</div>
                                )}
                                {individualItem.project_name && (
                                  <div>📋 {individualItem.project_name}</div>
                                )}
                              </div>
                            )}

                            {/* Show additional info for received items */}
                            {individualItem.status === 'received' && (
                              <div className="text-xs text-slate-600 space-y-0.5">
                                {individualItem.received_by && (
                                  <div>→ {individualItem.received_by}</div>
                                )}
                                {individualItem.received_at && (
                                  <div>📅 {new Date(individualItem.received_at).toLocaleDateString()}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="pl-0.5 sm:pl-1.5 pr-1 sm:pr-3 py-3">
                          <div className="text-sm text-slate-700">
                            <div className="font-medium">{item.ownerCompany}</div>
                            <div className="text-xs text-slate-500 mt-1">Individual Item</div>
                          </div>
                        </td>
                        <td className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-3">
                          <div className="text-sm text-slate-700 max-w-[150px]" title={item.location}>
                            {formatLocationDisplay(item.location)}
                          </div>
                        </td>
                        <td className="pl-1 sm:pl-3 pr-1 sm:pr-3 py-3">
                          <div className="text-sm text-slate-700">{formatPrice(item.unitPrice || 0)}</div>
                        </td>
                        <td className="pl-1 sm:pl-3 pr-2 sm:pr-6 py-3">
                          <div className="text-sm text-slate-700">{formatPrice(item.unitPrice || 0)}</div>
                        </td>
                        <td className="px-2 sm:px-6 py-3">
                          <div className="space-y-2">
                            {/* Retired checkbox */}
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={individualItem?.retired || false}
                                onChange={async (e) => {
                                  const retired = e.target.checked;
                                  const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);
                                  try {
                                    await updateIndividualItem(originalId, individualItem.id, {
                                      serial_number: individualItem.serial_number || '',
                                      notes: individualItem.notes || '',
                                      custom_fields: individualItem.custom_fields || {},
                                      retired: retired
                                    });
                                    // Update local state
                                    setIndividualItemsData(prev => ({
                                      ...prev,
                                      [item.id]: prev[item.id]?.map(ii =>
                                        ii.id === individualItem.id ? { ...ii, retired } : ii
                                      ) || []
                                    }));
                                  } catch (error) {
                                    console.error('Error updating retired status:', error);
                                    alert('Error updating status');
                                  }
                                }}
                                className="rounded border-steel-300 text-pink-600 focus:ring-pink-500"
                              />
                              <label className={`text-xs ${individualItem?.retired ? 'text-pink-600 font-semibold' : 'text-slate-600'}`}>
                                {individualItem?.retired ? '🚫 Retired' : 'Retired'}
                              </label>
                            </div>

                            {/* Action buttons - hide when retired */}
                            {!individualItem?.retired && (
                            <div className="flex flex-wrap items-center gap-1">
                            {/* Manual Return button - only show for issued items */}
                            {individualItem?.status === 'issued' && (
                              <button
                                onClick={() => handleManualReturn(individualItem, item)}
                                className="p-1 text-orange-600 hover:text-orange-900"
                                title="Manually return this item (use if project was deleted)"
                              >
                                <RefreshCw className="h-3 w-3" />
                              </button>
                            )}

                            <button
                              onClick={individualItem?.status === 'issued' ? undefined : () => {
                                // Create individual item assignment - set as selected item and open modal
                                const individualItemForAssignment = {
                                  id: `individual_${item.id}_${individualItem?.id || index}`,
                                  name: `${item.name} (Individual Item #${individualItem?.id || index + 1})`,
                                  quantityInStock: 1,
                                  originalId: item.id,
                                  individualId: individualItem?.id || index,
                                  type: 'warehouse_individual',
                                  customFields: item.customFields || [],
                                  // Pre-fill with current individual item's custom field values
                                  customFieldValues: individualItem?.custom_fields || {}
                                };

                                // Add item to the list first, then select it and open modal
                                setItems(prev => [...prev, individualItemForAssignment]);
                                setSelectedItems([individualItemForAssignment.id]);
                                // Use setTimeout to ensure state updates are processed before opening modal
                                setTimeout(() => {
                                  handleAssignment('person');
                                }, 0);
                              }}
                              className={`p-1 ${
                                individualItem?.status === 'issued'
                                  ? 'text-slate-400 cursor-not-allowed'
                                  : 'text-green-600 hover:text-green-900'
                              }`}
                              title={individualItem?.status === 'issued' ? 'Item already issued' : 'Assign to person'}
                              disabled={individualItem?.status === 'issued'}
                            >
                              <User className="h-3 w-3" />
                            </button>
                            <button
                              onClick={individualItem?.status === 'issued' ? undefined : () => {
                                // Create individual item assignment - set as selected item and open modal
                                const individualItemForAssignment = {
                                  id: `individual_${item.id}_${individualItem?.id || index}`,
                                  name: `${item.name} (Individual Item #${individualItem?.id || index + 1})`,
                                  quantityInStock: 1,
                                  originalId: item.id,
                                  individualId: individualItem?.id || index,
                                  type: 'warehouse_individual',
                                  customFields: item.customFields || [],
                                  // Pre-fill with current individual item's custom field values
                                  customFieldValues: individualItem?.custom_fields || {}
                                };

                                // Add item to the list first, then select it and open modal
                                setItems(prev => [...prev, individualItemForAssignment]);
                                setSelectedItems([individualItemForAssignment.id]);
                                // Use setTimeout to ensure state updates are processed before opening modal
                                setTimeout(() => {
                                  handleAssignment('project');
                                }, 0);
                              }}
                              className={`p-1 ${
                                individualItem?.status === 'issued'
                                  ? 'text-slate-400 cursor-not-allowed'
                                  : 'text-brand-600 hover:text-brand-800'
                              }`}
                              title={individualItem?.status === 'issued' ? 'Item already issued' : 'Assign to project'}
                              disabled={individualItem?.status === 'issued'}
                            >
                              <FolderOpen className="h-3 w-3" />
                            </button>
                            </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-brand-200 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-brand-200 text-sm font-medium rounded-xl text-slate-700 bg-white hover:bg-steel-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-brand-200 text-sm font-medium rounded-xl text-slate-700 bg-white hover:bg-steel-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-slate-700">
                    Showing <span className="font-medium">{showEquipmentOnly ? 1 : ((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-medium">{showEquipmentOnly ? paginatedItems.length : Math.min(currentPage * itemsPerPage, totalCount)}</span> of{' '}
                    <span className="font-medium">{effectiveTotalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-xl border border-brand-200 bg-white text-sm font-medium text-slate-500 hover:bg-steel-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="h-5 w-5 rotate-90" />
                    </button>
                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-brand-200 bg-white text-sm font-medium text-slate-700">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                              : 'bg-white border-brand-200 text-slate-500 hover:bg-steel-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-xl border border-brand-200 bg-white text-sm font-medium text-slate-500 hover:bg-steel-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="h-5 w-5 -rotate-90" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {items.length === 0 && !loading && (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No items found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {searchTerm || filterCategory !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first inventory item.'
              }
            </p>
          </div>
        )}


        {/* Add Item Modal */}
        {showAddModal && <AddItemModal 
          categories={categories.filter(cat => cat.value !== 'all')} 
          customFields={customFields}
          onSave={handleAddItem}
          onClose={() => setShowAddModal(false)}
          onCategoryDeleted={handleDeleteCategory}
        />}

        {/* Edit Item Modal */}
        {showEditModal && editingItem && <EditItemModal 
          item={editingItem}
          categories={categories.filter(cat => cat.value !== 'all')} 
          onSave={handleUpdateItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onCategoryDeleted={handleDeleteCategory}
        />}

        {/* Manage Categories Modal */}
        {showCategoryModal && <ManageCategoriesModal 
          categories={categories.filter(cat => cat.value !== 'all')} 
          onClose={() => setShowCategoryModal(false)}
          onRefresh={() => {
            fetchCategories();
            fetchFilteredItems();
          }}
        />}

        {/* Import Items Modal */}
        {/* Enhanced Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Export Inventory</h2>

                {/* Export Scope */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-3">Export Scope</label>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border border-brand-200 rounded-xl cursor-pointer hover:bg-steel-50">
                      <input
                        type="radio"
                        name="scope"
                        value="all"
                        checked={exportConfig.scope === 'all'}
                        onChange={(e) => setExportConfig({...exportConfig, scope: e.target.value})}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium">All Items</div>
                        <div className="text-sm text-slate-500">Export all {allItemsUnfiltered.length} items</div>
                      </div>
                    </label>
                    <label className="flex items-center p-3 border border-brand-200 rounded-xl cursor-pointer hover:bg-steel-50">
                      <input
                        type="radio"
                        name="scope"
                        value="filtered"
                        checked={exportConfig.scope === 'filtered'}
                        onChange={(e) => setExportConfig({...exportConfig, scope: e.target.value})}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium">Current View (Filtered)</div>
                        <div className="text-sm text-slate-500">Export {sortedItems.length} items from current filters</div>
                      </div>
                    </label>
                    <label className={`flex items-center p-3 border border-brand-200 rounded-xl ${selectedItems.length > 0 ? 'cursor-pointer hover:bg-steel-50' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="radio"
                        name="scope"
                        value="selected"
                        checked={exportConfig.scope === 'selected'}
                        onChange={(e) => setExportConfig({...exportConfig, scope: e.target.value})}
                        disabled={selectedItems.length === 0}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium">Selected Items Only</div>
                        <div className="text-sm text-slate-500">Export {selectedItems.length} selected items</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Export Format */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-3">Export Format</label>
                  <div className="grid grid-cols-3 gap-3">
                    <label className="flex items-center justify-center p-3 border border-brand-200 rounded-xl cursor-pointer hover:bg-steel-50">
                      <input
                        type="radio"
                        name="format"
                        value="excel"
                        checked={exportConfig.format === 'excel'}
                        onChange={(e) => setExportConfig({...exportConfig, format: e.target.value})}
                        className="mr-2"
                      />
                      <span className="font-medium">Excel (.xlsx)</span>
                    </label>
                    <label className="flex items-center justify-center p-3 border border-brand-200 rounded-xl cursor-pointer hover:bg-steel-50">
                      <input
                        type="radio"
                        name="format"
                        value="csv"
                        checked={exportConfig.format === 'csv'}
                        onChange={(e) => setExportConfig({...exportConfig, format: e.target.value})}
                        className="mr-2"
                      />
                      <span className="font-medium">CSV</span>
                    </label>
                    <label className="flex items-center justify-center p-3 border border-brand-200 rounded-xl cursor-pointer hover:bg-steel-50">
                      <input
                        type="radio"
                        name="format"
                        value="pdf"
                        checked={exportConfig.format === 'pdf'}
                        onChange={(e) => setExportConfig({...exportConfig, format: e.target.value})}
                        className="mr-2"
                      />
                      <span className="font-medium">PDF</span>
                    </label>
                  </div>
                </div>

                {/* Column Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-3">Columns to Export</label>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.keys(exportConfig.columns).map(column => (
                      <label key={column} className="flex items-center p-2 hover:bg-steel-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={exportConfig.columns[column]}
                          onChange={(e) => setExportConfig({
                            ...exportConfig,
                            columns: {...exportConfig.columns, [column]: e.target.checked}
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm capitalize">{column.replace(/([A-Z])/g, ' $1').trim()}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="px-4 py-2 text-slate-600 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEnhancedExport}
                    className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showImportModal && <ImportItemsModal
          categories={categories.filter(cat => cat.value !== 'all')}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            fetchInventoryData();
            setShowImportModal(false);
          }}
        />}

        {/* Manage Sharing Modal */}
        {showSharingModal && sharingItem && <ManageSharingModal
          item={sharingItem}
          onClose={() => {
            setShowSharingModal(false);
            setSharingItem(null);
          }}
          onRefresh={() => {
            fetchFilteredItems();
          }}
        />}

        {/* Equipment Group Location Modal */}
        {showEquipmentLocationModal && locationEquipmentGroup && <EquipmentGroupLocationModal
          equipmentGroup={locationEquipmentGroup}
          onClose={() => {
            setShowEquipmentLocationModal(false);
            setLocationEquipmentGroup(null);
          }}
          onRefresh={() => {
            fetchFilteredItems();
          }}
        />}

        {/* Equipment Items Location Modal */}
        {showEquipmentItemsLocationModal && selectedEquipmentItems.length > 0 && <EquipmentItemsLocationModal
          equipmentItems={selectedEquipmentItems}
          onClose={() => {
            setShowEquipmentItemsLocationModal(false);
            setSelectedEquipmentItems([]);
          }}
          onRefresh={() => {
            fetchFilteredItems();
          }}
        />}

        {/* Manage Equipment Group Sharing Modal */}
        {showEquipmentSharingModal && sharingEquipmentGroup && <ManageEquipmentGroupSharingModal
          equipmentGroup={sharingEquipmentGroup}
          onClose={() => {
            setShowEquipmentSharingModal(false);
            setSharingEquipmentGroup(null);
          }}
          onRefresh={() => {
            fetchFilteredItems();
          }}
        />}

        {/* Restock Modal */}
        {showRestockModal && restockingItem && <RestockModal
          item={restockingItem}
          onClose={() => {
            setShowRestockModal(false);
            setRestockingItem(null);
          }}
          onRefresh={() => {
            fetchFilteredItems();
          }}
        />}

        {/* Assignment Modal */}
        {showAssignmentModal && <AssignmentModal
          selectedItems={(() => {
            // Use allItemsUnfiltered to find all selected items, not just currently displayed ones
            console.log('[AssignmentModal] selectedItems IDs:', selectedItems);
            console.log('[AssignmentModal] allItemsUnfiltered count:', allItemsUnfiltered.length);
            // Merge allItemsUnfiltered with current page items to ensure we always have items available
            const itemsSource = allItemsUnfiltered.length > 0 ? allItemsUnfiltered : items;
            const fullItems = selectedItems.map(id => {
              const found = itemsSource.find(item => item.id === id);
              console.log(`[AssignmentModal] Looking for ID ${id}, found:`, found ? found.name : 'NOT FOUND');
              return found;
            }).filter(Boolean);
            console.log('[AssignmentModal] fullItems to pass to modal:', fullItems.map(i => i.name));
            fullItems.forEach(item => {
            });
            return fullItems;
          })()}
          assignmentType={assignmentType}
          customFields={customFields}
          allInventoryItems={allItemsUnfiltered.length > 0 ? allItemsUnfiltered : items}
          loadingAllItems={loadingAllItems}
          ensureAllItemsLoaded={ensureAllItemsLoaded}
          onClose={() => {
            setShowAssignmentModal(false);
            setAssignmentType(null);
            setSelectedItems([]);
          }}
          onSave={async (assignmentData) => {
            try {
              await handleAssignmentSave(assignmentData);
              setShowAssignmentModal(false);
              setAssignmentType(null);
              setSelectedItems([]);
              fetchFilteredItems(); // Refresh data after assignment
            } catch (error) {
              console.error('Error saving assignment:', error);
              alert('Error saving assignment. Please try again.');
            }
          }}
        />}

        {/* Image Preview Modal */}
        {imagePreview && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setImagePreview(null)}
          >
            <div className="relative max-w-7xl max-h-[90vh] mx-4">
              <button
                onClick={() => setImagePreview(null)}
                className="absolute -top-10 right-0 text-white hover:text-steel-300"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={imagePreview}
                alt="Preview"
                className="max-w-full max-h-[90vh] object-contain rounded-xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && historyItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl mx-4 max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-brand-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 flex items-center">
                      <History className="w-6 h-6 mr-2 text-brand-600" />
                      Item History
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      {historyItem.name} - {historyItem.sku}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowHistoryModal(false);
                      setHistoryItem(null);
                      setHistoryData([]);
                    }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                {historyData.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                    <p className="text-slate-500">No history records found for this item.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-brand-100">
                      <thead className="bg-steel-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Action</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Date & Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Performed by</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Issued</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-brand-100">
                        {historyData.map((record, index) => (
                          <tr key={index} className="hover:bg-steel-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                record.action === 'issued' ? 'bg-red-100 text-red-800' :
                                record.action === 'returned' ? 'bg-green-100 text-green-800' :
                                record.action === 'restocked' ? 'bg-brand-100 text-brand-800' :
                                'bg-steel-100 text-slate-800'
                              }`}>
                                {record.action === 'issued' ? 'Issued' :
                                 record.action === 'returned' ? 'Returned' :
                                 record.action === 'restocked' ? 'Restocked' :
                                 record.action}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              {record.quantity} {historyItem.unit}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              {new Date(record.created_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              {record.user_name || record.user || 'Unknown'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-900">
                              {record.assigned_to || record.project || record.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

{/* Edit Individual Item Modal */}
        {showEditIndividualModal && editingIndividualItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-brand-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 flex items-center">
                      <Edit3 className="w-6 h-6 mr-2 text-brand-600" />
                      {editingIndividualItem.type === 'equipment'
                        ? `Edit Equipment Item`
                        : `Edit Individual Item #${editingIndividualItem.id}`}
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      {editingIndividualItem.type === 'equipment'
                        ? `${editingIndividualItem.model || editingIndividualItem.name || 'Equipment'} - ${editingIndividualItem.manufacturer || 'No Manufacturer'}`
                        : items.find(item => item.id === editingIndividualItem?.parentItemId)?.name}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditIndividualModal(false);
                      setEditingIndividualItem(null);
                    }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <div className="space-y-6">
                  {/* Basic Fields */}
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Serial Number
                      </label>
                      <input
                        type="text"
                        value={editIndividualFormData.serial_number}
                        onChange={(e) => setEditIndividualFormData(prev => ({
                          ...prev,
                          serial_number: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                        placeholder="Enter serial number..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        rows={3}
                        value={editIndividualFormData.notes}
                        onChange={(e) => setEditIndividualFormData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                        placeholder="Enter notes..."
                      />
                    </div>
                  </div>

                  {/* Custom Fields / Additional Fields */}
                  <div>
                    <h3 className="text-lg font-medium text-slate-900 mb-4">
                      {editingIndividualItem.type === 'equipment'
                        ? 'Equipment Details'
                        : 'Custom Fields (Additionals)'}
                    </h3>
                    <div className="space-y-4">
                      {editingIndividualItem.type === 'equipment' ? (() => {
                        // Get custom fields for this equipment type (using company-specific type ID)
                        const equipmentTypeCustomFields = getEquipmentCustomFieldsForType(editingIndividualItem.typeId);

                        if (equipmentTypeCustomFields.length === 0) {
                          return (
                            <p className="text-slate-500 text-sm">
                              No custom fields defined for this equipment type.
                            </p>
                          );
                        }

                        return equipmentTypeCustomFields.map((field) => (
                          <div key={field.id}>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              {field.field_name}
                              {field.is_required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {field.field_type === 'text' && (
                              <input
                                type="text"
                                value={editIndividualFormData.custom_fields[field.field_name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.field_name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                placeholder={`Enter ${field.field_name.toLowerCase()}...`}
                              />
                            )}
                            {field.field_type === 'number' && (
                              <input
                                type="number"
                                value={editIndividualFormData.custom_fields[field.field_name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.field_name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                placeholder={`Enter ${field.field_name.toLowerCase()}...`}
                              />
                            )}
                            {field.field_type === 'date' && (
                              <input
                                type="date"
                                value={editIndividualFormData.custom_fields[field.field_name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.field_name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                              />
                            )}
                            {field.field_type === 'textarea' && (
                              <textarea
                                rows={3}
                                value={editIndividualFormData.custom_fields[field.field_name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.field_name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                placeholder={`Enter ${field.field_name.toLowerCase()}...`}
                              />
                            )}
                            {field.field_type === 'select' && field.field_options && (
                              <select
                                value={editIndividualFormData.custom_fields[field.field_name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.field_name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                              >
                                <option value="">Select {field.field_name}</option>
                                {field.field_options.split(',').map((option, index) => (
                                  <option key={index} value={option.trim()}>
                                    {option.trim()}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        ));
                      })() : customFields.length === 0 ? (
                        <p className="text-slate-500 text-sm">No custom fields defined for this item type.</p>
                      ) : (
                        customFields.map((field) => (
                          <div key={field.id}>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              {field.name}
                              {field.is_required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {field.field_type === 'text' && (
                              <input
                                type="text"
                                value={editIndividualFormData.custom_fields[field.name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                placeholder={field.help_text || `Enter ${field.name.toLowerCase()}...`}
                              />
                            )}
                            {field.field_type === 'number' && (
                              <input
                                type="number"
                                value={editIndividualFormData.custom_fields[field.name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                placeholder={field.help_text || `Enter ${field.name.toLowerCase()}...`}
                              />
                            )}
                            {field.field_type === 'textarea' && (
                              <textarea
                                rows={3}
                                value={editIndividualFormData.custom_fields[field.name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                placeholder={field.help_text || `Enter ${field.name.toLowerCase()}...`}
                              />
                            )}
                            {field.field_type === 'boolean' && (
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={editIndividualFormData.custom_fields[field.name] === true || editIndividualFormData.custom_fields[field.name] === 'true'}
                                  onChange={(e) => setEditIndividualFormData(prev => ({
                                    ...prev,
                                    custom_fields: {
                                      ...prev.custom_fields,
                                      [field.id]: e.target.checked
                                    }
                                  }))}
                                  className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                                />
                                <span className="ml-2 text-sm text-slate-700">{field.help_text || field.name}</span>
                              </div>
                            )}
                            {field.field_type === 'date' && (
                              <input
                                type="date"
                                value={editIndividualFormData.custom_fields[field.name] || ''}
                                onChange={(e) => setEditIndividualFormData(prev => ({
                                  ...prev,
                                  custom_fields: {
                                    ...prev.custom_fields,
                                    [field.name]: e.target.value
                                  }
                                }))}
                                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                              />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-brand-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditIndividualModal(false);
                    setEditingIndividualItem(null);
                  }}
                  className="px-4 py-2 border border-brand-200 rounded-xl text-slate-700 hover:bg-steel-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateIndividualItem}
                  className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual Return Modal */}
        {showManualReturnModal && manualReturnItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-brand-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center">
                    <RefreshCw className="w-6 h-6 mr-2 text-orange-600" />
                    Manual Return Confirmation
                  </h2>
                  <button
                    onClick={() => {
                      setShowManualReturnModal(false);
                      setManualReturnItem(null);
                    }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Warning: Manual Return</p>
                      <p>
                        This will forcibly return the item to the warehouse. Use this only if:
                      </p>
                      <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>The assigned project was deleted</li>
                        <li>Normal return process is not available</li>
                        <li>You are sure this item is physically back in the warehouse</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-medium text-slate-700">Item:</div>
                    <div className="text-sm text-slate-900 mt-1">
                      {manualReturnItem.parentItem?.name}
                      {manualReturnItem.individualItem?.serial_number && (
                        <span className="text-slate-600"> (S/N: {manualReturnItem.individualItem.serial_number})</span>
                      )}
                    </div>
                  </div>

                  {manualReturnItem.individualItem?.issued_to && (
                    <div>
                      <div className="text-sm font-medium text-slate-700">Currently Issued To:</div>
                      <div className="text-sm text-slate-900 mt-1">
                        {manualReturnItem.individualItem.issued_to}
                      </div>
                    </div>
                  )}

                  {manualReturnItem.individualItem?.issued_at && (
                    <div>
                      <div className="text-sm font-medium text-slate-700">Issued Date:</div>
                      <div className="text-sm text-slate-900 mt-1">
                        {new Date(manualReturnItem.individualItem.issued_at).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-brand-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowManualReturnModal(false);
                    setManualReturnItem(null);
                  }}
                  className="px-4 py-2 border border-brand-200 rounded-xl text-slate-700 hover:bg-steel-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmManualReturn}
                  className="px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Return to Warehouse
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Car Inventory Modal */}
        {showCarInventoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-brand-200 bg-teal-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center">
                    <Truck className="w-6 h-6 mr-2 text-teal-600" />
                    Car Inventory Management
                  </h2>
                  <button
                    onClick={() => {
                      setShowCarInventoryModal(false);
                      setSelectedCar(null);
                      setCarInventoryAssignments([]);
                    }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex h-[calc(90vh-80px)]">
                {/* Left Panel - Car Selection */}
                <div className="w-64 border-r border-brand-200 bg-steel-50 overflow-y-auto">
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
                      Select Car
                    </h3>
                    {cars.length === 0 ? (
                      <p className="text-sm text-slate-500">No cars available</p>
                    ) : (
                      <div className="space-y-2">
                        {cars.map(car => (
                          <button
                            key={car.id}
                            onClick={() => {
                              setSelectedCar(car.id);
                              fetchCarInventoryAssignments(car.id);
                            }}
                            className={`w-full text-left p-3 rounded-xl transition-colors ${
                              selectedCar === car.id
                                ? 'bg-teal-100 border-2 border-teal-500'
                                : 'bg-white border border-brand-200 hover:border-teal-300'
                            }`}
                          >
                            <div className="font-medium text-slate-900">
                              {car.car_brand} {car.car_model}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {car.plate_number}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Panel - Car Items */}
                <div className="flex-1 overflow-y-auto">
                  {!selectedCar ? (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      <div className="text-center">
                        <Truck className="w-16 h-16 mx-auto text-steel-300 mb-4" />
                        <p>Select a car to view its inventory</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      {/* Action Buttons */}
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-slate-900">
                          Items in {cars.find(c => c.id === selectedCar)?.car_brand} {cars.find(c => c.id === selectedCar)?.car_model}
                        </h3>
                        <button
                          onClick={() => setShowAssignToCarModal(true)}
                          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Assign Item
                        </button>
                      </div>

                      {/* Hazardous Items Warning */}
                      {carInventoryAssignments.some(a => a.item_is_hazardous && a.status === 'assigned') && (
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
                          <div className="flex items-center">
                            <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
                            <span className="text-sm font-medium text-orange-800">
                              This car contains hazardous materials
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Items List */}
                      {carInventoryLoading ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                          <p className="text-sm text-slate-500 mt-2">Loading...</p>
                        </div>
                      ) : carInventoryAssignments.filter(a => a.status === 'assigned').length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Package className="w-12 h-12 mx-auto text-steel-300 mb-3" />
                          <p>No items assigned to this car</p>
                          <p className="text-sm mt-1">Click "Assign Item" to add inventory</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {carInventoryAssignments.filter(a => a.status === 'assigned').map(assignment => (
                            <div
                              key={assignment.id}
                              className={`bg-white border border-brand-200 rounded-xl p-4 ${
                                assignment.item_is_hazardous ? 'border-orange-300 bg-orange-50' : 'border-brand-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center">
                                    <span className="font-medium text-slate-900">
                                      {assignment.item_name}
                                    </span>
                                    {assignment.item_is_hazardous && (
                                      <span className="ml-2 text-orange-500" title="Hazardous Material">⚠️</span>
                                    )}
                                  </div>
                                  <div className="text-sm text-slate-500 mt-1">
                                    <span className="mr-4">Qty: {assignment.quantity}</span>
                                    <span className="mr-4">SKU: {assignment.item_sku || 'N/A'}</span>
                                    <span>Assigned: {new Date(assignment.assigned_date).toLocaleDateString()}</span>
                                  </div>
                                  {assignment.notes && (
                                    <div className="text-xs text-slate-400 mt-1">
                                      Note: {assignment.notes}
                                    </div>
                                  )}
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleReturnFromCar(assignment.id)}
                                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-xl hover:bg-green-200"
                                    title="Return to warehouse"
                                  >
                                    Return
                                  </button>
                                  <button
                                    onClick={() => handleMarkDamaged(assignment.id)}
                                    className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-xl hover:bg-yellow-200"
                                    title="Mark as damaged"
                                  >
                                    Damaged
                                  </button>
                                  <button
                                    onClick={() => handleMarkLost(assignment.id)}
                                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-xl hover:bg-red-200"
                                    title="Mark as lost"
                                  >
                                    Lost
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* History Section - Returned/Lost/Damaged Items */}
                      {carInventoryAssignments.filter(a => a.status !== 'assigned').length > 0 && (
                        <div className="mt-8">
                          <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
                            History
                          </h4>
                          <div className="space-y-2">
                            {carInventoryAssignments.filter(a => a.status !== 'assigned').map(assignment => (
                              <div
                                key={assignment.id}
                                className="bg-steel-50 border border-brand-200 rounded-xl p-3 opacity-70"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="text-sm text-slate-700">{assignment.item_name}</span>
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                      assignment.status === 'returned' ? 'bg-green-100 text-green-700' :
                                      assignment.status === 'lost' ? 'bg-red-100 text-red-700' :
                                      'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {assignment.status}
                                    </span>
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {assignment.return_date && new Date(assignment.return_date).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assign Item to Car Modal */}
        {showAssignToCarModal && (
          <AssignToCarModal
            carId={selectedCar}
            car={selectedCar ? cars.find(c => c.id === selectedCar) : null}
            cars={cars}
            items={allItemsUnfiltered.length > 0 ? allItemsUnfiltered : items}
            selectedItems={selectedItems}
            allInventoryItems={allItemsUnfiltered}
            loadingAllItems={loadingAllItems}
            ensureAllItemsLoaded={ensureAllItemsLoaded}
            onClose={() => {
              setShowAssignToCarModal(false);
              if (!showCarInventoryModal) {
                setSelectedItems([]);
              }
            }}
            onAssign={handleAssignToCar}
          />
        )}

          </div> {/* End Right Content Area */}
        </div> {/* End Main Layout: Sidebar + Content */}

      </div>
    </div>
  );
};

// Add Item Modal Component
const AddItemModal = ({ categories, customFields, onSave, onClose, onCategoryDeleted }) => {
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState(null);
  const [filteredModels, setFilteredModels] = useState([]);
  const [categoryType, setCategoryType] = useState('warehouse'); // 'warehouse' or 'equipment'

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    subcategory: '',
    description: '',
    unit: 'pcs',
    unit_price: '',
    quantity_in_stock: '',
    minimum_stock_level: '',
    location: '',
    supplier: ''
  });
  
  // State for custom field values
  const [customFieldValues, setCustomFieldValues] = useState({});
  
  // State for SKU functionality
  const [skuSuggestions, setSkuSuggestions] = useState(null);
  const [showSKUSuggestions, setShowSKUSuggestions] = useState(false);
  const [skuValidation, setSkuValidation] = useState(null);
  
  // State for dynamically added fields during item creation
  const [dynamicFields, setDynamicFields] = useState([]);
  const [showAddFieldForm, setShowAddFieldForm] = useState(false);
  const [newFieldDefinition, setNewFieldDefinition] = useState({
    name: '',
    field_type: 'text',
    is_required: false,
    help_text: ''
  });

  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState(categories);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [itemPicture, setItemPicture] = useState(null);

  // Update availableCategories when categories prop changes
  useEffect(() => {
    setAvailableCategories(categories.filter(cat => cat.value !== 'all'));
  }, [categories]);

  // Fetch equipment types and models on mount
  useEffect(() => {
    fetchEquipmentData();
  }, []);

  const fetchEquipmentData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Fetch equipment types
      const typesResponse = await axios.get(`${API_BASE_URL}/safetyequipmenttype/`, config);
      const typesData = Array.isArray(typesResponse.data) ? typesResponse.data :
                       typesResponse.data?.results ? typesResponse.data.results : [];
      setEquipmentTypes(typesData);

      // Fetch equipment models
      const modelsResponse = await axios.get(`${API_BASE_URL}/safetyequipmentmodel/`, config);
      const modelsData = Array.isArray(modelsResponse.data) ? modelsResponse.data :
                        modelsResponse.data?.results ? modelsResponse.data.results : [];
      setEquipmentModels(modelsData);
    } catch (error) {
      console.error('Error fetching equipment data:', error);
    }
  };

  // Filter models based on selected equipment type
  useEffect(() => {
    if (selectedEquipmentType && Array.isArray(equipmentModels) && equipmentModels.length > 0) {
      const filtered = equipmentModels.filter(model =>
        model.safety_equipment_type === selectedEquipmentType
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels([]);
    }
  }, [selectedEquipmentType, equipmentModels]);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category || formData.category === '') {
        setAvailableSubcategories([]);
        setFormData(prev => ({ ...prev, subcategory: '' }));
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/?category=${formData.category}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const subcategoriesData = Array.isArray(response.data) ? response.data : response.data.results || [];
        setAvailableSubcategories(subcategoriesData);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setAvailableSubcategories([]);
      }
    };

    fetchSubcategories();
  }, [formData.category]);

  // Handle category selection change
  const handleCategoryChange = (value) => {
    // Check if it's an equipment type (prefixed with 'equipment_')
    if (value.startsWith('equipment_')) {
      const typeId = parseInt(value.replace('equipment_', ''));
      setSelectedEquipmentType(typeId);
      setCategoryType('equipment');
      handleChange('category', value);

      // Auto-fill name if equipment type is selected
      if (Array.isArray(equipmentTypes)) {
        const equipmentType = equipmentTypes.find(t => t.id === typeId);
        if (equipmentType) {
          handleChange('name', equipmentType.safety_equipment_type_name);
        }
      }
    } else {
      setSelectedEquipmentType(null);
      setCategoryType('warehouse');
      handleChange('category', value);
    }
  };

  const unitOptions = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'm', label: 'Meters' },
    { value: 'm2', label: 'Square Meters' },
    { value: 'm3', label: 'Cubic Meters' },
    { value: 'l', label: 'Liters' },
    { value: 'pairs', label: 'Pairs' },
    { value: 'sets', label: 'Sets' },
    { value: 'rolls', label: 'Rolls' },
    { value: 'boxes', label: 'Boxes' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.category || !formData.unit_price) {
      alert('Please fill in all required fields (Name, Category, and Unit Price)');
      return;
    }

    // Validate required dynamic fields (only private fields for this item)
    for (const field of dynamicFields) {
      if (field.is_required && !customFieldValues[field.id]) {
        alert(`Private field "${field.display_name || field.name}" is required`);
        return;
      }
    }

    try {
      // First, create any dynamic field definitions in the backend
      const fieldDefinitionIds = {};
      
      for (const field of dynamicFields) {
        // Always create field definition, regardless of whether it has a value
        const response = await axios.post(`${API_BASE_URL}/api/warehouse/custom-fields/`, {
          name: field.internal_name || field.name, // Use unique internal name for backend
          field_type: field.field_type,
          is_required: field.is_required,
          help_text: field.help_text
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
        fieldDefinitionIds[field.id] = response.data.id;
      }

      // Prepare custom fields data for API - include only private dynamic fields
      const customFieldsData = dynamicFields.map(field => {
        const fieldId = field.id;
        const definitionId = fieldDefinitionIds[fieldId] || parseInt(fieldId);
        const value = customFieldValues[fieldId] || ''; // Get value or empty string
        return {
          field_definition_id: definitionId,
          value: value
        };
      }).filter(item => item.field_definition_id); // Only include fields that have definition IDs

      const dataToSave = {
        ...formData,
        category: parseInt(formData.category),
        subcategory: formData.subcategory ? parseInt(formData.subcategory) : null,
        unit_price: parseFloat(formData.unit_price),
        quantity_in_stock: parseInt(formData.quantity_in_stock) || 0,
        minimum_stock_level: parseInt(formData.minimum_stock_level) || 0,
        custom_fields_data: customFieldsData,
        // Enable tracking when there are dynamic fields OR when we're creating custom field data
        requires_individual_tracking: dynamicFields.length > 0 || customFieldsData.length > 0
      };


      onSave(dataToSave, invoiceFile, itemPicture);
    } catch (error) {
      console.error('Error creating dynamic fields:', error);
      
      // Check if it's a unique constraint error
      if (error.response?.status === 500 && error.response?.data?.detail?.includes?.('UNIQUE constraint failed')) {
        alert('Error: One of the field names conflicts with an existing field. The system has automatically made them unique. Please try again.');
      } else if (error.response?.data?.name && Array.isArray(error.response.data.name)) {
        alert(`Field name error: ${error.response.data.name[0]}`);
      } else {
        alert('Error creating custom fields: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate SKU in real-time
    if (field === 'sku' && value.trim()) {
      validateSKU(value.trim());
    }
  };

  // Toggle existing SKUs display
  const toggleExistingSKUs = async () => {
    
    // If already showing, just hide
    if (showSKUSuggestions) {
      setShowSKUSuggestions(false);
      return;
    }
    
    // If not showing, fetch and show
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      const itemsData = Array.isArray(response.data) ? response.data : 
                       response.data.results ? response.data.results : [];
      
      // Extract existing SKUs
      const existingSKUs = itemsData
        .map(item => item.sku)
        .filter(sku => sku && sku.trim() !== '')
        .sort();
      
      
      setSkuSuggestions({
        existing_skus: existingSKUs,
        total_count: existingSKUs.length
      });
      setShowSKUSuggestions(true);
    } catch (error) {
      console.error('❌ Error fetching existing SKUs:', error);
      alert('Error fetching existing SKUs. Please try again.');
    }
  };

  // Select SKU from suggestions
  const selectSKU = (sku) => {
    setFormData(prev => ({ ...prev, sku }));
    setShowSKUSuggestions(false);
    validateSKU(sku);
  };

  // Validate SKU
  const validateSKU = async (sku) => {
    if (!sku) {
      setSkuValidation(null);
      return;
    }


    try {
      const response = await axios.post(`${API_BASE_URL}/api/warehouse/items/validate_sku/`, {
        sku: sku
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      setSkuValidation(response.data);
    } catch (error) {
      console.error('❌ Error validating SKU:', error);
      console.error('Validation error status:', error.response?.status);
      setSkuValidation({ valid: false, message: 'Error validating SKU' });
    }
  };

  const handleAddDynamicField = () => {
    if (!newFieldDefinition.name.trim()) {
      alert('Field name is required');
      return;
    }

    // Create a unique field name to avoid conflicts with other items
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    const uniqueFieldName = `${newFieldDefinition.name}_item_${timestamp}_${randomId}`;

    const newField = {
      id: `temp_${timestamp}_${randomId}`, // Temporary ID for frontend
      ...newFieldDefinition,
      internal_name: uniqueFieldName, // Unique name for backend
      display_name: newFieldDefinition.name, // Display name for user
      is_dynamic: true // Flag to identify dynamically created fields
    };

    setDynamicFields(prev => [...prev, newField]);
    setNewFieldDefinition({
      name: '',
      field_type: 'text',
      is_required: false,
      help_text: ''
    });
    setShowAddFieldForm(false);
  };

  const handleRemoveDynamicField = (fieldId) => {
    setDynamicFields(prev => prev.filter(field => field.id !== fieldId));
    setCustomFieldValues(prev => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
  };


  const handleDeleteCategoryInForm = async (categoryId, categoryName) => {
    if (onCategoryDeleted) {
      const success = await onCategoryDeleted(categoryId, categoryName);
      
      // Reset form if deleted category was selected and deletion was successful
      if (success && formData.category == categoryId) {
        setFormData(prev => ({ ...prev, category: '' }));
      }
    }
  };

  const handleCreateCategory = async (categoryData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/warehouse/categories/`, categoryData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      // Cache the show_in_equipment setting
      const showInEquip = categoryData.show_in_equipment || false;
      setCachedSetting(response.data.id, showInEquip);
      const newCategory = {
        value: response.data.id,
        label: response.data.name,
        show_in_equipment: showInEquip,
        description: response.data.description || ''
      };
      setAvailableCategories(prev => [...prev, newCategory]);
      setFormData(prev => ({ ...prev, category: response.data.id }));
      setShowNewCategoryModal(false);
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Add New Inventory Item</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1.5fr 2fr' }}>
              {/* Column 1: Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Internal Number
                  </label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => handleChange('sku', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="Optional internal reference"
                  />
                </div>

                <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <select
                      value={formData.category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select category or equipment type...</option>
                      <optgroup label="Warehouse Categories">
                        {availableCategories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </optgroup>
                      {Array.isArray(equipmentTypes) && equipmentTypes.length > 0 && (
                        <optgroup label="Equipment Types">
                          {equipmentTypes.map(type => (
                            <option key={`equipment_${type.id}`} value={`equipment_${type.id}`}>
                              {type.safety_equipment_type_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewCategoryModal(true)}
                    className="px-3 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm"
                    title="Create new category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Show subcategory selection if warehouse category is selected */}
              {formData.category && !formData.category.startsWith('equipment_') && availableSubcategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subcategory (Optional)
                  </label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => handleChange('subcategory', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">Select subcategory...</option>
                    {availableSubcategories.map(subcat => (
                      <option key={subcat.id} value={subcat.id}>
                        {subcat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Show model selection if equipment type is selected */}
              {categoryType === 'equipment' && filteredModels.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Equipment Model (Optional)
                  </label>
                  <select
                    value={formData.equipment_model || ''}
                    onChange={(e) => {
                      const modelId = e.target.value;
                      handleChange('equipment_model', modelId);

                      // Auto-fill SKU with model name if model is selected
                      if (Array.isArray(filteredModels)) {
                        const model = filteredModels.find(m => m.id === parseInt(modelId));
                        if (model) {
                          const timestamp = Date.now().toString(36).toUpperCase();
                          handleChange('sku', `${model.safety_equipment_model_name}-${timestamp}`);
                        }
                      }
                    }}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">Select model...</option>
                    {Array.isArray(filteredModels) && filteredModels.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.safety_equipment_model_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit *
                  </label>
                  <select
                    value={formData.unit}
                    onChange={(e) => handleChange('unit', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  >
                    {unitOptions.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Column 2: Pricing & Stock */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit Price ({getCurrencySymbol()}) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => handleChange('unit_price', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Initial Stock Quantity
                  </label>
                  <input
                    type="number"
                    value={formData.quantity_in_stock}
                    onChange={(e) => handleChange('quantity_in_stock', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Minimum Stock Level
                  </label>
                  <input
                    type="number"
                    value={formData.minimum_stock_level}
                    onChange={(e) => handleChange('minimum_stock_level', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Supplier
                  </label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => handleChange('supplier', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                {/* Item Picture Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Item Picture (Optional)
                  </label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => setItemPicture(e.target.files[0] || null)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Supported formats: JPG, PNG, WEBP (Max 5MB)
                  </p>
                  {itemPicture && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Image selected: {itemPicture.name}
                    </div>
                  )}
                </div>

                {/* Invoice Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Invoice (Optional)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => setInvoiceFile(e.target.files[0] || null)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
                  </p>
                  {invoiceFile && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      File selected: {invoiceFile.name}
                    </div>
                  )}
                </div>
              </div>

              {/* Column 3: Location & Details */}
              <div className="space-y-4">
                <LocationPicker
                  selectedLocation={formData.location}
                  onLocationChange={(location) => handleChange('location', location)}
                  required={false}
                  multiple={true}
                />

                {/* Custom Fields Section - Always show the "Add Field" option */}
                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-medium text-slate-900">Private Additional Fields</h4>
                    <button
                      type="button"
                      onClick={() => setShowAddFieldForm(true)}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700"
                    >
                      + Add Field
                    </button>
                  </div>

                  {/* Add field form */}
                  {showAddFieldForm && (
                    <div className="mb-6 p-4 border border-brand-200 rounded-xl bg-steel-50">
                      <h5 className="text-md font-medium text-slate-900 mb-3">New Field</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700">Field Name</label>
                          <input
                            type="text"
                            value={newFieldDefinition.name}
                            onChange={(e) => setNewFieldDefinition(prev => ({...prev, name: e.target.value}))}
                            placeholder="np. Power of generator"
                            className="mt-1 block w-full rounded-xl border-brand-200 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700">Field Type</label>
                          <select
                            value={newFieldDefinition.field_type}
                            onChange={(e) => setNewFieldDefinition(prev => ({...prev, field_type: e.target.value}))}
                            className="mt-1 block w-full rounded-xl border-brand-200 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="decimal">Decimal</option>
                            <option value="date">Date</option>
                            <option value="boolean">Yes/No</option>
                            <option value="textarea">Long Text</option>
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700">Help Text</label>
                          <input
                            type="text"
                            value={newFieldDefinition.help_text}
                            onChange={(e) => setNewFieldDefinition(prev => ({...prev, help_text: e.target.value}))}
                            placeholder="Describe what the user should enter"
                            className="mt-1 block w-full rounded-xl border-brand-200 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={newFieldDefinition.is_required}
                              onChange={(e) => setNewFieldDefinition(prev => ({...prev, is_required: e.target.checked}))}
                              className="rounded border-steel-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-slate-700">Required Field</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2 mt-3">
                        <button
                          type="button"
                          onClick={() => setShowAddFieldForm(false)}
                          className="px-3 py-1 text-sm border border-brand-200 rounded-xl text-slate-700 hover:bg-steel-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddDynamicField}
                          className="px-3 py-1 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20"
                        >
                          Add Field
                        </button>
                      </div>
                    </div>
                  )}

                  {dynamicFields.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Dynamic fields added in this session (private to this item) */}
                      {dynamicFields.map((field) => (
                        <div key={field.id} className="relative">
                          <div className="flex justify-between items-start mb-1">
                            <label className="block text-sm font-medium text-slate-700">
                              {field.display_name || field.name} {field.is_required && <span className="text-red-500">*</span>}
                              <span className="text-xs text-green-600 ml-1">(private)</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => handleRemoveDynamicField(field.id)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              ✕
                            </button>
                          </div>
                          {field.help_text && (
                            <p className="text-xs text-slate-500 mb-1">{field.help_text}</p>
                          )}

                          {field.field_type === 'text' && (
                            <input
                              type="text"
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.id]: e.target.value
                              }))}
                              className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          )}

                          {field.field_type === 'number' && (
                            <input
                              type="number"
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.id]: e.target.value
                              }))}
                              className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          )}

                          {field.field_type === 'decimal' && (
                            <input
                              type="number"
                              step="0.01"
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.id]: e.target.value
                              }))}
                              className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          )}

                          {field.field_type === 'date' && (
                            <input
                              type="date"
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.id]: e.target.value
                              }))}
                              className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          )}

                          {field.field_type === 'boolean' && (
                            <div className="flex items-center mt-2">
                              <input
                                type="checkbox"
                                checked={customFieldValues[field.id] === true || customFieldValues[field.id] === 'true'}
                                onChange={(e) => setCustomFieldValues(prev => ({
                                  ...prev,
                                  [field.id]: e.target.checked
                                }))}
                                className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                              />
                              <span className="ml-2 text-sm text-slate-700">Yes</span>
                            </div>
                          )}

                          {field.field_type === 'textarea' && (
                            <textarea
                              rows="3"
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.id]: e.target.value
                              }))}
                              className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show message if no fields */}
                  {dynamicFields.length === 0 && !showAddFieldForm && (
                    <p className="text-slate-500 text-center py-4">
                      No additional fields for this item. Click "+ Add Field" to create private fields that will only apply to this item.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-600 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors"
              >
                Add Item
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* New Category Modal */}
      {showNewCategoryModal && (
        <NewCategoryModal 
          onSave={handleCreateCategory}
          onClose={() => setShowNewCategoryModal(false)}
        />
      )}
    </div>
  );
};

// New Category Modal Component
const NewCategoryModal = ({ onSave, onClose }) => {
  const [categoryData, setCategoryData] = useState({
    name: '',
    description: '',
    show_in_equipment: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!categoryData.name.trim()) {
      alert('Please enter a category name');
      return;
    }

    onSave(categoryData);
  };

  const handleChange = (field, value) => {
    setCategoryData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-60">
      <div className="relative top-32 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Create New Category</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                value={categoryData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Safety Equipment, Tools, Electronics"
                className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                rows="3"
                value={categoryData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe what types of items belong to this category..."
                className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center space-x-3 p-3 bg-brand-50 rounded-xl">
              <input
                type="checkbox"
                id="show_in_equipment"
                checked={categoryData.show_in_equipment}
                onChange={(e) => handleChange('show_in_equipment', e.target.checked)}
                className="w-5 h-5 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
              />
              <label htmlFor="show_in_equipment" className="text-sm font-medium text-slate-700">
                Display items from this category in Equipment Management
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-600 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
              >
                Create Category
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Item Modal Component
const EditItemModal = ({ item, categories, onSave, onClose, onCategoryDeleted }) => {

  const [formData, setFormData] = useState({
    name: item.name || '',
    sku: item.sku || '',
    category: '', // Will be set from API
    subcategory: '', // Will be set from API
    description: item.description || '',
    unit: item.unit || 'pcs',
    unit_price: item.unitPrice || '',
    quantity_in_stock: item.quantity || '',
    minimum_stock_level: item.minStock || '',
    location: item.location || '',
    supplier: item.supplier || '',
    requires_individual_tracking: item.requires_individual_tracking || false
  });

  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState(categories);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  
  // Update availableCategories when categories prop changes
  useEffect(() => {
    setAvailableCategories(categories.filter(cat => cat.value !== 'all'));
  }, [categories]);
  
  // Custom fields state
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [itemPicture, setItemPicture] = useState(null);
  const [currentPicture, setCurrentPicture] = useState(item.item_picture || null);

  // Fetch full item data when modal opens
  useEffect(() => {
    const fetchItemData = async () => {
      console.log('🔵 EditItemModal: useEffect fetchItemData started');
      console.log('🔵 item.id:', item.id);
      console.log('🔵 item.originalId:', item.originalId);

      try {
        // Fetch item data
        const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);
        console.log('🔵 Fetching item data for originalId:', originalId);

        const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/${originalId}/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const itemData = response.data;
        console.log('🔵 Item data fetched successfully:', itemData);
        // Preserve the location data exactly as it comes from the backend
        // If it contains multiple locations (comma-separated), keep them
        setFormData({
          name: itemData.name || '',
          sku: itemData.sku || '',
          category: itemData.category || '',
          subcategory: itemData.subcategory || '',
          description: itemData.description || '',
          unit: itemData.unit || 'pcs',
          unit_price: itemData.unit_price || '',
          quantity_in_stock: itemData.quantity_in_stock || '',
          minimum_stock_level: itemData.minimum_stock_level || '',
          location: itemData.location || '', // This will preserve comma-separated locations
          supplier: itemData.supplier || '',
          requires_individual_tracking: itemData.requires_individual_tracking || false
        });

        // Set current picture
        setCurrentPicture(itemData.item_picture || null);

        // Use custom fields from the item data (private fields only)
        const itemCustomFields = itemData.custom_fields || [];

        // Extract field definitions from the item's custom fields
        const fieldsData = itemCustomFields.map(cf => cf.field_definition || cf);
        console.log('🔵 Custom fields definitions extracted:', fieldsData);
        setCustomFields(fieldsData);

        // Fetch existing custom field values for this item using the dedicated endpoint
        console.log('🔵 Fetching custom field values for item_id:', originalId);
        const valuesResponse = await axios.get(`${API_BASE_URL}/api/warehouse/custom-field-values/by_item/?item_id=${originalId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        console.log('🔵 Custom field values API response:', valuesResponse.data);

        const valuesData = Array.isArray(valuesResponse.data) ? valuesResponse.data :
                          valuesResponse.data.results ? valuesResponse.data.results : [];
        console.log('🔵 Parsed valuesData array:', valuesData);

        // Convert values to field_definition_id -> value mapping
        const fieldValues = {};
        valuesData.forEach((valueItem, index) => {
          console.log(`🔍 Processing valueItem[${index}]:`, valueItem);
          console.log(`🔍 valueItem.field_definition:`, valueItem.field_definition);
          console.log(`🔍 valueItem.value:`, valueItem.value);

          const fieldId = valueItem.field_definition?.id || valueItem.field_definition;
          console.log(`🔍 Extracted fieldId:`, fieldId);

          let value = valueItem.value;
          console.log(`🔍 Initial value:`, value);

          // Handle boolean values specifically for checkboxes
          if (valueItem.field_definition?.field_type === 'boolean') {
            value = value === true || value === 'true' || value === 'True';
            console.log(`🔍 Boolean value converted to:`, value);
          }

          console.log(`🔍 Checking if value is not null/undefined: ${value !== null && value !== undefined}`);
          if (value !== null && value !== undefined) {
            fieldValues[fieldId] = value;
            console.log(`✅ Added to fieldValues[${fieldId}] = ${value}`);
          } else {
            console.log(`❌ Skipped because value is null or undefined`);
          }
        });
        console.log('📋 EditItemModal: Loaded custom field values:', fieldValues);
        console.log('📋 EditItemModal: valuesData from API:', valuesData);
        console.log('🔵 Setting customFieldValues state to:', fieldValues);
        setCustomFieldValues(fieldValues);

        // Fetch subcategories for the initial category if it exists
        if (itemData.category) {
          try {
            const subResponse = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/?category=${itemData.category}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            const subcategoriesData = Array.isArray(subResponse.data) ? subResponse.data : subResponse.data.results || [];
            setAvailableSubcategories(subcategoriesData);
          } catch (error) {
            console.error('Error fetching initial subcategories:', error);
            setAvailableSubcategories([]);
          }
        }

      } catch (error) {
        console.error('🔴 Error fetching item data:', error);
        console.error('🔴 Error details:', error.response?.data || error.message);
      }
    };

    console.log('🔵 EditItemModal: Calling fetchItemData()');
    fetchItemData();
  }, [item.id]);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category || formData.category === '') {
        setAvailableSubcategories([]);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/?category=${formData.category}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const subcategoriesData = Array.isArray(response.data) ? response.data : response.data.results || [];
        setAvailableSubcategories(subcategoriesData);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setAvailableSubcategories([]);
      }
    };

    fetchSubcategories();
  }, [formData.category]);

  const unitOptions = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'm', label: 'Meters' },
    { value: 'm2', label: 'Square Meters' },
    { value: 'm3', label: 'Cubic Meters' },
    { value: 'l', label: 'Liters' },
    { value: 'pairs', label: 'Pairs' },
    { value: 'sets', label: 'Sets' },
    { value: 'rolls', label: 'Rolls' },
    { value: 'boxes', label: 'Boxes' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log('🚀 EditItemModal handleSubmit called');
    console.log('📋 customFieldValues state:', customFieldValues);
    console.log('📋 customFields definitions:', customFields);

    // Basic validation
    if (!formData.name || !formData.category || !formData.unit_price) {
      alert('Please fill in all required fields (Name, Category, and Unit Price)');
      return;
    }

    // Validate required custom fields
    for (const field of customFields) {
      if (field.is_required && !customFieldValues[field.id]) {
        alert(`Field "${field.name}" is required`);
        return;
      }
    }

    // Prepare custom fields data for API
    const customFieldsData = Object.entries(customFieldValues)
      .filter(([fieldId, value]) => value !== '' && value !== null && value !== undefined)
      .map(([fieldId, value]) => ({
        field_definition_id: parseInt(fieldId),
        value: value
      }));

    console.log('📋 customFieldsData prepared for API:', customFieldsData);

    const dataToSave = {
      ...formData,
      category: parseInt(formData.category),
      subcategory: formData.subcategory ? parseInt(formData.subcategory) : null,
      unit_price: parseFloat(formData.unit_price),
      quantity_in_stock: parseInt(formData.quantity_in_stock) || 0,
      minimum_stock_level: parseInt(formData.minimum_stock_level) || 0,
      custom_fields_data: customFieldsData
    };


    onSave(dataToSave, itemPicture);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteCategoryInEditForm = async (categoryId, categoryName) => {
    if (onCategoryDeleted) {
      const success = await onCategoryDeleted(categoryId, categoryName);
      
      // Reset form if deleted category was selected and deletion was successful
      if (success && formData.category == categoryId) {
        setFormData(prev => ({ ...prev, category: '' }));
      }
    }
  };

  const handleCreateCategory = async (categoryData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/warehouse/categories/`, categoryData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      // Cache the show_in_equipment setting
      const showInEquip = categoryData.show_in_equipment || false;
      setCachedSetting(response.data.id, showInEquip);
      const newCategory = {
        value: response.data.id,
        label: response.data.name,
        show_in_equipment: showInEquip,
        description: response.data.description || ''
      };
      setAvailableCategories(prev => [...prev, newCategory]);
      setFormData(prev => ({ ...prev, category: response.data.id }));
      setShowNewCategoryModal(false);
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Edit Inventory Item</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1.5fr 2fr' }}>
              {/* Column 1: Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Internal Number
                  </label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => handleChange('sku', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="Optional internal reference"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category *
                  </label>
                  <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <select
                      value={formData.category}
                      onChange={(e) => handleChange('category', e.target.value)}
                      className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a category</option>
                      {availableCategories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewCategoryModal(true)}
                    className="px-3 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                    title="Add new category"
                  >
                    +
                  </button>
                </div>
                </div>

                {/* Show subcategory selection if warehouse category is selected */}
                {formData.category && availableSubcategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Subcategory (Optional)
                    </label>
                    <select
                      value={formData.subcategory}
                      onChange={(e) => handleChange('subcategory', e.target.value)}
                      className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    >
                      <option value="">Select subcategory...</option>
                      {availableSubcategories.map(subcat => (
                        <option key={subcat.id} value={subcat.id}>
                          {subcat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit *
                  </label>
                  <select
                    value={formData.unit}
                    onChange={(e) => handleChange('unit', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  >
                    {unitOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Column 2: Pricing & Stock */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit Price ({getCurrencySymbol()}) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => handleChange('unit_price', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Quantity in Stock
                  </label>
                  <input
                    type="number"
                    value={formData.quantity_in_stock}
                    onChange={(e) => handleChange('quantity_in_stock', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Minimum Stock Level
                  </label>
                  <input
                    type="number"
                    value={formData.minimum_stock_level}
                    onChange={(e) => handleChange('minimum_stock_level', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Supplier
                  </label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => handleChange('supplier', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                {/* Item Picture Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Item Picture {currentPicture && '(Update)'}
                  </label>
                  {currentPicture && !itemPicture && (
                    <div className="mb-2 relative inline-block">
                      <img
                        src={currentPicture}
                        alt="Current item"
                        className="w-20 h-20 object-cover rounded-xl border border-brand-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Remove current picture?')) {
                            setCurrentPicture(null);
                            setItemPicture('DELETE'); // Special marker to delete
                          }
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-xs"
                        title="Remove picture"
                      >
                        ✕
                      </button>
                      <p className="text-xs text-slate-500 mt-1">Current picture</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => setItemPicture(e.target.files[0] || null)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Supported formats: JPG, PNG, WEBP (Max 5MB)
                  </p>
                  {itemPicture && itemPicture !== 'DELETE' && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      New image selected: {itemPicture.name}
                    </div>
                  )}
                  {itemPicture === 'DELETE' && (
                    <div className="mt-2 flex items-center text-sm text-red-600">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                      Picture will be removed
                    </div>
                  )}
                </div>
              </div>

              {/* Column 3: Location & Custom Fields */}
              <div className="space-y-4">
                <LocationPicker
                  selectedLocation={formData.location}
                  onLocationChange={(location) => handleChange('location', location)}
                  required={false}
                  multiple={true}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                {/* Custom Fields Section */}
                {customFields.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-3">Additional Fields</h4>
                    <div className="space-y-3">
                  {customFields.map((field) => {
                    // Extract display name from internal name (remove _item_timestamp_randomid suffix)
                    const getDisplayName = (internalName) => {
                      if (!internalName) return '';
                      // Match pattern: "FieldName_item_timestamp_randomid"
                      const match = internalName.match(/^(.+?)_item_\d+_[a-z0-9]+$/);
                      return match ? match[1] : internalName;
                    };
                    const displayName = getDisplayName(field.name);
                    
                    return (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {displayName} {field.is_required && <span className="text-red-500">*</span>}
                        <span className="text-xs text-green-600 ml-1">(private)</span>
                      </label>
                      {field.help_text && (
                        <p className="text-xs text-slate-500 mb-1">{field.help_text}</p>
                      )}
                      
                      {field.field_type === 'text' && (
                        <input
                          type="text"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                          className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      )}
                      
                      {field.field_type === 'number' && (
                        <input
                          type="number"
                          min={field.min_value || undefined}
                          max={field.max_value || undefined}
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                          className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      )}
                      
                      {field.field_type === 'decimal' && (
                        <input
                          type="number"
                          step="0.01"
                          min={field.min_value || undefined}
                          max={field.max_value || undefined}
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                          className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      )}
                      
                      {field.field_type === 'date' && (
                        <input
                          type="date"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                          className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      )}
                      
                      {field.field_type === 'boolean' && (
                        <div className="flex items-center mt-2">
                          <input
                            type="checkbox"
                            checked={customFieldValues[field.id] === true || customFieldValues[field.id] === 'true'}
                            onChange={(e) => setCustomFieldValues(prev => ({
                              ...prev,
                              [field.id]: e.target.checked
                            }))}
                            className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                          />
                          <span className="ml-2 text-sm text-slate-700">Yes</span>
                        </div>
                      )}
                      
                      {field.field_type === 'textarea' && (
                        <textarea
                          rows="3"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                          className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      )}
                    </div>
                    );
                  })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors"
              >
                Update Item
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* New Category Modal */}
      {showNewCategoryModal && <NewCategoryModal 
        onSave={handleCreateCategory}
        onClose={() => setShowNewCategoryModal(false)}
      />}
    </div>
  );
};

// Manage Categories Modal Component
const ManageCategoriesModal = ({ categories, onClose, onRefresh }) => {
  const [categoryList, setCategoryList] = useState(categories);
  const [categoriesFullData, setCategoriesFullData] = useState({});
  const [subcategories, setSubcategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddSubcategoryForm, setShowAddSubcategoryForm] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryHazardous, setNewCategoryHazardous] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [newSubcategoryDescription, setNewSubcategoryDescription] = useState('');
  const [newSubcategoryHazardous, setNewSubcategoryHazardous] = useState(false);

  // Fetch categories and subcategories data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesResponse = await axios.get(`${API_BASE_URL}/api/warehouse/categories/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const data = Array.isArray(categoriesResponse.data) ? categoriesResponse.data : categoriesResponse.data.results || [];
        const dataMap = {};
        data.forEach(cat => {
          dataMap[cat.id] = cat;
        });
        setCategoriesFullData(dataMap);

        // Fetch subcategories
        const subcategoriesResponse = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const subcategoriesData = Array.isArray(subcategoriesResponse.data) ? subcategoriesResponse.data : subcategoriesResponse.data.results || [];

        // Group subcategories by category
        const subcategoriesMap = {};
        subcategoriesData.forEach(subcat => {
          if (!subcategoriesMap[subcat.category]) {
            subcategoriesMap[subcat.category] = [];
          }
          subcategoriesMap[subcat.category].push(subcat);
        });
        setSubcategories(subcategoriesMap);
      } catch (error) {
        console.error('Error fetching categories/subcategories data:', error);
      }
    };
    fetchData();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      const createData = {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim(),
        is_hazardous: newCategoryHazardous
      };
      const response = await axios.post(`${API_BASE_URL}/api/warehouse/categories/`, createData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const newCategory = {
        value: response.data.id,
        label: response.data.name,
        description: response.data.description || '',
        is_hazardous: response.data.is_hazardous || false
      };
      setCategoryList(prev => [...prev, newCategory]);
      setCategoriesFullData(prev => ({ ...prev, [response.data.id]: response.data }));
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryHazardous(false);
      setShowAddForm(false);
      onRefresh();
      alert('Category added successfully!');
    } catch (error) {
      console.error('Error adding category:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.name?.[0] || error.response?.data?.detail || 'Error adding category. Please try again.';
      alert(errorMessage);
    }
  };

  const handleEditCategory = async (categoryId, newName, newDescription, isHazardous = false) => {
    if (!newName.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      const updateData = {
        name: newName.trim(),
        description: newDescription.trim(),
        is_hazardous: isHazardous
      };
      const response = await axios.put(`${API_BASE_URL}/api/warehouse/categories/${categoryId}/`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      setCategoryList(prev => prev.map(cat =>
        cat.value === categoryId
          ? { ...cat, label: newName.trim(), is_hazardous: isHazardous }
          : cat
      ));
      setCategoriesFullData(prev => ({ ...prev, [categoryId]: response.data }));
      setEditingCategory(null);
      onRefresh();
      alert('Category updated successfully!');
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Error updating category. Please try again.');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"? This will affect all items and subcategories in this category.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/categories/${categoryId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setCategoryList(prev => prev.filter(cat => cat.value !== categoryId));
      // Also remove subcategories for this category
      setSubcategories(prev => {
        const updated = { ...prev };
        delete updated[categoryId];
        return updated;
      });
      onRefresh();
      alert('Category deleted successfully!');
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.status === 400
        ? 'Cannot delete category. It may contain items that need to be moved to another category first.'
        : 'Error deleting category. Please try again.';
      alert(errorMessage);
    }
  };

  // Subcategory management functions
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleAddSubcategory = async (categoryId) => {
    if (!newSubcategoryName.trim()) {
      alert('Please enter a subcategory name');
      return;
    }

    try {
      const createData = {
        name: newSubcategoryName.trim(),
        description: newSubcategoryDescription.trim(),
        category: categoryId,
        is_hazardous: newSubcategoryHazardous
      };
      const response = await axios.post(`${API_BASE_URL}/api/warehouse/subcategories/`, createData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Add subcategory to local state
      setSubcategories(prev => ({
        ...prev,
        [categoryId]: [...(prev[categoryId] || []), response.data]
      }));

      setNewSubcategoryName('');
      setNewSubcategoryDescription('');
      setNewSubcategoryHazardous(false);
      setShowAddSubcategoryForm(null);
      onRefresh();
      alert('Subcategory added successfully!');
    } catch (error) {
      console.error('Error adding subcategory:', error);
      alert('Error adding subcategory. Please try again.');
    }
  };

  const handleEditSubcategory = async (subcategoryId, newName, newDescription, isHazardous = false) => {
    if (!newName.trim()) {
      alert('Please enter a subcategory name');
      return;
    }

    try {
      const updateData = {
        name: newName.trim(),
        description: newDescription.trim(),
        is_hazardous: isHazardous
      };
      const response = await axios.put(`${API_BASE_URL}/api/warehouse/subcategories/${subcategoryId}/`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Update subcategory in local state
      setSubcategories(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(categoryId => {
          updated[categoryId] = updated[categoryId].map(subcat =>
            subcat.id === subcategoryId ? { ...subcat, ...response.data } : subcat
          );
        });
        return updated;
      });

      setEditingSubcategory(null);
      onRefresh();
      alert('Subcategory updated successfully!');
    } catch (error) {
      console.error('Error updating subcategory:', error);
      alert('Error updating subcategory. Please try again.');
    }
  };

  const handleDeleteSubcategory = async (subcategoryId, subcategoryName, categoryId) => {
    if (!window.confirm(`Are you sure you want to delete the subcategory "${subcategoryName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/subcategories/${subcategoryId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      // Remove subcategory from local state
      setSubcategories(prev => ({
        ...prev,
        [categoryId]: prev[categoryId]?.filter(subcat => subcat.id !== subcategoryId) || []
      }));

      onRefresh();
      alert('Subcategory deleted successfully!');
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert('Error deleting subcategory. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Manage Categories</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Add New Category Button */}
          <div className="mb-4">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Category
            </button>
          </div>

          {/* Add Category Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-steel-50 rounded-xl border">
              <h4 className="font-medium text-slate-900 mb-3">Add New Category</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter category name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="2"
                    placeholder="Enter category description"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="new-category-hazardous"
                    checked={newCategoryHazardous}
                    onChange={(e) => setNewCategoryHazardous(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-steel-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="new-category-hazardous" className="ml-2 text-sm text-slate-700 flex items-center">
                    <span className="text-orange-500 mr-1">⚠️</span> Hazardous Materials Category
                  </label>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleAddCategory}
                    className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                  >
                    Add Category
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setNewCategoryName('');
                      setNewCategoryDescription('');
                      setNewCategoryHazardous(false);
                    }}
                    className="px-4 py-2 bg-steel-300 text-slate-700 rounded-xl hover:bg-steel-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Categories List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {categoryList.map((category) => (
              <div key={category.value} className="border border-brand-200 rounded-xl bg-steel-50">
                {editingCategory === category.value ? (
                  <div className="p-3">
                    <EditCategoryForm
                      category={category}
                      onSave={handleEditCategory}
                      onCancel={() => setEditingCategory(null)}
                    />
                  </div>
                ) : (
                  <>
                    {/* Category Header */}
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleCategory(category.value)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          {expandedCategories.has(category.value) ?
                            <ChevronDown className="h-4 w-4" /> :
                            <ChevronRight className="h-4 w-4" />
                          }
                        </button>
                        <div>
                          <div className="font-medium text-slate-900 flex items-center">
                            {category.label}
                            {categoriesFullData[category.value]?.is_hazardous && (
                              <span className="ml-2 text-orange-500" title="Hazardous Materials Category">⚠️</span>
                            )}
                          </div>
                          {categoriesFullData[category.value]?.description && (
                            <div className="text-xs text-slate-500 mt-1">
                              {categoriesFullData[category.value].description}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowAddSubcategoryForm(category.value)}
                          className="text-green-600 hover:text-green-900"
                          title="Add subcategory"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingCategory(category.value)}
                          className="text-brand-600 hover:text-brand-800"
                          title="Edit category"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.value, category.label)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete category"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Add Subcategory Form */}
                    {showAddSubcategoryForm === category.value && (
                      <div className="px-6 pb-3">
                        <div className="p-3 bg-white rounded border">
                          <h5 className="font-medium text-slate-900 mb-2">Add Subcategory</h5>
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={newSubcategoryName}
                              onChange={(e) => setNewSubcategoryName(e.target.value)}
                              className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="Subcategory name"
                            />
                            <textarea
                              value={newSubcategoryDescription}
                              onChange={(e) => setNewSubcategoryDescription(e.target.value)}
                              className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              rows="2"
                              placeholder="Subcategory description"
                            />
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`new-subcategory-hazardous-${category.value}`}
                                checked={newSubcategoryHazardous}
                                onChange={(e) => setNewSubcategoryHazardous(e.target.checked)}
                                className="w-4 h-4 text-orange-600 border-steel-300 rounded focus:ring-orange-500"
                              />
                              <label htmlFor={`new-subcategory-hazardous-${category.value}`} className="ml-2 text-sm text-slate-700 flex items-center">
                                <span className="text-orange-500 mr-1">⚠️</span> Hazardous Materials Subcategory
                              </label>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleAddSubcategory(category.value)}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                              >
                                Add
                              </button>
                              <button
                                onClick={() => {
                                  setShowAddSubcategoryForm(null);
                                  setNewSubcategoryName('');
                                  setNewSubcategoryDescription('');
                                  setNewSubcategoryHazardous(false);
                                }}
                                className="px-3 py-1 bg-steel-300 text-slate-700 rounded-xl hover:bg-steel-400 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Subcategories List */}
                    {expandedCategories.has(category.value) && subcategories[category.value] && (
                      <div className="px-6 pb-3">
                        <div className="space-y-2">
                          {subcategories[category.value].map((subcategory) => (
                            <div key={subcategory.id} className="flex items-center justify-between p-2 bg-white rounded border-l-4 border-green-500">
                              {editingSubcategory === subcategory.id ? (
                                <EditSubcategoryForm
                                  subcategory={subcategory}
                                  onSave={handleEditSubcategory}
                                  onCancel={() => setEditingSubcategory(null)}
                                />
                              ) : (
                                <>
                                  <div>
                                    <div className="font-medium text-slate-800 text-sm flex items-center">
                                      {subcategory.name}
                                      {subcategory.is_hazardous && (
                                        <span className="ml-2 text-orange-500" title="Hazardous Materials Subcategory">⚠️</span>
                                      )}
                                    </div>
                                    {subcategory.description && (
                                      <div className="text-xs text-slate-500">{subcategory.description}</div>
                                    )}
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => setEditingSubcategory(subcategory.id)}
                                      className="text-brand-600 hover:text-brand-800"
                                      title="Edit subcategory"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSubcategory(subcategory.id, subcategory.name, category.value)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete subcategory"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                        {(!subcategories[category.value] || subcategories[category.value].length === 0) && (
                          <div className="text-center py-4 text-slate-500 text-sm">
                            No subcategories. Click + to add the first one.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {categoryList.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No categories found. Add your first category to get started.
            </div>
          )}

          <div className="flex justify-end pt-4 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-steel-300 text-slate-700 rounded-xl hover:bg-steel-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Category Form Component
const EditCategoryForm = ({ category, onSave, onCancel }) => {
  const [name, setName] = useState(category.label);
  const [description, setDescription] = useState('');
  const [isHazardous, setIsHazardous] = useState(false);

  useEffect(() => {
    // Fetch full category data
    const fetchCategoryData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/warehouse/categories/${category.value}/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        setDescription(response.data.description || '');
        setIsHazardous(response.data.is_hazardous || false);
      } catch (error) {
        console.error('Error fetching category data:', error);
      }
    };

    fetchCategoryData();
  }, [category.value]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(category.value, name, description, isHazardous);
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1">
      <div className="space-y-3">
        <div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            placeholder="Category name"
          />
        </div>
        <div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            rows="2"
            placeholder="Category description"
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="category-hazardous"
            checked={isHazardous}
            onChange={(e) => setIsHazardous(e.target.checked)}
            className="w-4 h-4 text-orange-600 border-steel-300 rounded focus:ring-orange-500"
          />
          <label htmlFor="category-hazardous" className="ml-2 text-sm text-slate-700 flex items-center">
            <span className="text-orange-500 mr-1">⚠️</span> Hazardous Materials
          </label>
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="px-3 py-1 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors text-sm"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 bg-steel-300 text-slate-700 rounded-xl hover:bg-steel-400 transition-colors text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

// Edit Subcategory Form Component
const EditSubcategoryForm = ({ subcategory, onSave, onCancel }) => {
  const [name, setName] = useState(subcategory.name);
  const [description, setDescription] = useState(subcategory.description || '');
  const [isHazardous, setIsHazardous] = useState(subcategory.is_hazardous || false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(subcategory.id, name, description, isHazardous);
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1">
      <div className="space-y-2">
        <div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
            placeholder="Subcategory name"
          />
        </div>
        <div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
            rows="2"
            placeholder="Subcategory description"
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id={`subcategory-hazardous-${subcategory.id}`}
            checked={isHazardous}
            onChange={(e) => setIsHazardous(e.target.checked)}
            className="w-4 h-4 text-orange-600 border-steel-300 rounded focus:ring-orange-500"
          />
          <label htmlFor={`subcategory-hazardous-${subcategory.id}`} className="ml-2 text-sm text-slate-700 flex items-center">
            <span className="text-orange-500 mr-1">⚠️</span> Hazardous
          </label>
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="px-3 py-1 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors text-xs"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 bg-steel-300 text-slate-700 rounded-xl hover:bg-steel-400 transition-colors text-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

// Manage Sharing Modal Component
const ManageSharingModal = ({ item, onClose, onRefresh }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isShared, setIsShared] = useState(item.isShared || false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    // Po załadowaniu firm, ustaw które są obecnie wybrane
    if (companies.length > 0 && item.sharedWithCompanies) {
      const companyIds = companies
        .filter(company => item.sharedWithCompanies.includes(company.name))
        .map(company => company.id);
      setSelectedCompanies(companyIds);
    }
  }, [companies, item.sharedWithCompanies]);

  const fetchCompanies = async () => {
    try {
      // Spróbuj najpierw warehouse endpoint dla dostępnych firm
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/accessible_companies/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Jeśli to się udało, użyj tych danych
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      
      // Fallback - stwórz listę manualnie z dostępnymi firmami
      setCompanies([
        { id: 1, name: 'Omega' },
        { id: 2, name: 'EccVestas' },
        { id: 3, name: 'Vestas' },
        { id: 4, name: 'Vulcan' },
        { id: 5, name: 'Waterfall' },
        { id: 6, name: 'M4Wind' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const updateData = {
        is_shared: isShared,
        shared_with_companies: isShared ? selectedCompanies : []
      };


      // Extract the original ID by removing the warehouse_ prefix
      const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);

      await axios.patch(`${API_BASE_URL}/api/warehouse/items/${originalId}/`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      alert('Sharing settings updated successfully!');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error updating sharing settings:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Error updating sharing settings. Please try again.';
      alert(errorMessage);
    }
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-slate-900">
              Manage Sharing: {item.name}
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Current owner info */}
            <div className="bg-steel-50 p-3 rounded-xl">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Owner:</span> {item.ownerCompany}
              </p>
            </div>

            {/* Share toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="shareItem"
                checked={isShared}
                onChange={(e) => setIsShared(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-steel-300 rounded"
              />
              <label htmlFor="shareItem" className="ml-2 block text-sm text-slate-900">
                Share this item with other companies
              </label>
            </div>

            {/* Companies list */}
            {isShared && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Select companies to share with:
                </label>
                {loading ? (
                  <div className="text-center text-slate-500">Loading companies...</div>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {companies.map(company => (
                      <div key={company.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`company-${company.id}`}
                          checked={selectedCompanies.includes(company.id)}
                          onChange={() => handleCompanyToggle(company.id)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-steel-300 rounded"
                        />
                        <label 
                          htmlFor={`company-${company.id}`} 
                          className="ml-2 block text-sm text-slate-900"
                        >
                          {company.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-200 border border-brand-200 rounded-xl hover:bg-steel-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-steel-400"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-xl hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Company Partnerships Management Modal
const CompanyPartnershipsModal = ({ onClose }) => {
  const [partnerships, setPartnerships] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');

  useEffect(() => {
    fetchPartnerships();
    fetchAvailablePartners();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/accessible_companies/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setPartnerships(response.data || []);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
      setPartnerships([]);
    }
  };

  const fetchAvailablePartners = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/available_partners/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setAvailablePartners(response.data || []);
    } catch (error) {
      console.error('Error fetching available partners:', error);
      setAvailablePartners([]);
    } finally {
      setLoading(false);
    }
  };

  const createPartnership = async () => {
    if (!selectedPartner) return;
    
    setCreating(true);
    try {
      await axios.post(`${API_BASE_URL}/api/warehouse/company-access/create_partnership/`, {
        partner_company_id: selectedPartner,
        access_level: 'full_access'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      alert('Partnership created successfully!');
      setSelectedPartner('');
      fetchPartnerships();
      fetchAvailablePartners();
    } catch (error) {
      console.error('Error creating partnership:', error);
      const errorMessage = error.response?.data?.error || 'Error creating partnership';
      alert(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const removePartnership = async (partnerId, partnerName) => {
    if (!window.confirm(`Remove partnership with ${partnerName}?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/company-access/remove_partnership/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        data: {
          partner_company_id: partnerId
        }
      });

      alert('Partnership removed successfully!');
      fetchPartnerships();
      fetchAvailablePartners();
    } catch (error) {
      console.error('Error removing partnership:', error);
      alert('Error removing partnership');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-3/4 max-w-4xl shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-slate-900">
              Company Partnerships
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <p className="text-sm text-slate-600 mb-4">
              Manage partnerships with other companies to enable warehouse inventory sharing.
              Only partnered companies can share inventory items with each other.
            </p>

            {/* Current Partnerships */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-slate-800 mb-3">Current Partnerships</h4>
              {loading ? (
                <div className="text-center text-slate-500">Loading partnerships...</div>
              ) : partnerships.length === 0 ? (
                <div className="text-center text-slate-500 py-4 border-2 border-dashed border-steel-300 rounded-xl">
                  No partnerships established yet
                </div>
              ) : (
                <div className="space-y-3">
                  {partnerships.map(partnership => (
                    <div key={partnership.id} className="flex items-center justify-between p-3 bg-steel-50 rounded-xl border">
                      <div>
                        <span className="font-medium text-slate-900">{partnership.name}</span>
                        <span className="ml-2 text-xs px-2 py-1 bg-brand-100 text-brand-800 rounded-full">
                          {partnership.access_level?.replace('_', ' ') || 'Full Access'}
                        </span>
                      </div>
                      <button
                        onClick={() => removePartnership(partnership.id, partnership.name)}
                        className="text-red-600 hover:text-red-900 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Partnership */}
            <div>
              <h4 className="text-md font-medium text-slate-800 mb-3">Add New Partnership</h4>
              <div className="flex items-center space-x-3">
                <select
                  value={selectedPartner}
                  onChange={(e) => setSelectedPartner(e.target.value)}
                  className="flex-1 px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                  disabled={creating}
                >
                  <option value="">Select a company...</option>
                  {availablePartners.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={createPartnership}
                  disabled={!selectedPartner || creating}
                  className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:bg-steel-400 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Add Partnership'}
                </button>
              </div>
              {availablePartners.length === 0 && !loading && (
                <p className="text-sm text-slate-500 mt-2">
                  All available companies are already partnered with you.
                </p>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="bg-brand-50 p-4 rounded-xl">
            <h4 className="text-sm font-medium text-brand-800 mb-2">How Partnerships Work:</h4>
            <ul className="text-sm text-brand-700 space-y-1">
              <li>• Partnerships are bidirectional - both companies can share inventory with each other</li>
              <li>• Only partnered companies will appear in the sharing options when managing inventory items</li>
              <li>• Removing a partnership will stop all inventory sharing between the companies</li>
              <li>• All partnerships provide full access (view, issue, return, create) by default</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Assign to Car Modal Component
const AssignToCarModal = ({ carId, car, cars = [], items, selectedItems = [], allInventoryItems = [], loadingAllItems = false, ensureAllItemsLoaded = () => {}, onClose, onAssign }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCarId, setSelectedCarId] = useState(carId || null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Person selection - support for two people
  const [people, setPeople] = useState([]);
  const [selectedPersonId1, setSelectedPersonId1] = useState(null);
  const [selectedPersonId2, setSelectedPersonId2] = useState(null);
  const [personSearchTerm, setPersonSearchTerm] = useState('');
  const [loadingPeople, setLoadingPeople] = useState(false);

  // Additional items selection (beyond pre-selected)
  const [additionalItems, setAdditionalItems] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

  // Fetch people on mount
  useEffect(() => {
    const fetchPeople = async () => {
      setLoadingPeople(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/users/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const peopleData = response.data.results || response.data || [];
        setPeople(Array.isArray(peopleData) ? peopleData : []);
      } catch (error) {
        console.error('Error fetching people:', error);
      } finally {
        setLoadingPeople(false);
      }
    };
    fetchPeople();
  }, [API_BASE_URL]);

  // Filter people by search term
  const filteredPeople = useMemo(() => {
    if (!personSearchTerm) return people;
    const term = personSearchTerm.toLowerCase();
    return people.filter(person =>
      person.first_name?.toLowerCase().includes(term) ||
      person.last_name?.toLowerCase().includes(term) ||
      person.email?.toLowerCase().includes(term)
    );
  }, [people, personSearchTerm]);

  // Mode: if we have pre-selected items from bulk selection, we need to select a car
  // If we have a car already selected, we need to select items
  const hasPreSelectedItems = selectedItems.length > 0;
  const needsCarSelection = !carId && hasPreSelectedItems;

  // Get selected items data from items array
  const preSelectedItemsData = useMemo(() => {
    if (!hasPreSelectedItems) return [];
    return selectedItems.map(id => items.find(item => item.id === id)).filter(Boolean);
  }, [selectedItems, items, hasPreSelectedItems]);

  // State for quantities of pre-selected items
  const [itemQuantities, setItemQuantities] = useState({});

  // Initialize quantities when pre-selected items change
  useEffect(() => {
    if (preSelectedItemsData.length > 0) {
      const initialQuantities = {};
      preSelectedItemsData.forEach(item => {
        initialQuantities[item.id] = 1;
      });
      setItemQuantities(initialQuantities);
    }
  }, [preSelectedItemsData.length]);

  // Update quantity for a specific item
  const updateItemQuantity = (itemId, newQuantity) => {
    setItemQuantities(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  };

  // Filter items that are warehouse items and have available stock
  const filteredItems = useMemo(() => {
    const warehouseItems = items.filter(item =>
      item.type === 'warehouse' &&
      item.quantity > 0
    );

    if (!searchTerm) return warehouseItems.slice(0, 50); // Limit initial display

    const term = searchTerm.toLowerCase();
    return warehouseItems.filter(item =>
      item.name?.toLowerCase().includes(term) ||
      item.sku?.toLowerCase().includes(term) ||
      item.category?.toLowerCase().includes(term)
    );
  }, [items, searchTerm]);

  const selectedPerson1 = people.find(p => p.id === selectedPersonId1);
  const selectedPerson2 = people.find(p => p.id === selectedPersonId2);

  // Add item to additional items list
  const addAdditionalItem = (item) => {
    if (!additionalItems.find(ai => ai.id === item.id)) {
      setAdditionalItems(prev => [...prev, item]);
      setItemQuantities(prev => ({
        ...prev,
        [item.id]: 1
      }));
    }
    setSearchTerm('');
  };

  // Remove item from additional items
  const removeAdditionalItem = (itemId) => {
    setAdditionalItems(prev => prev.filter(item => item.id !== itemId));
    setItemQuantities(prev => {
      const newQuantities = { ...prev };
      delete newQuantities[itemId];
      return newQuantities;
    });
  };

  // All items to assign (pre-selected + additional)
  const allItemsToAssign = useMemo(() => {
    const preSelected = preSelectedItemsData || [];
    // Filter out additional items that are already in pre-selected
    const additional = additionalItems.filter(ai => !preSelected.find(ps => ps.id === ai.id));
    return [...preSelected, ...additional];
  }, [preSelectedItemsData, additionalItems]);

  const handleAssign = async () => {
    const targetCarId = selectedCarId || carId;

    if (!targetCarId) {
      alert('Please select a car');
      return;
    }

    // If we have items to assign (pre-selected + additional)
    if (allItemsToAssign.length > 0) {
      setSaving(true);
      try {
        for (const item of allItemsToAssign) {
          const qty = itemQuantities[item.id] || 1;
          await onAssign(targetCarId, item.id, qty, notes, selectedPersonId1, selectedPersonId2);
        }
        onClose();
      } catch (error) {
        console.error('Error assigning items:', error);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Regular single item assignment
    if (!selectedItem) {
      alert('Please select an item');
      return;
    }
    if (quantity <= 0) {
      alert('Quantity must be greater than 0');
      return;
    }
    if (quantity > selectedItem.quantity) {
      alert(`Only ${selectedItem.quantity} available in stock`);
      return;
    }

    setSaving(true);
    try {
      await onAssign(targetCarId, selectedItem.id, quantity, notes, selectedPersonId1, selectedPersonId2);
      onClose();
    } catch (error) {
      console.error('Error assigning item:', error);
    } finally {
      setSaving(false);
    }
  };

  const currentCar = car || cars.find(c => c.id === selectedCarId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-3 border-b border-brand-200 bg-teal-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900 flex items-center">
              <Truck className="w-5 h-5 mr-2 text-teal-600" />
              Assign Items to Car
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content - Three Column Layout */}
        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Column - Person Selection */}
          <div className="w-1/5 border-r border-brand-200 bg-steel-50 flex flex-col">
            <div className="p-3 flex flex-col h-full">
              <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center flex-shrink-0">
                <User className="w-3 h-3 mr-1 text-green-600" />
                Persons (Optional)
              </h4>

              {/* Person Search */}
              <div className="relative mb-2 flex-shrink-0">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                <input
                  type="text"
                  value={personSearchTerm}
                  onChange={(e) => setPersonSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-brand-200 rounded-xl focus:ring-1 focus:ring-green-500 focus:border-transparent"
                  placeholder="Search..."
                />
              </div>

              {loadingPeople ? (
                <div className="p-2 text-center text-slate-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-1 overflow-y-auto flex-1">
                  {filteredPeople.slice(0, 50).map(person => (
                    <button
                      key={person.id}
                      onClick={() => {
                        if (selectedPersonId1 === person.id) {
                          setSelectedPersonId1(null);
                        } else if (selectedPersonId2 === person.id) {
                          setSelectedPersonId2(null);
                        } else if (!selectedPersonId1) {
                          setSelectedPersonId1(person.id);
                        } else if (!selectedPersonId2) {
                          setSelectedPersonId2(person.id);
                        }
                      }}
                      className={`w-full text-left p-1.5 rounded text-xs transition-all ${
                        selectedPersonId1 === person.id
                          ? 'bg-green-100 border border-green-500'
                          : selectedPersonId2 === person.id
                          ? 'bg-brand-100 border border-brand-500'
                          : 'bg-white border border-brand-200 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="truncate flex-1">
                          <div className="font-medium text-slate-900 truncate">
                            {person.last_name || ''} {person.first_name || person.email}
                          </div>
                        </div>
                        {selectedPersonId1 === person.id && (
                          <span className="text-xs bg-green-500 text-white px-1 rounded ml-1">1</span>
                        )}
                        {selectedPersonId2 === person.id && (
                          <span className="text-xs bg-brand-500 text-white px-1 rounded ml-1">2</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Persons - fixed at bottom */}
              {(selectedPerson1 || selectedPerson2) && (
                <div className="mt-auto pt-2 space-y-1 flex-shrink-0 border-t border-brand-200">
                  {selectedPerson1 && (
                    <div className="p-1.5 bg-green-50 rounded border border-green-200 flex items-center justify-between">
                      <div className="truncate flex-1">
                        <div className="text-xs text-green-600 font-medium">Person 1</div>
                        <div className="font-medium text-green-800 text-xs truncate">
                          {selectedPerson1.last_name || ''} {selectedPerson1.first_name}
                        </div>
                      </div>
                      <button onClick={() => setSelectedPersonId1(null)} className="text-green-600 hover:text-green-800 ml-1">×</button>
                    </div>
                  )}
                  {selectedPerson2 && (
                    <div className="p-1.5 bg-brand-50 rounded border border-brand-200 flex items-center justify-between">
                      <div className="truncate flex-1">
                        <div className="text-xs text-brand-600 font-medium">Person 2</div>
                        <div className="font-medium text-brand-800 text-xs truncate">
                          {selectedPerson2.last_name || ''} {selectedPerson2.first_name}
                        </div>
                      </div>
                      <button onClick={() => setSelectedPersonId2(null)} className="text-brand-600 hover:text-brand-800 ml-1">×</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Middle Column - Car Selection */}
          <div className="w-1/5 border-r border-brand-200 bg-white overflow-y-auto">
            <div className="p-3">
              <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center">
                <Truck className="w-3 h-3 mr-1 text-teal-600" />
                Select Car
              </h4>
              {cars.length === 0 ? (
                <div className="p-3 text-center text-slate-500 bg-steel-50 rounded-xl border border-brand-200">
                  <Truck className="w-6 h-6 mx-auto text-steel-300 mb-1" />
                  <p className="text-xs">No cars available</p>
                </div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {cars.map(carOption => (
                    <button
                      key={carOption.id}
                      onClick={() => setSelectedCarId(carOption.id)}
                      className={`w-full text-left p-2 rounded transition-all ${
                        selectedCarId === carOption.id
                          ? 'bg-teal-100 border-2 border-teal-500'
                          : 'bg-steel-50 border border-brand-200 hover:border-teal-300'
                      }`}
                    >
                      <div className="font-medium text-slate-900 text-sm">
                        {carOption.car_brand} {carOption.car_model}
                      </div>
                      <div className="text-xs text-slate-500">
                        {carOption.plate_number}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Car Info */}
              {currentCar && (
                <div className="mt-3 p-2 bg-teal-50 rounded border border-teal-200">
                  <div className="text-xs text-teal-600 font-medium uppercase">Selected</div>
                  <div className="font-semibold text-teal-800 text-sm">
                    {currentCar.car_brand} {currentCar.car_model}
                  </div>
                  <div className="text-xs text-teal-600">{currentCar.plate_number}</div>
                </div>
              )}

              {/* Notes */}
              <div className="mt-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-2 text-xs border border-brand-200 rounded-xl focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                  rows="2"
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          </div>

          {/* Right Column - Items Selection (largest) */}
          <div className="w-3/5 flex flex-col">
            <div className="p-4 flex flex-col h-full">
              {/* Show all items to assign (pre-selected + additional) */}
              {allItemsToAssign.length > 0 && (
                <div className="mb-4 flex-1" style={{maxHeight: '75%'}}>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center">
                    <Package className="w-4 h-4 mr-2 text-brand-600" />
                    Items to Assign ({allItemsToAssign.length})
                  </h4>
                  <div className="border border-brand-200 rounded-xl overflow-hidden bg-white overflow-y-auto" style={{maxHeight: 'calc(100% - 30px)'}}>
                    {allItemsToAssign.map((item, index) => {
                      const isAdditional = additionalItems.find(ai => ai.id === item.id);
                      return (
                        <div key={item.id} className={`p-3 flex items-center ${index !== allItemsToAssign.length - 1 ? 'border-b border-steel-100' : ''}`}>
                          {/* Item Image */}
                          <div className="w-12 h-12 flex-shrink-0 mr-3">
                            {item.item_picture ? (
                              <img
                                src={item.item_picture}
                                alt={item.name}
                                className="w-12 h-12 object-cover rounded-xl border border-brand-200"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-steel-100 rounded-xl border border-brand-200 flex items-center justify-center">
                                <Package className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 flex items-center">
                              <span className="truncate">{item.name}</span>
                              {isAdditional && (
                                <span className="ml-2 text-xs bg-brand-100 text-brand-600 px-1.5 py-0.5 rounded flex-shrink-0">added</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              SKU: {item.sku || 'N/A'} | Category: {item.category || 'N/A'}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 ml-3 flex-shrink-0">
                            <div className="text-right">
                              <div className="text-sm font-medium text-slate-700">
                                {item.quantity} {item.unit}
                              </div>
                              <div className="text-xs text-slate-500">in stock</div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <label className="text-xs text-slate-500">Qty:</label>
                              <input
                                type="number"
                                min="1"
                                max={item.quantity}
                                value={itemQuantities[item.id] || 1}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  updateItemQuantity(item.id, Math.min(Math.max(1, val), item.quantity));
                                }}
                                className="w-16 px-2 py-1 text-sm border border-brand-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center"
                              />
                            </div>
                            {isAdditional && (
                              <button
                                onClick={() => removeAdditionalItem(item.id)}
                                className="text-red-500 hover:text-red-700 text-lg"
                              >×</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add more items section - using same component as Assign to Person */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center">
                  <Plus className="w-4 h-4 mr-2 text-brand-600" />
                  Add More Items
                </h4>
                <AddItemsToAssignmentSection
                  currentItems={allItemsToAssign.map(item => ({ itemId: item.id }))}
                  allInventoryItems={allInventoryItems.length > 0 ? allInventoryItems : items}
                  loadingAllItems={loadingAllItems}
                  ensureAllItemsLoaded={ensureAllItemsLoaded}
                  onAddItems={(newItems) => {
                    newItems.forEach(newItem => {
                      const fullItem = (allInventoryItems.length > 0 ? allInventoryItems : items).find(i => i.id === newItem.itemId);
                      if (fullItem && !additionalItems.find(ai => ai.id === fullItem.id)) {
                        setAdditionalItems(prev => [...prev, fullItem]);
                        setItemQuantities(prev => ({
                          ...prev,
                          [fullItem.id]: 1
                        }));
                      }
                    });
                  }}
                />
              </div>

              {/* Single item selection (when no bulk items) - kept for backward compatibility */}
              {allItemsToAssign.length === 0 && (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center">
                      <Package className="w-4 h-4 mr-2 text-brand-600" />
                      Select Item
                    </h4>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="Search by name, SKU, or category..."
                      />
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="mb-4">
                    <div className="border border-brand-200 rounded-xl overflow-hidden bg-white max-h-64 overflow-y-auto">
                      {filteredItems.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">
                          <Package className="w-10 h-10 mx-auto text-steel-300 mb-2" />
                          <p>No items found</p>
                        </div>
                      ) : (
                        filteredItems.map((item, index) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              setSelectedItem(item);
                              setQuantity(1);
                            }}
                            className={`w-full text-left p-3 transition-colors ${
                              index !== filteredItems.length - 1 ? 'border-b border-steel-100' : ''
                            } ${
                              selectedItem?.id === item.id
                                ? 'bg-teal-50 border-l-4 border-l-teal-500'
                                : 'hover:bg-steel-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-slate-900">{item.name}</div>
                                <div className="text-xs text-slate-500">
                                  SKU: {item.sku || 'N/A'} | Category: {item.category || 'N/A'}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium text-slate-700">
                                  {item.quantity} {item.unit}
                                </div>
                                <div className="text-xs text-slate-500">available</div>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Selected Item & Quantity */}
                  {selectedItem && (
                    <div className="p-4 bg-brand-50 rounded-xl border border-brand-200 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-xs text-brand-600 font-medium uppercase">Selected Item</div>
                          <div className="font-semibold text-brand-800">{selectedItem.name}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-brand-600">Available: {selectedItem.quantity} {selectedItem.unit}</div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-700 mb-1">
                          Quantity to Assign
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={selectedItem.quantity}
                          value={quantity}
                          onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                          className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-brand-200 bg-steel-50 flex justify-between items-center">
          <div className="text-sm text-slate-500">
            {currentCar && allItemsToAssign.length > 0 && (
              <span className="text-teal-600 font-medium">
                Ready to assign {allItemsToAssign.length} item(s) to {currentCar.car_brand} {currentCar.car_model}
                {selectedPerson1 && ` (${selectedPerson1.last_name || selectedPerson1.first_name})`}
                {selectedPerson2 && ` & ${selectedPerson2.last_name || selectedPerson2.first_name}`}
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-brand-200 rounded-xl text-slate-700 hover:bg-steel-100"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={allItemsToAssign.length === 0 || !selectedCarId || saving}
              className={`px-6 py-2 rounded-xl text-white flex items-center ${
                (allItemsToAssign.length === 0 || !selectedCarId || saving)
                  ? 'bg-steel-400 cursor-not-allowed'
                  : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Assigning...
                </>
              ) : (
                <>
                  <Truck className="w-4 h-4 mr-2" />
                  Assign to Car
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Assignment Modal Component
const AssignmentModal = ({ selectedItems, assignmentType, customFields, allInventoryItems, loadingAllItems, ensureAllItemsLoaded, onClose, onSave }) => {

  const [assignmentData, setAssignmentData] = useState(() => {
    const initialData = {
      assignmentType,
      selectedPerson: '',
      selectedPersonName: '',
      project: '',
      selectedVehicle: '',
      selectedDriver: '',
      selectedShipment: '',
      notes: '',
      items: selectedItems.map(item => {
        const itemData = {
          itemId: item.type === 'warehouse_individual' ? item.originalId : item.id, // Use originalId for individual items
          itemName: item.name,
          itemSku: item.sku,
          availableQuantity: item.quantity,
          quantityToAssign: 1,
          customFieldValues: item.customFieldValues || {}, // Pre-fill with existing values if available
          individualItems: item.type === 'warehouse_individual' ?
            [{
              customFieldValues: item.customFieldValues || {},
              individualId: item.individualId // Add individual ID to identify which specific individual item
            }] :
            []
        };
        return itemData;
      })
    };
    return initialData;
  });

  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [showPersonDropdown, setShowPersonDropdown] = useState(false);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [showIndividualItemsModal, setShowIndividualItemsModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [shipmentForm, setShipmentForm] = useState({
    name: '',
    price: ''
  });
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState([]);

  useEffect(() => {
    if (assignmentType === 'project') {
      fetchProjects();
      fetchVehicles();
      fetchPeople(); // Need people for driver selection
      fetchShipments();
    }
    if (assignmentType === 'person') {
      fetchProjects(); // Load projects (needed for backend workaround)
      fetchPeople();
    }
  }, [assignmentType]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const projectsData = Array.isArray(response.data) ? response.data :
                          response.data.results ? response.data.results : [];

      // Don't filter projects here - keep all so existing assignments can show project names
      // Filter will be applied in dropdowns for new assignments
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchPeople = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const peopleData = response.data.results || response.data || [];
      setPeople(Array.isArray(peopleData) ? peopleData : []);
    } catch (error) {
      console.error('Error fetching people:', error);
      setPeople([]);
    }
  };

  const fetchVehicles = async () => {
    try {
      // Use the available vehicles endpoint like in WarehouseIssue
      const response = await axios.get(`${API_BASE_URL}/api/vehicles/available/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const vehiclesData = response.data || [];
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
    } catch (error) {
      console.error('Error fetching available vehicles:', error);
      // Fallback to cars endpoint if available endpoint fails
      try {
        const fallbackResponse = await axios.get(`${API_BASE_URL}/api/cars/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const fallbackData = fallbackResponse.data.results || fallbackResponse.data || [];
        setVehicles(Array.isArray(fallbackData) ? fallbackData : []);
      } catch (fallbackError) {
        console.error('Error fetching cars as fallback:', fallbackError);
        setVehicles([]);
      }
    }
  };

  const fetchShipments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/shipments/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const shipmentsData = Array.isArray(response.data) ? response.data : 
                           response.data.results ? response.data.results : [];
      setShipments(shipmentsData.map(shipment => ({
        id: shipment.id,
        name: shipment.name,
        price: parseFloat(shipment.price || 0),
        project: shipment.project,
        projectName: shipment.project_name
      })));
    } catch (error) {
      console.error('Error fetching shipments:', error);
      setShipments([]);
    }
  };

  const handleShipmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        name: shipmentForm.name,
        price: parseFloat(shipmentForm.price)
      };
      
      await axios.post(`${API_BASE_URL}/api/warehouse/shipments/`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      setShowShipmentModal(false);
      setShipmentForm({ name: '', price: '' });
      fetchShipments(); // Refresh shipments list
      alert('Shipment created successfully!');
    } catch (error) {
      console.error('Error creating shipment:', error);
      alert('Error creating shipment. Please try again.');
    }
  };

  const handleItemQuantityChange = (itemIndex, quantity) => {
    const newQuantity = parseInt(quantity) || 1;

    setAssignmentData(prev => ({
      ...prev,
      items: prev.items.map((item, index) => {
        if (index === itemIndex) {
          // Create individual custom field values for each quantity
          const individualItems = [];
          for (let i = 0; i < newQuantity; i++) {
            // Preserve existing values if they exist, otherwise create new empty object
            individualItems.push(
              item.individualItems && item.individualItems[i]
                ? item.individualItems[i]
                : { customFieldValues: {} }
            );
          }

          return {
            ...item,
            quantityToAssign: newQuantity,
            individualItems: individualItems
          };
        }
        return item;
      })
    }));
  };

  const handleIndividualItemsSetup = (itemIndex) => {
    setCurrentItemIndex(itemIndex);
    setShowIndividualItemsModal(true);
  };

  const generateOptimalRoute = () => {
    // Filter items that have quantity to assign > 0
    const itemsToAssign = assignmentData.items.filter(item => item.quantityToAssign > 0);
    
    if (itemsToAssign.length === 0) {
      alert('Add items with quantity to assign first to generate route');
      return;
    }

    // Find the corresponding inventory items to get location data
    const locations = itemsToAssign.map(assignItem => {
      const inventoryItem = selectedItems.find(invItem => invItem.id === assignItem.itemId);
      return {
        location: inventoryItem?.location || 'Unknown',
        items: [{ 
          name: assignItem.itemName, 
          quantity: assignItem.quantityToAssign, 
          unit: inventoryItem?.unit || 'pcs' 
        }]
      };
    });

    // Group items by location
    const locationGroups = {};
    locations.forEach(loc => {
      if (!locationGroups[loc.location]) {
        locationGroups[loc.location] = [];
      }
      locationGroups[loc.location].push(...loc.items);
    });

    // Convert to array and sort locations optimally
    const sortedLocations = Object.entries(locationGroups).map(([location, items]) => ({
      location,
      items,
      sortKey: location // Simple alphabetical sort for now
    })).sort((a, b) => {
      // Advanced sorting logic: Rack -> Shelf -> Container
      const parseLocation = (loc) => {
        const parts = loc.split('-');
        return {
          rack: parts[0] || '',
          shelf: parseInt(parts[1]) || 0,
          container: parts[2] || ''
        };
      };

      const locA = parseLocation(a.location);
      const locB = parseLocation(b.location);

      // First by rack
      if (locA.rack !== locB.rack) {
        return (locA.rack || '').localeCompare(locB.rack || '');
      }
      // Then by shelf
      if (locA.shelf !== locB.shelf) {
        return locA.shelf - locB.shelf;
      }
      // Finally by container
      return (locA.container || '').localeCompare(locB.container || '');
    });

    setOptimizedRoute(sortedLocations);
    setShowRouteModal(true);
  };

  const generateAssignmentPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text(`WAREHOUSE ${assignmentType === 'project' ? 'PROJECT' : 'PERSONAL'} ASSIGNMENT`, 20, 20);
      
      doc.setFontSize(12);
      doc.text(`Assignment Date: ${new Date().toLocaleDateString()}`, 20, 35);
      
      // Assignment details
      if (assignmentType === 'project') {
        const project = projects.find(p => p.id === parseInt(assignmentData.project));
        doc.text(`Project: ${project?.name || 'N/A'} (${project?.project_number || 'N/A'})`, 20, 45);
        
        if (assignmentData.selectedVehicle) {
          const vehicle = vehicles.find(v => v.id === parseInt(assignmentData.selectedVehicle));
          doc.text(`Vehicle: ${vehicle?.car_brand} ${vehicle?.car_model} (${vehicle?.plate_number})`, 20, 55);
        }
        
        if (assignmentData.selectedDriver) {
          const driver = people.find(p => p.id === parseInt(assignmentData.selectedDriver));
          doc.text(`Driver: ${driver?.first_name} ${driver?.last_name}`, 20, 65);
        }
        
        if (assignmentData.selectedShipment) {
          const shipment = shipments.find(s => s.id === parseInt(assignmentData.selectedShipment));
          doc.text(`Shipment: ${shipment?.name} (${formatPrice(shipment?.price || 0)})`, 20, 75);
        }
      } else {
        const person = people.find(p => p.id === parseInt(assignmentData.selectedPerson));
        doc.text(`Assigned to: ${person?.first_name} ${person?.last_name} (${person?.email})`, 20, 45);
      }
      
      
      if (assignmentData.notes) {
        doc.text(`Notes: ${assignmentData.notes}`, 20, assignmentType === 'project' ? 95 : 65);
      }
      
      // Items table
      const tableStartY = assignmentType === 'project' ? 110 : 80;
      doc.text('ASSIGNED ITEMS:', 20, tableStartY);
      
      // Table headers
      doc.text('Item Name', 20, tableStartY + 10);
      doc.text('SKU', 80, tableStartY + 10);
      doc.text('Quantity', 120, tableStartY + 10);
      doc.text('Available', 160, tableStartY + 10);
      
      // Draw line under header
      doc.line(20, tableStartY + 12, 200, tableStartY + 12);
      
      // Table content
      let yPos = tableStartY + 20;
      assignmentData.items.forEach(item => {
        if (item.quantityToAssign > 0) {
          doc.text(item.itemName || 'N/A', 20, yPos);
          doc.text(item.itemSku || 'N/A', 80, yPos);
          doc.text(item.quantityToAssign.toString(), 120, yPos);
          doc.text(`${item.availableQuantity} units`, 160, yPos);
          yPos += 10;
        }
      });
      
      // Total
      doc.line(20, yPos, 200, yPos);
      yPos += 10;
      const totalItems = assignmentData.items.reduce((sum, item) => sum + item.quantityToAssign, 0);
      doc.text(`Total Items: ${totalItems} units`, 20, yPos);
      
      // Footer
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, yPos + 20);
      doc.text(`Assignment ID: ASSIGN-${Date.now()}`, 20, yPos + 30);
      
      // Save PDF
      const filename = assignmentType === 'project' 
        ? `project-assignment-${Date.now()}.pdf`
        : `personal-assignment-${Date.now()}.pdf`;
      doc.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (assignmentType === 'project' && !assignmentData.project) {
      alert('Please select a project');
      return;
    }
    
    if (assignmentType === 'person' && !assignmentData.selectedPerson) {
      alert('Please select a person');
      return;
    }
    
    // Validate driver is selected when vehicle is selected
    if (assignmentData.selectedVehicle && !assignmentData.selectedDriver) {
      alert('Please select a driver when assigning a vehicle.');
      return;
    }

    // Check if any item quantity exceeds available
    for (const item of assignmentData.items) {
      if (item.quantityToAssign > item.availableQuantity) {
        alert(`Quantity for ${item.itemName} exceeds available stock (${item.availableQuantity})`);
        return;
      }
    }

    // Validate required custom fields (using item-specific private fields)
    for (let i = 0; i < assignmentData.items.length; i++) {
      const item = assignmentData.items[i];
      const actualItem = allInventoryItems.find(ai => {
        if (ai.type === 'warehouse_individual') {
          // Match by originalId for individual items
          return ai.originalId === item.itemId;
        } else {
          // Match by full ID for regular items
          return ai.id === item.itemId;
        }
      });
      const itemCustomFields = actualItem?.customFields || [];
      
      for (const fieldData of itemCustomFields) {
        const field = fieldData.field_definition || fieldData;
        if (field.is_required) {
          const value = item.customFieldValues?.[field.name] || item.customFieldValues?.[field.id];
          if (!value || value === '') {
            // Extract display name from internal name
            const getDisplayName = (internalName) => {
              if (!internalName) return '';
              const match = internalName.match(/^(.+?)_item_\d+_[a-z0-9]+$/);
              return match ? match[1] : internalName;
            };
            const displayName = getDisplayName(field.name);
            alert(`Please fill in the required field "${displayName}" for ${item.itemName}`);
            return;
          }
        }
      }
    }

    onSave(assignmentData);
  };

  // Check if any selected item has custom fields
  const itemsWithCustomFields = selectedItems.filter(item => item.customFields && item.customFields.length > 0);
  const hasItemsWithCustomFields = itemsWithCustomFields.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className={`relative top-10 mx-auto p-5 border shadow-lg rounded-2xl bg-white max-h-[90vh] overflow-y-auto ${hasItemsWithCustomFields ? 'w-[98vw] max-w-none' : 'w-[95vw] max-w-7xl'}`}>
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              {assignmentType === 'person' ? 'Assign to Person' : 'Assign to Project'}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={`grid grid-cols-1 gap-6 ${hasItemsWithCustomFields ? 'lg:grid-cols-[30%_70%]' : 'lg:grid-cols-[35%_65%]'}`}>
              {/* Left Side - Assignment Details */}
              <div className="space-y-6">
                <div className="border border-brand-200 rounded-xl p-4">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Assignment Details</h4>
                  
                  <div className="space-y-4">
                {assignmentType === 'project' ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                      <select
                        value={assignmentData.project}
                        onChange={(e) => setAssignmentData(prev => ({...prev, project: e.target.value}))}
                        className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select a project</option>
                        {projects
                          .filter(project => project.is_active !== false)
                          .map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name} ({project.project_number || `PRJ-${project.id}`})
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Responsible Person Assignment */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                        <User className="h-4 w-4 mr-2" />
                        Responsible Person
                      </label>
                      <select
                        value={assignmentData.selectedPerson}
                        onChange={(e) => {
                          const personId = e.target.value;
                          const selectedPersonObj = people.find(p => p.id === parseInt(personId));
                          setAssignmentData(prev => ({
                            ...prev,
                            selectedPerson: personId,
                            selectedPersonName: selectedPersonObj
                              ? `${selectedPersonObj.first_name || ''} ${selectedPersonObj.last_name || ''}`.trim()
                              : ''
                          }));
                        }}
                        className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        <option value="">Select responsible person</option>
                        {people.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.first_name || ''} {person.last_name || ''} {person.email ? `(${person.email})` : ''}
                          </option>
                        ))}
                      </select>
                      {assignmentData.selectedPerson && (
                        <div className="mt-2 p-3 bg-green-50 rounded-xl">
                          <p className="text-sm text-green-800">
                            <User className="h-4 w-4 inline mr-1" />
                            Responsible: {people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.first_name} {people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.last_name}
                            {people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.email && (
                              <> ({people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.email})</>
                            )}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Vehicle Assignment */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                        <Truck className="h-4 w-4 mr-2" />
                        Assign to Vehicle (Optional)
                      </label>
                      <select
                        value={assignmentData.selectedVehicle}
                        onChange={(e) => setAssignmentData(prev => ({...prev, selectedVehicle: e.target.value}))}
                        className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        <option value="">No vehicle selected</option>
                        {vehicles.map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.car_brand} {vehicle.car_model} ({vehicle.plate_number})
                          </option>
                        ))}
                      </select>
                      {assignmentData.selectedVehicle && (
                        <div className="mt-2 p-3 bg-brand-50 rounded-xl">
                          <p className="text-sm text-brand-800">
                            <Truck className="h-4 w-4 inline mr-1" />
                            Vehicle: {vehicles.find(v => v.id === parseInt(assignmentData.selectedVehicle))?.car_brand} {vehicles.find(v => v.id === parseInt(assignmentData.selectedVehicle))?.car_model} 
                            ({vehicles.find(v => v.id === parseInt(assignmentData.selectedVehicle))?.plate_number})
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Driver Assignment */}
                    {assignmentData.selectedVehicle && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          Assign Driver (Required for Vehicle)
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Type to search or select a driver..."
                            value={assignmentData.selectedDriverName || ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              setAssignmentData(prev => ({
                                ...prev,
                                selectedDriverName: inputValue,
                                selectedDriver: '' // Clear selection when typing
                              }));
                              setShowDriverDropdown(true);
                            }}
                            onFocus={() => setShowDriverDropdown(true)}
                            onBlur={() => setTimeout(() => setShowDriverDropdown(false), 150)}
                            className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            required={!!assignmentData.selectedVehicle}
                          />
                          {showDriverDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-brand-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                              {people
                                .filter(person => {
                                  if (!assignmentData.selectedDriverName) return true;
                                  const searchLower = assignmentData.selectedDriverName.toLowerCase();
                                  const fullName = `${person.first_name || ''} ${person.last_name || ''}`.toLowerCase();
                                  const email = person.email?.toLowerCase() || '';
                                  return fullName.includes(searchLower) || email.includes(searchLower);
                                })
                                .map(person => {
                                  const displayText = `${person.first_name || ''} ${person.last_name || ''}${person.email ? ` (${person.email})` : ''}`.trim();
                                  return (
                                    <div
                                      key={person.id}
                                      className="p-3 hover:bg-steel-100 cursor-pointer border-b border-steel-100 last:border-b-0"
                                      onClick={() => {
                                        setAssignmentData(prev => ({
                                          ...prev,
                                          selectedDriver: person.id.toString(),
                                          selectedDriverName: `${person.first_name} ${person.last_name}`
                                        }));
                                        setShowDriverDropdown(false);
                                      }}
                                    >
                                      {displayText}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                        {assignmentData.selectedDriver && (
                          <div className="mt-2 p-3 bg-green-50 rounded-xl">
                            <p className="text-sm text-green-800">
                              <User className="h-4 w-4 inline mr-1" />
                              Driver: {people.find(p => p.id === parseInt(assignmentData.selectedDriver))?.first_name} {people.find(p => p.id === parseInt(assignmentData.selectedDriver))?.last_name}
                              ({people.find(p => p.id === parseInt(assignmentData.selectedDriver))?.email})
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Shipment Selection */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-slate-700">Select Shipment (Optional)</label>
                        <button
                          type="button"
                          onClick={() => setShowShipmentModal(true)}
                          className="flex items-center px-3 py-1 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors text-sm"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Shipment
                        </button>
                      </div>
                      <select
                        value={assignmentData.selectedShipment}
                        onChange={(e) => setAssignmentData(prev => ({...prev, selectedShipment: e.target.value}))}
                        className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        <option value="">No shipment selected</option>
                        {shipments
                          .filter(shipment => !assignmentData.project || !shipment.project || shipment.project === parseInt(assignmentData.project))
                          .map(shipment => (
                            <option key={shipment.id} value={shipment.id}>
                              {shipment.name} - {formatPrice(shipment.price || 0)}
                              {shipment.projectName && ` (${shipment.projectName})`}
                            </option>
                          ))}
                      </select>
                      {assignmentData.selectedShipment && (
                        <div className="mt-2 p-3 bg-indigo-50 rounded-xl">
                          <p className="text-sm text-indigo-800">
                            Shipment: {shipments.find(s => s.id === parseInt(assignmentData.selectedShipment))?.name} - 
                            {formatPrice(shipments.find(s => s.id === parseInt(assignmentData.selectedShipment))?.price || 0)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Select Person *</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Type to search or select a person..."
                        value={assignmentData.selectedPersonName || ''}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setAssignmentData(prev => ({
                            ...prev,
                            selectedPersonName: inputValue,
                            selectedPerson: '' // Clear selection when typing
                          }));
                          setShowPersonDropdown(true);
                        }}
                        onFocus={() => setShowPersonDropdown(true)}
                        onBlur={() => setTimeout(() => setShowPersonDropdown(false), 150)}
                        className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        required
                      />
                      {showPersonDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-brand-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {people
                            .filter(person => {
                              if (!assignmentData.selectedPersonName) return true;
                              const searchLower = assignmentData.selectedPersonName.toLowerCase();
                              const fullName = `${person.first_name || ''} ${person.last_name || ''}`.toLowerCase();
                              const email = person.email?.toLowerCase() || '';
                              return fullName.includes(searchLower) || email.includes(searchLower);
                            })
                            .map(person => {
                              const displayText = `${person.first_name || ''} ${person.last_name || ''}${person.email ? ` (${person.email})` : ''}`.trim();
                              return (
                                <div
                                  key={person.id}
                                  className="p-3 hover:bg-steel-100 cursor-pointer border-b border-steel-100 last:border-b-0"
                                  onClick={() => {
                                    setAssignmentData(prev => ({
                                      ...prev,
                                      selectedPerson: person.id.toString(),
                                      selectedPersonName: `${person.first_name || ''} ${person.last_name || ''}`.trim()
                                    }));
                                    setShowPersonDropdown(false);
                                  }}
                                >
                                  {displayText}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                    {assignmentData.selectedPerson && (
                      <div className="mt-2 p-3 bg-green-50 rounded-xl">
                        <p className="text-sm text-green-800">
                          <User className="h-4 w-4 inline mr-1" />
                          Selected: {people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.first_name} {people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.last_name}
                          ({people.find(p => p.id === parseInt(assignmentData.selectedPerson))?.email})
                        </p>
                      </div>
                    )}
                  </div>
                )}

              </div>


              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Additional Notes</label>
                <textarea
                  rows="2"
                  value={assignmentData.notes}
                  onChange={(e) => setAssignmentData(prev => ({...prev, notes: e.target.value}))}
                  className="w-full p-3 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Any additional notes or instructions..."
                />
              </div>
                </div>
              </div>
              
              {/* Right Side - Items to Assign */}
              <div className="space-y-6">
                <div className="border border-brand-200 rounded-xl p-4">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Items to Assign ({assignmentData.items.length})</h4>

                  {/* Items Table */}
                  <div className="overflow-x-auto max-w-full">
                    <table className="w-full divide-y divide-brand-100">
                      <thead className="bg-steel-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider whitespace-nowrap min-w-[200px]">
                            Item Name
                          </th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-slate-900 uppercase tracking-wider whitespace-nowrap w-24">
                            Available
                          </th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-slate-900 uppercase tracking-wider whitespace-nowrap w-32">
                            Quantity to Assign
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider whitespace-nowrap min-w-[250px]">
                            Additional Fields
                          </th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-slate-900 uppercase tracking-wider whitespace-nowrap w-24">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-brand-100">
                        {assignmentData.items.map((item, index) => {
                          // For individual items, we need to match by the full individual ID, not just originalId
                          // Use allInventoryItems instead of selectedItems to find the actual item data
                          const actualItem = allInventoryItems.find(ai => {
                            if (ai.type === 'warehouse_individual') {
                              // Match by originalId for individual items
                              return ai.originalId === item.itemId;
                            } else {
                              // Match by full ID for regular items
                              return ai.id === item.itemId;
                            }
                          });
                          
                          // Use item-specific custom fields (private fields saved with this item)
                          // Try both customFields and custom_fields properties
                          const itemCustomFields = actualItem?.customFields || actualItem?.custom_fields || [];
                          const hasCustomFields = itemCustomFields.length > 0;
                          
                          // Debug: Log custom fields

                          // Helper function to get custom field value (try field.name first, fallback to field.id)
                          const getCustomFieldValue = (field) => {
                            return item.customFieldValues?.[field.name] || item.customFieldValues?.[field.id] || '';
                          };

                          return (
                            <tr key={`item-${actualItem?.id || item.itemId}-${index}`} className="hover:bg-steel-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-slate-900">{item.itemName}</div>
                                  <div className="text-xs text-slate-500">SKU: {item.itemSku || 'N/A'}</div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm text-slate-900">{item.availableQuantity}</span>
                                <span className="text-xs text-slate-500 ml-1">units</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="number"
                                  min="1"
                                  max={item.availableQuantity}
                                  value={item.quantityToAssign}
                                  onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                                  className="w-20 p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm text-center"
                                />
                              </td>
                              <td className="px-4 py-3">
                                {hasCustomFields ? (
                                  <div className="space-y-2">
                                    <div className="text-xs font-medium text-slate-700 mb-2">
                                      Fields for {item.itemName}:
                                    </div>
                                    {/* Generate fields for each quantity */}
                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                      {Array.from({ length: item.quantityToAssign }, (_, qtyIndex) => (
                                        <div key={`qty-${qtyIndex}`} className="border border-brand-200 rounded-xl p-3 bg-steel-50">
                                          <div className="text-xs font-semibold text-slate-600 mb-2">
                                            Item #{qtyIndex + 1}
                                          </div>
                                          <div className="grid grid-cols-1 gap-2">
                                            {itemCustomFields.map((fieldData) => {
                                              // Extract field definition from the customField structure
                                              const field = fieldData.field_definition || fieldData;

                                              // Extract display name from internal name (remove _item_timestamp_randomid suffix)
                                              const getDisplayName = (internalName) => {
                                                if (!internalName) return '';
                                                // Match pattern: "FieldName_item_timestamp_randomid"
                                                const match = internalName.match(/^(.+?)_item_\d+_[a-z0-9]+$/);
                                                return match ? match[1] : internalName;
                                              };

                                              const displayName = getDisplayName(field.name);

                                              return (
                                          <div key={`field-${item.itemId}-${field.id}`} className="bg-steel-50 p-2 rounded">
                                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                              {displayName} {field.is_required && <span className="text-red-500">*</span>}
                                            </label>
                                            {field.help_text && (
                                              <p className="text-xs text-slate-500 mb-1">{field.help_text}</p>
                                            )}
                                            
                                            {/* Text field */}
                                            {field.field_type === 'text' && (
                                              <input
                                                type="text"
                                                value={getCustomFieldValue(field)}
                                                onChange={(e) => {
                                                  setAssignmentData(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((itm, idx) => {
                                                      if (idx === index) {
                                                        const updatedIndividualItems = [...(itm.individualItems || [])];
                                                        if (!updatedIndividualItems[qtyIndex]) {
                                                          updatedIndividualItems[qtyIndex] = { customFieldValues: {} };
                                                        }
                                                        updatedIndividualItems[qtyIndex].customFieldValues[field.id] = e.target.value;

                                                        return {
                                                          ...itm,
                                                          individualItems: updatedIndividualItems
                                                        };
                                                      }
                                                      return itm;
                                                    })
                                                  }));
                                                }}
                                                className="w-full px-2 py-1 border border-brand-200 rounded-xl text-xs focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                                placeholder={field.default_value || ''}
                                                required={field.is_required}
                                              />
                                            )}
                                            
                                            {/* Number field */}
                                            {field.field_type === 'number' && (
                                              <input
                                                type="number"
                                                min={field.min_value || undefined}
                                                max={field.max_value || undefined}
                                                value={getCustomFieldValue(field)}
                                                onChange={(e) => {
                                                  setAssignmentData(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((itm, idx) => {
                                                      if (idx === index) {
                                                        const updatedIndividualItems = [...(itm.individualItems || [])];
                                                        if (!updatedIndividualItems[qtyIndex]) {
                                                          updatedIndividualItems[qtyIndex] = { customFieldValues: {} };
                                                        }
                                                        updatedIndividualItems[qtyIndex].customFieldValues[field.id] = e.target.value;

                                                        return {
                                                          ...itm,
                                                          individualItems: updatedIndividualItems
                                                        };
                                                      }
                                                      return itm;
                                                    })
                                                  }));
                                                }}
                                                className="w-full px-2 py-1 border border-brand-200 rounded-xl text-xs focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                                placeholder={field.default_value || ''}
                                                required={field.is_required}
                                              />
                                            )}
                                            
                                            {/* Decimal field */}
                                            {field.field_type === 'decimal' && (
                                              <input
                                                type="number"
                                                step="0.01"
                                                min={field.min_value || undefined}
                                                max={field.max_value || undefined}
                                                value={getCustomFieldValue(field)}
                                                onChange={(e) => {
                                                  setAssignmentData(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((itm, idx) => {
                                                      if (idx === index) {
                                                        const updatedIndividualItems = [...(itm.individualItems || [])];
                                                        if (!updatedIndividualItems[qtyIndex]) {
                                                          updatedIndividualItems[qtyIndex] = { customFieldValues: {} };
                                                        }
                                                        updatedIndividualItems[qtyIndex].customFieldValues[field.id] = e.target.value;

                                                        return {
                                                          ...itm,
                                                          individualItems: updatedIndividualItems
                                                        };
                                                      }
                                                      return itm;
                                                    })
                                                  }));
                                                }}
                                                className="w-full px-2 py-1 border border-brand-200 rounded-xl text-xs focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                                placeholder={field.default_value || ''}
                                                required={field.is_required}
                                              />
                                            )}
                                            
                                            {/* Date field */}
                                            {field.field_type === 'date' && (
                                              <input
                                                type="date"
                                                value={getCustomFieldValue(field)}
                                                onChange={(e) => {
                                                  setAssignmentData(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((itm, idx) => {
                                                      if (idx === index) {
                                                        const updatedIndividualItems = [...(itm.individualItems || [])];
                                                        if (!updatedIndividualItems[qtyIndex]) {
                                                          updatedIndividualItems[qtyIndex] = { customFieldValues: {} };
                                                        }
                                                        updatedIndividualItems[qtyIndex].customFieldValues[field.id] = e.target.value;

                                                        return {
                                                          ...itm,
                                                          individualItems: updatedIndividualItems
                                                        };
                                                      }
                                                      return itm;
                                                    })
                                                  }));
                                                }}
                                                className="w-full px-2 py-1 border border-brand-200 rounded-xl text-xs focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                                required={field.is_required}
                                              />
                                            )}
                                            
                                            {/* Boolean field */}
                                            {field.field_type === 'boolean' && (
                                              <div className="flex items-center pt-1">
                                                <input
                                                  type="checkbox"
                                                  id={`field-${item.itemId}-${field.id}`}
                                                  checked={getCustomFieldValue(field) === true || getCustomFieldValue(field) === 'true'}
                                                  onChange={(e) => {
                                                    setAssignmentData(prev => ({
                                                      ...prev,
                                                      items: prev.items.map((itm, idx) => 
                                                        idx === index 
                                                          ? {
                                                              ...itm,
                                                              customFieldValues: {
                                                                ...itm.customFieldValues,
                                                                [field.id]: e.target.checked
                                                              }
                                                            }
                                                          : itm
                                                      )
                                                    }));
                                                  }}
                                                  className="h-3 w-3 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                                                />
                                                <label htmlFor={`field-${item.itemId}-${field.id}`} className="ml-2 text-xs text-slate-700">
                                                  Yes
                                                </label>
                                              </div>
                                            )}
                                            
                                            {/* Textarea field */}
                                            {field.field_type === 'textarea' && (
                                              <textarea
                                                rows="2"
                                                value={getCustomFieldValue(field)}
                                                onChange={(e) => {
                                                  setAssignmentData(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((itm, idx) => {
                                                      if (idx === index) {
                                                        const updatedIndividualItems = [...(itm.individualItems || [])];
                                                        if (!updatedIndividualItems[qtyIndex]) {
                                                          updatedIndividualItems[qtyIndex] = { customFieldValues: {} };
                                                        }
                                                        updatedIndividualItems[qtyIndex].customFieldValues[field.id] = e.target.value;

                                                        return {
                                                          ...itm,
                                                          individualItems: updatedIndividualItems
                                                        };
                                                      }
                                                      return itm;
                                                    })
                                                  }));
                                                }}
                                                className="w-full px-2 py-1 border border-brand-200 rounded-xl text-xs focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                                placeholder={field.default_value || ''}
                                                required={field.is_required}
                                              />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                                ) : (
                                  <span className="text-xs text-slate-400">No additional fields</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAssignmentData(prev => ({
                                      ...prev,
                                      items: prev.items.filter((_, i) => i !== index)
                                    }));
                                  }}
                                  className="text-red-600 hover:text-red-900 text-xs"
                                  title="Remove item from assignment"
                                >
                                  ✕ Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-steel-50">
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">
                            Total Items
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-slate-600">
                            {assignmentData.items.reduce((sum, item) => sum + item.availableQuantity, 0)} units
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-medium text-slate-900">
                            {assignmentData.items.reduce((sum, item) => sum + item.quantityToAssign, 0)} units
                          </td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  {/* Add Items Section */}
                  <div className="mt-4 p-3 border-t border-brand-200">
                    <h5 className="text-sm font-medium text-slate-700 mb-2">Add More Items</h5>
                    <AddItemsToAssignmentSection
                      currentItems={assignmentData.items}
                      allInventoryItems={allInventoryItems}
                      loadingAllItems={loadingAllItems}
                      ensureAllItemsLoaded={ensureAllItemsLoaded}
                      onAddItems={(newItems) => {
                        setAssignmentData(prev => ({
                          ...prev,
                          items: [...prev.items, ...newItems]
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={generateOptimalRoute}
                  disabled={assignmentData.items.filter(item => item.quantityToAssign > 0).length === 0}
                  className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:bg-steel-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Route className="h-4 w-4 mr-2" />
                  Generate Collection Route
                </button>
                <button
                  type="button"
                  onClick={generateAssignmentPDF}
                  className="flex items-center px-4 py-2 text-slate-700 bg-white border border-brand-200 rounded-xl hover:bg-steel-50 transition-colors"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Generate PDF
                </button>
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors"
                >
                  Create Assignment
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Shipment Modal */}
      {showShipmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Create New Shipment</h3>
              <form onSubmit={handleShipmentSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Shipment Name
                  </label>
                  <input
                    type="text"
                    value={shipmentForm.name}
                    onChange={(e) => setShipmentForm({...shipmentForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="e.g., DHL, UPS, FedEx, Local Transport"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Price ({getCurrencySymbol()})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={shipmentForm.price}
                    onChange={(e) => setShipmentForm({...shipmentForm, price: e.target.value})}
                    className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-brand-600 text-white py-2 px-4 rounded-xl hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 shadow-lg shadow-brand-500/20"
                  >
                    Create Shipment
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowShipmentModal(false)}
                    className="flex-1 bg-steel-300 text-slate-700 py-2 px-4 rounded-xl hover:bg-steel-400 focus:outline-none focus:ring-2 focus:ring-steel-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Individual Items Setup Modal */}
      {showIndividualItemsModal && currentItemIndex !== null && (
        <IndividualItemsModal
          item={assignmentData.items[currentItemIndex]}
          customFields={selectedItems[currentItemIndex]?.customFields || []}
          onClose={() => {
            setShowIndividualItemsModal(false);
            setCurrentItemIndex(null);
          }}
          onSave={(individualItemsData) => {
            setAssignmentData(prev => ({
              ...prev,
              items: prev.items.map((item, index) => 
                index === currentItemIndex 
                  ? { ...item, individualItems: individualItemsData }
                  : item
              )
            }));
            setShowIndividualItemsModal(false);
            setCurrentItemIndex(null);
          }}
        />
      )}

      {/* Route Modal */}
      {showRouteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-2xl bg-white max-h-[90vh] overflow-hidden">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-900 flex items-center">
                  <Route className="h-6 w-6 mr-2 text-brand-600" />
                  Optimal Collection Route
                </h3>
                <button 
                  onClick={() => setShowRouteModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="mb-4 p-4 bg-brand-50 rounded-xl border border-brand-200">
                <p className="text-sm text-brand-800">
                  This route has been optimized based on warehouse locations to minimize collection time. 
                  Follow this order when collecting items for assignment.
                </p>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  {optimizedRoute.map((stop, index) => (
                    <div key={index} className="bg-steel-50 rounded-xl p-4 border border-brand-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-brand-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900 flex items-center">
                            <MapPin className="h-4 w-4 mr-1 text-brand-600" />
                            Location: {stop.location}
                          </h4>
                          <p className="text-sm text-slate-600">
                            {stop.items.length} item{stop.items.length > 1 ? 's' : ''} to collect
                          </p>
                        </div>
                      </div>
                      
                      <div className="ml-11">
                        <div className="space-y-2">
                          {stop.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex justify-between items-center py-2 px-3 bg-white rounded border">
                              <span className="font-medium text-slate-900">{item.name}</span>
                              <span className="text-sm text-slate-600 bg-steel-100 px-2 py-1 rounded">
                                Qty: {item.quantity} {item.unit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-brand-200">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-slate-600">
                    Total stops: <strong>{optimizedRoute.length}</strong> | 
                    Total items: <strong>{optimizedRoute.reduce((sum, stop) => sum + stop.items.length, 0)}</strong>
                  </div>
                  <button
                    onClick={() => setShowRouteModal(false)}
                    className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors"
                  >
                    Got it!
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Items Modal Component - for setting up custom fields for each individual item
const IndividualItemsModal = ({ item, customFields, onClose, onSave }) => {
  const [individualItems, setIndividualItems] = useState([]);

  useEffect(() => {
    // Initialize individual items based on quantity
    const items = [];
    for (let i = 0; i < item.quantityToAssign; i++) {
      const existingItem = item.individualItems[i] || {};
      items.push({
        id: existingItem.id || `item_${i + 1}`,
        customFieldValues: existingItem.customFieldValues || {}
      });
    }
    setIndividualItems(items);
  }, [item.quantityToAssign, item.individualItems]);

  const handleCustomFieldChange = (itemIndex, fieldId, value) => {
    setIndividualItems(prev => prev.map((individualItem, index) => 
      index === itemIndex 
        ? {
            ...individualItem,
            customFieldValues: {
              ...individualItem.customFieldValues,
              [fieldId]: value
            }
          }
        : individualItem
    ));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    for (let i = 0; i < individualItems.length; i++) {
      for (const field of customFields) {
        if (field.is_required && !individualItems[i].customFieldValues[field.id]) {
          alert(`Item ${i + 1}: Field "${field.name}" is required`);
          return;
        }
      }
    }

    onSave(individualItems);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-2xl bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Setup Individual Items: {item.itemName}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <p className="text-sm text-slate-600 mb-6">
            Configure custom field values for each individual item. This allows you to assign specific serial numbers, 
            conditions, or other details to each unit.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {individualItems.map((individualItem, itemIndex) => (
                <div key={individualItem.id} className="border border-brand-200 rounded-xl p-4 bg-steel-50">
                  <h4 className="font-medium text-slate-900 mb-4">
                    Item #{itemIndex + 1}
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {customFields.map((field) => (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {field.name} {field.is_required && <span className="text-red-500">*</span>}
                        </label>
                        {field.help_text && (
                          <p className="text-xs text-slate-500 mb-1">{field.help_text}</p>
                        )}
                        
                        {field.field_type === 'text' && (
                          <input
                            type="text"
                            value={individualItem.customFieldValues[field.id] || ''}
                            onChange={(e) => handleCustomFieldChange(itemIndex, field.id, e.target.value)}
                            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                            placeholder={field.field_type === 'text' && field.name.toLowerCase().includes('serial') ? 'e.g., SN123456789' : ''}
                          />
                        )}
                        
                        {field.field_type === 'number' && (
                          <input
                            type="number"
                            min={field.min_value || undefined}
                            max={field.max_value || undefined}
                            value={individualItem.customFieldValues[field.id] || ''}
                            onChange={(e) => handleCustomFieldChange(itemIndex, field.id, e.target.value)}
                            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                          />
                        )}
                        
                        {field.field_type === 'decimal' && (
                          <input
                            type="number"
                            step="0.01"
                            min={field.min_value || undefined}
                            max={field.max_value || undefined}
                            value={individualItem.customFieldValues[field.id] || ''}
                            onChange={(e) => handleCustomFieldChange(itemIndex, field.id, e.target.value)}
                            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                          />
                        )}
                        
                        {field.field_type === 'date' && (
                          <input
                            type="date"
                            value={individualItem.customFieldValues[field.id] || ''}
                            onChange={(e) => handleCustomFieldChange(itemIndex, field.id, e.target.value)}
                            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                          />
                        )}
                        
                        {field.field_type === 'boolean' && (
                          <div className="flex items-center mt-2">
                            <input
                              type="checkbox"
                              checked={individualItem.customFieldValues[field.id] === true || individualItem.customFieldValues[field.id] === 'true'}
                              onChange={(e) => handleCustomFieldChange(itemIndex, field.id, e.target.checked)}
                              className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                            />
                            <span className="ml-2 text-sm text-slate-700">Yes</span>
                          </div>
                        )}
                        
                        {field.field_type === 'textarea' && (
                          <textarea
                            rows="3"
                            value={individualItem.customFieldValues[field.id] || ''}
                            onChange={(e) => handleCustomFieldChange(itemIndex, field.id, e.target.value)}
                            className="w-full p-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors"
              >
                Save Details
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Component to add more items to assignment
const AddItemsToAssignmentSection = ({ currentItems, allInventoryItems, loadingAllItems, ensureAllItemsLoaded, onAddItems }) => {
  const [showAddItems, setShowAddItems] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedNewItems, setSelectedNewItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const prepareAvailableItems = React.useCallback(() => {
    console.log('[AddItemsToAssignmentSection] prepareAvailableItems - allInventoryItems count:', allInventoryItems.length);
    // Show all items with quantity > 0, but mark which are already in assignment
    const currentItemIds = currentItems.map(item => item.itemId);
    const available = allInventoryItems
      .filter(item => item.quantity > 0)
      .map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        sku: item.sku,
        category: item.category,
        isAlreadyInAssignment: currentItemIds.includes(item.id)
      }));

    console.log('[AddItemsToAssignmentSection] prepareAvailableItems - available items:', available.length);
    setAvailableItems(available);
  }, [allInventoryItems, currentItems]);

  // Update available items when allInventoryItems changes (as background loading progresses)
  React.useEffect(() => {
    if (showAddItems && allInventoryItems.length > 0) {
      console.log('[AddItemsToAssignmentSection] Updating available items as more load...');
      prepareAvailableItems();
    }
  }, [allInventoryItems, showAddItems, prepareAvailableItems]);

  const handleOpenAddItems = () => {
    console.log('[AddItemsToAssignmentSection] Opening modal immediately with current items...');
    // Open modal immediately with whatever items we have
    prepareAvailableItems();
    setShowAddItems(true);

    // Start loading all items in background if not already loaded
    console.log('[AddItemsToAssignmentSection] Starting background load of all items...');
    ensureAllItemsLoaded();
  };

  // Filter items based on search term
  const filteredItems = availableItems.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(search) ||
      item.sku?.toLowerCase().includes(search) ||
      item.category?.toLowerCase().includes(search)
    );
  });

  const handleAddSelectedItems = () => {
    const newItems = selectedNewItems.map(itemId => {
      const item = availableItems.find(i => i.id === itemId);
      return {
        itemId: item.id,
        itemName: item.name,
        itemSku: item.sku,
        availableQuantity: item.quantity,
        quantityToAssign: 1,
        customFieldValues: {}, // Initialize empty custom field values
        notes: '',
        individualItems: []
      };
    });
    
    onAddItems(newItems);
    setSelectedNewItems([]);
    setShowAddItems(false);
  };

  if (!showAddItems) {
    return (
      <button
        type="button"
        onClick={handleOpenAddItems}
        className="text-sm text-brand-600 hover:text-brand-800 flex items-center"
      >
        + Add more items to this assignment
      </button>
    );
  }

  return (
    <div className="border border-brand-200 rounded-xl p-3 bg-steel-50">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-slate-700">Select items to add:</span>
        <button
          type="button"
          onClick={() => {
            setShowAddItems(false);
            setSearchTerm('');
          }}
          className="text-slate-400 hover:text-slate-600"
        >
          ✕
        </button>
      </div>

      {/* Search Input */}
      <div className="mb-3 relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search by name, SKU, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
        />
      </div>

      {/* Loading indicator */}
      {loadingAllItems && (
        <div className="mb-2 text-xs text-brand-600 flex items-center">
          <span className="animate-spin mr-2">⏳</span>
          Loading more items in background... ({availableItems.length} items shown)
        </div>
      )}

      <div className="max-h-40 overflow-y-auto space-y-2">
        {filteredItems.length > 0 ? (
          filteredItems.map(item => (
            <label key={item.id} className={`flex items-center space-x-2 text-sm p-2 rounded ${item.isAlreadyInAssignment ? 'bg-yellow-50 border border-yellow-200' : 'hover:bg-steel-50'}`}>
              <input
                type="checkbox"
                checked={selectedNewItems.includes(item.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedNewItems(prev => [...prev, item.id]);
                  } else {
                    setSelectedNewItems(prev => prev.filter(id => id !== item.id));
                  }
                }}
                className="rounded border-steel-300"
              />
              <span className="flex-1">
                {item.name}
                <span className="text-slate-500">({item.quantity} available)</span>
                {item.isAlreadyInAssignment && (
                  <span className="ml-2 text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">
                    Already in assignment
                  </span>
                )}
              </span>
            </label>
          ))
        ) : searchTerm ? (
          <div className="text-center py-4 text-slate-500">
            <p className="text-sm">No items found matching "{searchTerm}"</p>
          </div>
        ) : availableItems.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-2">No more items available</p>
        ) : null}
      </div>
      
      <div className="flex justify-end space-x-2 mt-3">
        <button
          type="button"
          onClick={() => setShowAddItems(false)}
          className="px-3 py-1 text-xs bg-steel-200 text-slate-700 rounded-xl hover:bg-steel-300"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleAddSelectedItems}
          disabled={selectedNewItems.length === 0}
          className="px-3 py-1 text-xs bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 disabled:bg-steel-300"
        >
          Add {selectedNewItems.length} items
        </button>
      </div>
    </div>
  );
};

// Restock Modal Component
const RestockModal = ({ item, onClose, onRefresh }) => {
  const [restockData, setRestockData] = useState({
    quantity: '',
    unitPrice: item?.unitPrice || '',
    reason: '',
    supplierInfo: '',
    expectedDeliveryDate: '',
    document: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('quantity_to_add', restockData.quantity);
      formData.append('unit_price', restockData.unitPrice);
      formData.append('restock_reason', restockData.reason);
      formData.append('supplier_info', restockData.supplierInfo);
      formData.append('expected_delivery_date', restockData.expectedDeliveryDate);
      
      if (restockData.document) {
        formData.append('purchase_document', restockData.document);
      }

      // Extract the original ID by removing the warehouse_ prefix
      const originalId = item.originalId || (item.id ? item.id.replace('warehouse_', '') : item.id);

      const response = await axios.post(
        `${API_BASE_URL}/api/warehouse/items/${originalId}/restock/`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        alert('Product successfully restocked!');
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error('Restock error:', error);
      setError(error.response?.data?.message || 'Error restocking product');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PDF, images, etc.)
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (allowedTypes.includes(file.type)) {
        setRestockData(prev => ({ ...prev, document: file }));
      } else {
        alert('Please upload PDF, JPG, or PNG files only');
        e.target.value = '';
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                Add Stock: {item?.name}
              </h3>
              <div className="text-sm text-slate-600">
                Current quantity: <strong>{item?.quantity}</strong> | Status: 
                <span className={`ml-1 px-2 py-1 rounded text-xs ${
                  item?.status === 'out_of_stock' ? 'bg-red-100 text-red-800' :
                  item?.status === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {item?.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Quantity to Add *
                </label>
                <input
                  type="number"
                  min="1"
                  value={restockData.quantity}
                  onChange={(e) => setRestockData(prev => ({ ...prev, quantity: e.target.value }))}
                  className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Unit Price ({getCurrencySymbol()})
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={restockData.unitPrice}
                  onChange={(e) => setRestockData(prev => ({ ...prev, unitPrice: e.target.value }))}
                  className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reason for Stock Addition *
              </label>
              <select
                value={restockData.reason}
                onChange={(e) => setRestockData(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                required
              >
                <option value="">Select reason...</option>
                <option value="purchase">New Purchase</option>
                <option value="return">Stock Return</option>
                <option value="adjustment">Inventory Adjustment</option>
                <option value="restock">Regular Restock</option>
                <option value="emergency">Emergency Restock</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Supplier Information
              </label>
              <input
                type="text"
                value={restockData.supplierInfo}
                onChange={(e) => setRestockData(prev => ({ ...prev, supplierInfo: e.target.value }))}
                placeholder="Supplier name, contact, etc."
                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Expected Delivery Date
              </label>
              <input
                type="date"
                value={restockData.expectedDeliveryDate}
                onChange={(e) => setRestockData(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Upload Document (Invoice, Receipt, etc.)
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  id="document-upload"
                />
                <label
                  htmlFor="document-upload"
                  className="flex items-center px-4 py-2 bg-steel-100 border border-brand-200 rounded-xl cursor-pointer hover:bg-steel-200"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose File
                </label>
                {restockData.document && (
                  <span className="text-sm text-slate-600">
                    {restockData.document.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Supported formats: PDF, JPG, PNG (Max 5MB)
              </p>
            </div>

            {error && (
              <div className="text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-brand-200 rounded-xl text-slate-700 hover:bg-steel-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !restockData.quantity || !restockData.reason}
                className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:bg-steel-400 flex items-center"
              >
                {loading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                {loading ? 'Adding Stock...' : 'Add Stock'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Equipment Items Location Modal Component (for selected individual items)
const EquipmentItemsLocationModal = ({ equipmentItems, onClose, onRefresh }) => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSaveLocation = async () => {
    if (!selectedLocation) {
      alert('Please select a location');
      return;
    }

    setLoading(true);
    try {
      const equipmentAPI = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

      // IMPORTANT: Filter to only update equipment that the company OWNS
      // Do NOT update location for equipment that is shared with us or that we only inspect
      const ownedEquipmentItems = equipmentItems.filter(item => item.isOwner === true);

      if (ownedEquipmentItems.length === 0) {
        alert('None of the selected items are owned by your company. You can only set location for equipment that you own.');
        setLoading(false);
        return;
      }

      const skippedCount = equipmentItems.length - ownedEquipmentItems.length;

      // Update each owned equipment item's location
      const updatePromises = ownedEquipmentItems.map(item =>
        axios.patch(
          `${equipmentAPI}/safetyequipment/${item.originalId}/`,
          { safety_equipment_location: selectedLocation },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        )
      );

      await Promise.all(updatePromises);

      let successMessage = `Successfully updated location for ${ownedEquipmentItems.length} equipment item(s)`;
      if (skippedCount > 0) {
        successMessage += `\n\nNote: ${skippedCount} item(s) were skipped because they are not owned by your company.`;
      }

      alert(successMessage);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error updating equipment location:', error);
      alert('Error updating equipment location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center">
              <MapPin className="h-6 w-6 mr-2 text-brand-600" />
              Set Location for Equipment Items
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="mb-4 p-4 bg-brand-50 rounded-xl border border-brand-200">
            <p className="text-sm text-brand-800">
              You are setting location for <strong>{equipmentItems.length}</strong> equipment item{equipmentItems.length > 1 ? 's' : ''}:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-brand-700">
              {equipmentItems.slice(0, 5).map((item, index) => (
                <li key={index}>• S/N: {item.serialNumber} ({item.name})</li>
              ))}
              {equipmentItems.length > 5 && (
                <li className="text-brand-600 font-medium">... and {equipmentItems.length - 5} more</li>
              )}
            </ul>
          </div>

          <div className="min-h-[500px]">
            <LocationPicker
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
              required={true}
              multiple={false}
            />
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-brand-200 rounded-xl text-slate-700 hover:bg-steel-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveLocation}
              disabled={loading || !selectedLocation}
              className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 flex items-center"
            >
              {loading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {loading ? 'Updating...' : 'Set Location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Equipment Group Location Modal Component
const EquipmentGroupLocationModal = ({ equipmentGroup, onClose, onRefresh }) => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSaveLocation = async () => {
    if (!selectedLocation) {
      alert('Please select a location');
      return;
    }

    setLoading(true);
    try {
      // Update all equipment items in this group with the selected location
      const equipmentAPI = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

      // IMPORTANT: Filter to only update equipment that the company OWNS
      // Do NOT update location for equipment that is shared with us or that we only inspect
      const ownedEquipmentItems = equipmentGroup.equipmentItems?.filter(item => item.isOwner === true) || [];

      if (ownedEquipmentItems.length === 0) {
        alert(`No equipment items in "${equipmentGroup.name}" are owned by your company. You can only set location for equipment that you own.`);
        setLoading(false);
        return;
      }

      const skippedCount = (equipmentGroup.equipmentItems?.length || 0) - ownedEquipmentItems.length;

      // Update each owned equipment item's location
      const updatePromises = ownedEquipmentItems.map(item =>
        axios.patch(
          `${equipmentAPI}/safetyequipment/${item.originalId}/`,
          { safety_equipment_location: selectedLocation },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        )
      );

      await Promise.all(updatePromises);

      let successMessage = `Successfully updated location for ${ownedEquipmentItems.length} equipment item(s)`;
      if (skippedCount > 0) {
        successMessage += `\n\nNote: ${skippedCount} item(s) were skipped because they are not owned by your company.`;
      }

      alert(successMessage);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error updating equipment location:', error);
      alert('Error updating equipment location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-auto" style={{ maxHeight: '85vh' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Set Location for {equipmentGroup.name}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-slate-600 mb-4">
            This will set the location for {equipmentGroup.equipmentItems?.filter(item => item.isOwner).length || 0} equipment item(s) in this group that you own.
            {equipmentGroup.equipmentItems && equipmentGroup.equipmentItems.filter(item => !item.isOwner).length > 0 && (
              <span className="block mt-1 text-amber-600">
                Note: {equipmentGroup.equipmentItems.filter(item => !item.isOwner).length} item(s) not owned by your company will be skipped.
              </span>
            )}
          </p>

          <div className="min-h-[500px]">
            <LocationPicker
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
              required={false}
              multiple={false}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveLocation}
            className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Set Location'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Equipment Group Sharing Modal Component
const ManageEquipmentGroupSharingModal = ({ equipmentGroup, onClose, onRefresh }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isShared, setIsShared] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      // Spróbuj najpierw warehouse endpoint dla dostępnych firm
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/accessible_companies/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      // Jeśli to się udało, użyj tych danych
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);

      // Fallback - stwórz listę manualnie z dostępnymi firmami
      setCompanies([
        { id: 1, name: 'Omega' },
        { id: 2, name: 'EccVestas' },
        { id: 3, name: 'Vestas' },
        { id: 4, name: 'Vulcan' },
        { id: 5, name: 'Waterfall' },
        { id: 6, name: 'M4Wind' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Since equipment groups are virtual entities, we'll need to share all individual equipment items in the group
      const equipmentItems = equipmentGroup.equipmentItems || [];

      // IMPORTANT: Filter to only share equipment that the company OWNS
      // Do NOT share equipment that is shared with us or that we only inspect
      const ownedEquipmentItems = equipmentItems.filter(item => item.isOwner === true);

      if (ownedEquipmentItems.length === 0) {
        alert(`No equipment items in "${equipmentGroup.name}" are owned by your company. You can only share equipment that you own.`);
        return;
      }

      const skippedCount = equipmentItems.length - ownedEquipmentItems.length;

      // Share only owned equipment items
      for (const item of ownedEquipmentItems) {
        try {
          await axios.patch(`${API_BASE_URL}/api/safetyequipment/${item.originalId}/`, {
            is_shared: isShared,
            shared_with_companies: isShared ? selectedCompanies : []
          }, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (itemError) {
          console.warn(`Failed to update sharing for equipment item ${item.originalId}:`, itemError);
        }
      }

      let successMessage = `Equipment group "${equipmentGroup.name}" sharing settings updated successfully! ${ownedEquipmentItems.length} items updated.`;
      if (skippedCount > 0) {
        successMessage += `\n\nNote: ${skippedCount} item(s) were skipped because they are not owned by your company.`;
      }

      alert(successMessage);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error updating equipment group sharing settings:', error);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          'Error updating sharing settings. Please try again.';
      alert(errorMessage);
    }
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-2xl bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-slate-900">
              Share Equipment Group: {equipmentGroup.name}
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Equipment group info */}
            <div className="bg-steel-50 p-3 rounded-xl">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Equipment Type:</span> {equipmentGroup.name}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">Items in group:</span> {equipmentGroup.totalCount}
                <span className="ml-2 text-green-600">({equipmentGroup.availableCount} available)</span>
              </p>
            </div>

            {/* Share toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="shareEquipmentGroup"
                checked={isShared}
                onChange={(e) => setIsShared(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-steel-300 rounded"
              />
              <label htmlFor="shareEquipmentGroup" className="ml-2 block text-sm text-slate-900">
                Share this equipment group with other companies
              </label>
            </div>

            {/* Companies list */}
            {isShared && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Select companies to share with:</p>
                {loading ? (
                  <p className="text-sm text-slate-500">Loading companies...</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {companies.map(company => (
                      <div key={company.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`company-${company.id}`}
                          checked={selectedCompanies.includes(company.id)}
                          onChange={() => handleCompanyToggle(company.id)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-steel-300 rounded"
                        />
                        <label htmlFor={`company-${company.id}`} className="ml-2 block text-sm text-slate-900">
                          {company.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 border border-brand-200 rounded-xl hover:bg-steel-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-xl hover:bg-green-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Import Items Modal Component
const ImportItemsModal = ({ categories, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults(null);
      setErrors([]);
    }
  };

  const handleImport = async () => {
    if (!file) {
      alert('Please select a file to import');
      return;
    }

    setImporting(true);
    setErrors([]);

    try {
      // Read Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        alert('The file is empty or has no valid data');
        setImporting(false);
        return;
      }

      // Process each row
      const importErrors = [];
      const successfulImports = [];
      const categoryCache = {}; // Cache to avoid fetching same category multiple times

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        const rowNum = i + 2; // Excel row number (accounting for header)

        try {
          // Validate required fields
          if (!row['Item Name']) {
            importErrors.push(`Row ${rowNum}: Item Name is required`);
            continue;
          }
          if (!row['Category']) {
            importErrors.push(`Row ${rowNum}: Category is required`);
            continue;
          }
          if (!row['Unit']) {
            importErrors.push(`Row ${rowNum}: Unit is required`);
            continue;
          }
          if (!row['Unit Price']) {
            importErrors.push(`Row ${rowNum}: Unit Price is required`);
            continue;
          }

          // Get or create category
          const categoryName = row['Category'].toString().trim();
          let categoryId;

          if (categoryCache[categoryName]) {
            categoryId = categoryCache[categoryName];
          } else {
            // Try to find existing category
            try {
              const categoriesResponse = await axios.get(
                `${API_BASE_URL}/api/warehouse/categories/`,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                  }
                }
              );
              const categoriesData = Array.isArray(categoriesResponse.data)
                ? categoriesResponse.data
                : categoriesResponse.data.results || [];

              const existingCategory = categoriesData.find(
                cat => cat.name.toLowerCase() === categoryName.toLowerCase()
              );

              if (existingCategory) {
                categoryId = existingCategory.id;
              } else {
                // Create new category
                const newCategoryResponse = await axios.post(
                  `${API_BASE_URL}/api/warehouse/categories/`,
                  { name: categoryName },
                  {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                      'Content-Type': 'application/json'
                    }
                  }
                );
                categoryId = newCategoryResponse.data.id;
              }

              categoryCache[categoryName] = categoryId;
            } catch (catError) {
              console.error('Error handling category:', catError);
              importErrors.push(`Row ${rowNum}: Error with category "${categoryName}"`);
              continue;
            }
          }

          // Prepare item data - convert empty/undefined to appropriate values
          const itemData = {
            name: row['Item Name'],
            category: categoryId,
            unit: row['Unit'],
            unit_price: parseFloat(row['Unit Price']),
            quantity_in_stock: parseInt(row['Initial Stock Quantity']) || 0,
            minimum_stock_level: parseInt(row['Minimum Stock Level']) || 0
          };

          // Add optional fields only if they have values
          if (row['Internal Number (SKU)'] && row['Internal Number (SKU)'].toString().trim()) {
            itemData.sku = row['Internal Number (SKU)'].toString().trim();
          }
          if (row['Location'] && row['Location'].toString().trim()) {
            itemData.location = row['Location'].toString().trim();
          }
          if (row['Supplier'] && row['Supplier'].toString().trim()) {
            itemData.supplier = row['Supplier'].toString().trim();
          }
          if (row['Description'] && row['Description'].toString().trim()) {
            itemData.description = row['Description'].toString().trim();
          }

          // Send to API
          await axios.post(
            `${API_BASE_URL}/api/warehouse/items/`,
            itemData,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Content-Type': 'application/json'
              }
            }
          );

          successfulImports.push(row['Item Name']);
        } catch (error) {
          console.error(`Error importing row ${rowNum}:`, error);
          console.error('Error response data:', error.response?.data);

          // Extract detailed error message
          let errorMsg = 'Unknown error';
          if (error.response?.data) {
            const data = error.response.data;
            // Try to extract first error message from various formats
            if (typeof data === 'string') {
              errorMsg = data;
            } else if (data.detail) {
              errorMsg = data.detail;
            } else if (data.name) {
              errorMsg = Array.isArray(data.name) ? data.name[0] : data.name;
            } else if (data.sku) {
              errorMsg = `SKU: ${Array.isArray(data.sku) ? data.sku[0] : data.sku}`;
            } else if (data.category_name) {
              errorMsg = `Category: ${Array.isArray(data.category_name) ? data.category_name[0] : data.category_name}`;
            } else {
              // Get first error from any field
              const firstError = Object.entries(data).find(([key, value]) => value);
              if (firstError) {
                const [field, message] = firstError;
                errorMsg = `${field}: ${Array.isArray(message) ? message[0] : message}`;
              }
            }
          } else {
            errorMsg = error.message;
          }

          importErrors.push(`Row ${rowNum} (${row['Item Name']}): ${errorMsg}`);
        }
      }

      // Show results
      setResults({
        total: jsonData.length,
        successful: successfulImports.length,
        failed: importErrors.length
      });
      setErrors(importErrors);

      if (successfulImports.length > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please make sure it is a valid Excel file.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full mx-auto max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-slate-900">Import Items from Excel</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-4">
            <h4 className="font-semibold text-brand-800 mb-2">Instructions:</h4>
            <ol className="text-sm text-brand-800 space-y-1 list-decimal list-inside">
              <li>Download the Excel template using the "Download Template" button</li>
              <li>Fill in your items data following the template format</li>
              <li>Required fields: Item Name, Category, Unit, Unit Price</li>
              <li>Remove the example rows before uploading</li>
              <li>Upload the completed file below</li>
            </ol>
          </div>

          <div className="border-2 border-dashed border-steel-300 rounded-xl p-8 text-center">
            <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <label className="cursor-pointer">
              <span className="text-brand-600 hover:text-brand-700 font-medium">
                Choose Excel file
              </span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            <p className="text-sm text-slate-500 mt-2">
              {file ? `Selected: ${file.name}` : 'or drag and drop'}
            </p>
          </div>
        </div>

        {results && (
          <div className="mb-6">
            <div className="bg-steel-50 border border-brand-200 rounded-xl p-4">
              <h4 className="font-semibold text-slate-900 mb-3">Import Results:</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-slate-900">{results.total}</div>
                  <div className="text-sm text-slate-600">Total Rows</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{results.successful}</div>
                  <div className="text-sm text-slate-600">Successful</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">{results.failed}</div>
                  <div className="text-sm text-slate-600">Failed</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-h-60 overflow-y-auto">
              <h4 className="font-semibold text-red-900 mb-2">Errors:</h4>
              <ul className="text-sm text-red-800 space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
            disabled={importing}
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              onClick={handleImport}
              className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors disabled:opacity-50"
              disabled={!file || importing}
            >
              {importing ? 'Importing...' : 'Import Items'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarehouseInventory;